import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, MessageSquare, Ship, Plane, Truck, Warehouse, Package, Anchor, Star, ArrowUp, DollarSign, FileText, Check, X, AlertCircle, Loader2, Calculator, Info, Send, Edit, Eye, EyeOff, CheckCircle, Download, Scale, Search, Filter, Calendar, MapPin, Hash, Clock, User, Users, Building2, Inbox, SendHorizontal, Copy, Globe, Upload, Trash2, RotateCcw, Archive } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { SearchableSelect } from './ui/searchable-select';
import { quotesApi, directoryApi, profileApi, chatApi, teamApi, paymentsApi, uploadsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import RestrictedOverlay from './ui/RestrictedOverlay';
import { toast } from 'sonner';
import { getErrorMessage, addDaysIsoLocal, sanitizeHtmlDateValue } from '../lib/utils';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import BidComparison from './BidComparison';
import InquiryCreatorIdentity from './InquiryCreatorIdentity';
import { SCOPE_OPTIONS_BY_MODE, getChargeHeadsForModeAndScopes } from '../config/chargeHeadsByModeLeg';

// Import detailed quote forms
import OceanFreightForm from './quotes/OceanFreightForm';
import AirFreightForm from './quotes/AirFreightForm';
import TransportForm from './quotes/TransportForm';
import WarehousingForm from './quotes/WarehousingForm';
import LCLForm from './quotes/LCLForm';
import BreakbulkForm from './quotes/BreakbulkForm';

/** Receiver-facing LCL cargo lines (matches LCLForm `dimensionsRows` + legacy `dimensions`) */
function formatLclRowStackability(val) {
  if (val === 'stackable') return 'Stackable';
  if (val === 'non_stackable') return 'Non-stackable';
  return '—';
}

function getLclCargoDimensionRows(cargo) {
  if (Array.isArray(cargo?.dimensionsRows) && cargo.dimensionsRows.length > 0) {
    return cargo.dimensionsRows;
  }
  const d = cargo?.dimensions;
  if (d && (d.length || d.width || d.height)) {
    return [{
      pieces: cargo?.pieces ?? '',
      length: d.length ?? '',
      width: d.width ?? '',
      height: d.height ?? '',
      unit: d.unit ?? 'cm',
      stackability: ''
    }];
  }
  return [];
}

function getQuoteDimensionRows(formData, defaultUnit = 'cm') {
  if (Array.isArray(formData?.dimensionsRows) && formData.dimensionsRows.length > 0) {
    return formData.dimensionsRows.map((row) => ({
      ...row,
      unit: row?.unit || defaultUnit,
    }));
  }
  if (Array.isArray(formData?.dimensionsList) && formData.dimensionsList.length > 0) {
    const fallbackUnit = formData?.dimensions?.unit || defaultUnit;
    return formData.dimensionsList.map((row) => ({
      ...row,
      unit: row?.unit || fallbackUnit,
    }));
  }
  const d = formData?.dimensions;
  if (d && (d.length || d.width || d.height)) {
    return [{
      pieces: formData?.pieces ?? '',
      length: d.length ?? '',
      width: d.width ?? '',
      height: d.height ?? '',
      unit: d.unit || defaultUnit,
      stackability: formData?.stackability || '',
    }];
  }
  return [];
}

function renderQuoteDimensions(formData, defaultUnit = 'cm') {
  const rows = getQuoteDimensionRows(formData, defaultUnit);
  if (rows.length === 0) return null;

  return (
    <div className="mt-4">
      <Label className="text-xs text-gray-500">Dimensions</Label>
      <div className="space-y-1 mt-1">
        {rows.map((row, index) => {
          const pieces = row?.pieces !== '' && row?.pieces != null ? `${row.pieces} pcs` : null;
          const dims = `${row?.length ?? '-'} × ${row?.width ?? '-'} × ${row?.height ?? '-'} ${row?.unit || defaultUnit}`;
          const stackability = formatLclRowStackability(row?.stackability);
          const details = [pieces, dims, stackability === '—' ? null : stackability].filter(Boolean).join(' | ');
          return (
            <p key={index} className="text-sm">
              Set {index + 1}: {details}
            </p>
          );
        })}
      </div>
    </div>
  );
}

function normalizeHazardousFiles(...sources) {
  const collected = [];
  sources.flat().forEach((source) => {
    if (!source) return;
    if (Array.isArray(source)) {
      source.forEach((item) => {
        if (item) collected.push(item);
      });
      return;
    }
    collected.push(source);
  });

  return collected
    .map((item, index) => {
      if (typeof item === 'string') {
        return {
          url: item,
          name: item.split('/').pop() || `MSDS ${index + 1}`,
        };
      }
      const url = item.url || item.file_url;
      if (!url) return null;
      return {
        url,
        name: item.name || item.original_filename || url.split('/').pop() || `MSDS ${index + 1}`,
      };
    })
    .filter(Boolean);
}

function OceanLclCargoLineReceiverView({ cargo }) {
  const rows = getLclCargoDimensionRows(cargo);
  const hasRowData = rows.some((r) =>
    r.pieces || r.length || r.width || r.height || (r.stackability && r.stackability !== '')
  );
  return (
    <div className="p-3 bg-white rounded border text-sm space-y-2">
      {cargo.description && (
        <p>
          <span className="text-gray-500">Description:</span> {cargo.description}
        </p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
        {cargo.hsCode ? <span>HS: {cargo.hsCode}</span> : null}
        {cargo.weight != null && String(cargo.weight).trim() !== '' ? (
          <span>Weight: {cargo.weight} kg</span>
        ) : null}
        {cargo.volume != null && String(cargo.volume).trim() !== '' ? (
          <span>Vol: {cargo.volume} m³</span>
        ) : null}
        {cargo.pieces != null && String(cargo.pieces).trim() !== '' ? (
          <span>Total pieces: {cargo.pieces}</span>
        ) : null}
      </div>
      {hasRowData ? (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-600 mb-1">Dimensions &amp; stackability (per set)</p>
          <ul className="space-y-2 text-xs">
            {rows.map((row, ri) => {
              const dimParts = [row.length, row.width, row.height].filter(
                (x) => x !== '' && x != null && String(x).trim() !== ''
              );
              const dimStr = dimParts.length ? `${dimParts.join(' × ')} ${row.unit || 'cm'}` : '—';
              return (
                <li key={ri} className="pl-2 border-l-2 border-blue-200 text-gray-800">
                  <span className="font-medium text-gray-600">Set {ri + 1}:</span>{' '}
                  <span className="whitespace-normal break-words">
                    Pieces: <strong>{row.pieces !== '' && row.pieces != null ? row.pieces : '—'}</strong>
                    {' · '}
                    L×W×H: <strong>{dimStr}</strong>
                    {' · '}
                    {formatLclRowStackability(row.stackability)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function formatLclDimensionTextClean(length, width, height, unit = 'cm') {
  const dimParts = [length, width, height].filter(
    (x) => x !== '' && x != null && String(x).trim() !== ''
  );
  return dimParts.length ? `${dimParts.join(' x ')} ${unit}` : 'Not specified';
}

function formatLclRowStackabilityClean(val) {
  if (val === 'stackable') return 'Stackable';
  if (val === 'non_stackable') return 'Non-stackable';
  return 'Not specified';
}

function formatLclRowSummaryClean(row, rowIndex) {
  const pieces = row?.pieces !== '' && row?.pieces != null ? row.pieces : 'N/A';
  const dimensions = formatLclDimensionTextClean(row?.length, row?.width, row?.height, row?.unit || 'cm');
  return `Set ${rowIndex + 1}: ${pieces} pcs, ${dimensions}, ${formatLclRowStackabilityClean(row?.stackability)}`;
}

function buildLclCargoDescription(formData, fallbackDescription = '') {
  const notes = Array.isArray(formData.specialRequirements)
    ? [...(formData.specialRequirements || []), formData.notes].filter(Boolean).join('; ')
    : (formData.specialRequirements || formData.cargoDescription || formData.notes || fallbackDescription || '');

  if (!Array.isArray(formData.cargoDetails) || formData.cargoDetails.length === 0) {
    return notes;
  }

  const itemLines = formData.cargoDetails.map((cargo, cargoIndex) => {
    const label = (cargo.description || '').trim() || 'Cargo';
    const rows = getLclCargoDimensionRows(cargo);
    const rowText = rows.length > 0
      ? rows.map((row, rowIndex) => formatLclRowSummaryClean(row, rowIndex)).join(' | ')
      : 'Dimensions not specified';
    return `Item ${cargoIndex + 1} (${label}): ${rowText}`;
  });

  const fragile = Array.isArray(formData.specialHandling) && formData.specialHandling.includes('Fragile')
    ? `Fragile: ${formData.fragileHandling === 'specific' ? `${formData.fragileBoxCount || '?'} boxes` : 'All cargo'}`
    : '';

  return [notes, itemLines.join('\n'), fragile].filter(Boolean).join('\n');
}

function OceanLclCargoCard({ cargo }) {
  const rows = getLclCargoDimensionRows(cargo);
  const statCards = [
    cargo.hsCode ? { label: 'HS Code', value: cargo.hsCode } : null,
    cargo.weight != null && String(cargo.weight).trim() !== '' ? { label: 'Weight', value: `${cargo.weight} kg` } : null,
    cargo.volume != null && String(cargo.volume).trim() !== '' ? { label: 'Volume', value: `${cargo.volume} m3` } : null,
    cargo.pieces != null && String(cargo.pieces).trim() !== '' ? { label: 'Total Pieces', value: cargo.pieces } : null
  ].filter(Boolean);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="space-y-4">
        {cargo.description && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Description</p>
            <p className="mt-1 text-base font-semibold text-slate-900">{cargo.description}</p>
          </div>
        )}

        {statCards.length > 0 && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {statCards.map((item) => (
              <div key={item.label} className="rounded-lg bg-slate-50 px-3 py-2">
                <p className="text-xs text-slate-500">{item.label}</p>
                <p className="font-medium text-slate-900">{item.value}</p>
              </div>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Dimensions and Stackability</p>
            <ul className="space-y-2">
              {rows.map((row, rowIndex) => (
                <li key={rowIndex} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                  {formatLclRowSummaryClean(row, rowIndex)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function getQuoteDisplayCargoDescription(quote) {
  const fd = quote?.form_data || {};
  if (quote?.quote_type === 'Ocean LCL') {
    const hasStructuredLclData =
      Array.isArray(fd.cargoDetails) && fd.cargoDetails.length > 0 ||
      Boolean(fd.specialRequirements) ||
      Boolean(fd.notes) ||
      Boolean(fd.specialHandling?.length);
    return hasStructuredLclData
      ? buildLclCargoDescription(fd)
      : buildLclCargoDescription(fd, quote?.cargo_description || '');
  }
  return quote?.cargo_description || '';
}

function getLclNotesText(quote) {
  const fd = quote?.form_data || {};
  const notes = Array.isArray(fd.specialRequirements)
    ? [...fd.specialRequirements, fd.notes].filter(Boolean).join('; ')
    : (fd.specialRequirements || fd.notes || '');
  return String(notes || '').trim();
}

function getInsuranceDisplay(formData = {}) {
  const insuranceValue = formData?.insuranceValue ?? formData?.customsValue ?? formData?.customsDetails?.customsValue;
  const hasInsuranceValue = insuranceValue !== '' && insuranceValue != null;
  if (!formData?.insuranceRequired && !hasInsuranceValue) return '';
  if (!hasInsuranceValue) return 'Required';
  const numericValue = Number(insuranceValue);
  const displayValue = Number.isFinite(numericValue)
    ? numericValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : insuranceValue;
  return `${formData?.insuranceRequired ? 'Required - ' : ''}Invoice Value (USD) ${displayValue}`;
}

function getFileDownloadInfo(file, fallbackName = 'Download file') {
  if (!file) return null;
  const rawUrl = typeof file === 'string'
    ? file
    : (file.url || file.file_url || file.path || '');
  if (!rawUrl) return null;
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const url = rawUrl.startsWith('http')
    ? rawUrl
    : `${base}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
  const name = typeof file === 'object'
    ? (file.name || file.original_filename || file.filename || fallbackName)
    : (rawUrl.split('/').pop() || fallbackName);
  return { url, name };
}

function hasPaymentBankDetails(details = {}) {
  return ['account_name', 'bank_name', 'account_number', 'iban', 'swift_code', 'bank_address']
    .some((key) => String(details?.[key] || '').trim());
}

function maskPaymentAccountNumber(accountNumber = '') {
  const value = String(accountNumber || '').replace(/\s/g, '');
  if (!value) return '';
  return value.length <= 4 ? value : `****${value.slice(-4)}`;
}

function getPaymentBankDetailsLabel(details = {}, fallback = 'Bank details') {
  if (details.label) return details.label;
  const bankName = String(details.bank_name || '').trim();
  const maskedAccount = maskPaymentAccountNumber(details.account_number);
  if (bankName && maskedAccount) return `${bankName} - ${maskedAccount}`;
  return bankName || String(details.account_name || '').trim() || fallback;
}

function normalizeSavedPaymentBankDetails(details = {}) {
  return {
    detail_id: details.detail_id || '',
    label: getPaymentBankDetailsLabel(details),
    account_name: details.account_name || '',
    bank_name: details.bank_name || '',
    account_number: details.account_number || '',
    iban: details.iban || '',
    swift_code: details.swift_code || '',
    bank_address: details.bank_address || ''
  };
}

function buildPaymentDetailsFormFromBankDetails(previousForm, bankDetails = {}) {
  return {
    ...previousForm,
    account_name: bankDetails.account_name || '',
    bank_name: bankDetails.bank_name || '',
    account_number: bankDetails.account_number || '',
    iban: bankDetails.iban || '',
    swift_code: bankDetails.swift_code || '',
    bank_address: bankDetails.bank_address || ''
  };
}

function getPriorityUntilDateValue(priorityUntil) {
  return sanitizeHtmlDateValue(priorityUntil ?? '');
}

function getTodayDateValue() {
  return addDaysIsoLocal(0);
}

function getSafeFutureDateValue(value) {
  const normalized = sanitizeHtmlDateValue(value);
  if (!normalized) return '';
  return normalized >= getTodayDateValue() ? normalized : '';
}

function getSafePastOrTodayDateValue(value) {
  const normalized = sanitizeHtmlDateValue(value);
  if (!normalized) return '';
  return normalized <= getTodayDateValue() ? normalized : getTodayDateValue();
}

function getPromotionStartDateValue(quote) {
  const today = getTodayDateValue();
  const currentUntil = getPriorityUntilDateValue(quote?.priority_until);
  if ((quote?.priority === 'High' || quote?.priority === 'Medium') && currentUntil && currentUntil >= today) {
    return addDaysIsoLocal(currentUntil, 1);
  }
  return today;
}

function formatPriorityUntilLabel(priorityUntil) {
  const value = getPriorityUntilDateValue(priorityUntil);
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function toLocalDateTimeInputValue(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const POSTED_INQUIRY_EDITABLE_FIELDS = new Set([
  'quote_type',
  'commodity',
  'hs_code',
  'attachments',
  'cargo_description',
  'origin_port',
  'destination_port',
  'origin_city',
  'destination_city',
  'incoterm',
  'is_hazardous',
  'hazardous_class',
  'un_number',
  'containers',
  'weight_kg',
  'volume_cbm',
  'dimensions',
  'sender_line_items',
  'special_instructions',
  'target_rates',
  'terms_and_conditions',
  'payment_terms',
  'cargo_ready_date',
  'valid_until',
  'response_deadline',
  'special_requirements',
  'photos',
  'form_data',
]);

const isPlainFormObject = (value) => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const cloneFormValue = (value) => {
  if (Array.isArray(value)) return value.map(cloneFormValue);
  if (isPlainFormObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneFormValue(item)]));
  }
  return value;
};

const formValuesEqual = (left, right) => {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left)
      && Array.isArray(right)
      && left.length === right.length
      && left.every((value, index) => formValuesEqual(value, right[index]));
  }
  if (isPlainFormObject(left) || isPlainFormObject(right)) {
    if (!isPlainFormObject(left) || !isPlainFormObject(right)) return false;
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    return leftKeys.length === rightKeys.length
      && leftKeys.every(
        (key) => Object.prototype.hasOwnProperty.call(right, key)
          && formValuesEqual(left[key], right[key])
      );
  }
  return false;
};

// Return only fields changed by the user after the editor finished hydrating.
// Missing fields are deliberately not treated as removals: older inquiry forms
// did not contain every modern control, and a save must never erase that data.
const buildFormDataPatch = (initialValue, currentValue) => {
  if (formValuesEqual(initialValue, currentValue)) return undefined;
  if (isPlainFormObject(initialValue) && isPlainFormObject(currentValue)) {
    const patch = {};
    Object.keys(currentValue).forEach((key) => {
      const valuePatch = buildFormDataPatch(initialValue[key], currentValue[key]);
      if (valuePatch !== undefined) patch[key] = valuePatch;
    });
    return Object.keys(patch).length > 0 ? patch : undefined;
  }
  return cloneFormValue(currentValue);
};

const mergeFormDataPatch = (existingValue, patch) => {
  if (!isPlainFormObject(patch)) return cloneFormValue(patch);
  const merged = isPlainFormObject(existingValue) ? cloneFormValue(existingValue) : {};
  Object.entries(patch).forEach(([key, value]) => {
    merged[key] = mergeFormDataPatch(merged[key], value);
  });
  return merged;
};

const isMissingFormValue = (value) => (
  value === undefined
  || value === null
  || value === ''
  || (Array.isArray(value) && value.length === 0)
  || (isPlainFormObject(value) && Object.keys(value).length === 0)
);

const setIfMissing = (target, key, value) => {
  if (isMissingFormValue(target[key]) && !isMissingFormValue(value)) {
    target[key] = cloneFormValue(value);
  }
};

const fillLocationIfMissing = (target, key, city, country) => {
  const location = isPlainFormObject(target[key]) ? { ...target[key] } : {};
  if (isMissingFormValue(location.city) && !isMissingFormValue(city)) location.city = city;
  if (isMissingFormValue(location.country) && !isMissingFormValue(country)) location.country = country;
  if (Object.keys(location).length > 0) target[key] = location;
};

const quotePortValue = (port, city) => port?.code || port?.name || city || '';

const getLegacyContainerFormValues = (containers) => {
  if (!Array.isArray(containers)) return null;
  const quantities = {};
  const weights = {};
  containers.forEach((container) => {
    const type = container?.container_type || container?.type;
    if (!type) return;
    const quantity = container?.quantity ?? container?.qty;
    if (quantity !== undefined && quantity !== null && quantity !== '') quantities[type] = quantity;
    if (container?.weight_kg !== undefined && container?.weight_kg !== null && container?.weight_kg !== '') {
      weights[type] = container.weight_kg;
    }
  });
  return {
    quantities: Object.keys(quantities).length > 0 ? quantities : null,
    weights: Object.keys(weights).length > 0 ? weights : null,
  };
};

// Old posted records can predate the form_data snapshot.  Populate only
// displayable form fields from their canonical top-level values; any field the
// current UI cannot represent stays on the record and is omitted from updates.
const buildPostedInquiryInitialFormData = (quote) => {
  const formData = isPlainFormObject(quote?.form_data)
    ? cloneFormValue(quote.form_data)
    : {};
  if (!quote) return formData;

  const origin = quote.origin_port || {};
  const destination = quote.destination_port || {};
  const quoteType = quote.quote_type || '';
  const originValue = quotePortValue(origin, quote.origin_city);
  const destinationValue = quotePortValue(destination, quote.destination_city);

  setIfMissing(formData, 'commodity', quote.commodity);
  setIfMissing(formData, 'incoterm', quote.incoterm);
  setIfMissing(formData, 'cargoDescription', quote.cargo_description);
  if (quoteType !== 'Transport') {
    setIfMissing(formData, 'specialRequirements', quote.special_requirements || quote.cargo_description);
  } else {
    setIfMissing(formData, 'notes', quote.special_requirements || quote.cargo_description);
  }
  setIfMissing(formData, 'isHaz', quote.is_hazardous);
  setIfMissing(formData, 'isHazardous', quote.is_hazardous);
  setIfMissing(formData, 'hazClass', quote.hazardous_class);
  setIfMissing(formData, 'unNumber', quote.un_number);

  if (quoteType === 'Ocean FCL' || quoteType === 'Ocean LCL') {
    setIfMissing(formData, 'pol', originValue);
    setIfMissing(formData, 'polName', origin.name);
    setIfMissing(formData, 'polCountry', origin.country);
    setIfMissing(formData, 'pod', destinationValue);
    setIfMissing(formData, 'podName', destination.name);
    setIfMissing(formData, 'podCountry', destination.country);
  }

  if (quoteType === 'Ocean FCL') {
    const legacyContainers = getLegacyContainerFormValues(quote.containers);
    if (legacyContainers?.quantities) setIfMissing(formData, 'containers', legacyContainers.quantities);
    if (legacyContainers?.weights) setIfMissing(formData, 'containerWeights', legacyContainers.weights);
  }

  if (quoteType === 'Ocean LCL') {
    setIfMissing(formData, 'totalWeight', quote.weight_kg);
    setIfMissing(formData, 'totalVolume', quote.volume_cbm);
    if ((!Array.isArray(formData.cargoDetails) || formData.cargoDetails.length === 0) && quote.cargo_description) {
      formData.cargoDetails = [{
        description: quote.cargo_description,
        weight: quote.weight_kg ?? '',
        volume: quote.volume_cbm ?? '',
        pieces: 0,
      }];
    }
  }

  if (quoteType === 'Air') {
    setIfMissing(formData, 'origin', originValue);
    setIfMissing(formData, 'originCountry', origin.country);
    setIfMissing(formData, 'destination', destinationValue);
    setIfMissing(formData, 'destinationCountry', destination.country);
    setIfMissing(formData, 'totalWeight', quote.weight_kg);
    if ((!Array.isArray(formData.dimensionsRows) || formData.dimensionsRows.length === 0) && isPlainFormObject(quote.dimensions)) {
      formData.dimensionsRows = [{ pieces: '', ...cloneFormValue(quote.dimensions), unit: quote.dimensions.unit || 'cm' }];
    }
  }

  if (quoteType === 'Transport') {
    fillLocationIfMissing(formData, 'pickupLocation', quote.origin_city, origin.country);
    fillLocationIfMissing(formData, 'deliveryLocation', quote.destination_city, destination.country);
    if (!Array.isArray(formData.cargoDetails) || formData.cargoDetails.length === 0) {
      formData.cargoDetails = [{
        commodity: quote.commodity || '',
        cargoWeight: quote.weight_kg ?? '',
        cargoVolume: quote.volume_cbm ?? '',
        dimensions: isPlainFormObject(quote.dimensions)
          ? { ...cloneFormValue(quote.dimensions), unit: quote.dimensions.unit || 'cm' }
          : { length: '', width: '', height: '', unit: 'cm' },
      }];
    }
  }

  if (quoteType === 'Warehousing') {
    fillLocationIfMissing(formData, 'location', quote.origin_city, origin.country);
  }

  if (quoteType === 'Breakbulk') {
    if (!isPlainFormObject(formData.origin)) formData.origin = {};
    if (!isPlainFormObject(formData.destination)) formData.destination = {};
    setIfMissing(formData.origin, 'port', originValue);
    setIfMissing(formData.origin, 'country', origin.country);
    setIfMissing(formData.destination, 'port', destinationValue);
    setIfMissing(formData.destination, 'country', destination.country);
    setIfMissing(formData, 'totalWeight', quote.weight_kg);
    setIfMissing(formData, 'dimensions', quote.dimensions);
    setIfMissing(formData, 'photos', quote.photos);
  }

  return formData;
};

const responseDeadlineChanged = (originalDeadline, currentInputValue) => (
  toLocalDateTimeInputValue(originalDeadline) !== String(currentInputValue || '')
);

function getMinimumResponseDeadline() {
  return toLocalDateTimeInputValue(new Date(Date.now() + (5 * 60 * 1000)));
}

function getResponseDeadlineInfo(quote, nowMs = Date.now()) {
  if (!quote?.response_deadline) return null;
  const deadline = new Date(quote.response_deadline);
  if (Number.isNaN(deadline.getTime())) return null;
  const remainingMs = deadline.getTime() - nowMs;
  const isClosed = remainingMs <= 0;
  const totalMinutes = Math.max(0, Math.ceil(remainingMs / (60 * 1000)));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  const countdown = isClosed
    ? 'Submission closed'
    : days > 0
      ? `${days}d ${hours}h remaining`
      : hours > 0
        ? `${hours}h ${minutes}m remaining`
        : `${minutes}m remaining`;
  return {
    isClosed,
    dateLabel: deadline.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }),
    countdown,
  };
}

function getPublicInquiryUrl(shareToken) {
  if (!shareToken) return '';
  return `${window.location.origin}/public/inquiry/${encodeURIComponent(shareToken)}`;
}

function getBidDraftStorageKey(memberId, quoteId) {
  if (!memberId || !quoteId) return null;
  return `quote-bid-draft:${memberId}:${quoteId}`;
}

function getHiddenReceivedQuotesStorageKey(memberId) {
  if (!memberId) return null;
  return `hidden-received-quotes:${memberId}`;
}

const ARCHIVE_EXPIRY_DAYS = 10;

function readHiddenReceivedQuotes(memberId) {
  const storageKey = getHiddenReceivedQuotesStorageKey(memberId);
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    const expiryMs = ARCHIVE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    // Migrate old format (plain string IDs) to new format (objects with timestamp)
    return parsed
      .map((entry) => (typeof entry === 'string' ? { quote_id: entry, hidden_at: now } : entry))
      .filter((entry) => entry?.quote_id && (now - (entry.hidden_at || 0)) < expiryMs);
  } catch (error) {
    console.error('Failed to read hidden received quotes:', error);
    return [];
  }
}

function readHiddenReceivedQuoteIds(memberId) {
  return readHiddenReceivedQuotes(memberId).map((e) => e.quote_id);
}

function writeHiddenReceivedQuotes(memberId, entries) {
  const storageKey = getHiddenReceivedQuotesStorageKey(memberId);
  if (!storageKey) return;
  localStorage.setItem(storageKey, JSON.stringify(Array.isArray(entries) ? entries : []));
}

function writeHiddenReceivedQuoteIds(memberId, quoteIds) {
  // Legacy compat — convert to new format preserving existing timestamps
  const existing = readHiddenReceivedQuotes(memberId);
  const existingMap = Object.fromEntries(existing.map((e) => [e.quote_id, e]));
  const entries = quoteIds.map((id) => existingMap[id] || { quote_id: id, hidden_at: Date.now() });
  writeHiddenReceivedQuotes(memberId, entries);
}

function getQuotePriorityRank(quote) {
  const today = getTodayDateValue();
  const priorityUntil = getPriorityUntilDateValue(quote?.priority_until);
  const hasActivePriorityWindow =
    !priorityUntil || priorityUntil >= today;

  if (hasActivePriorityWindow && quote?.priority === 'High') return 2;
  if (hasActivePriorityWindow && quote?.priority === 'Medium') return 1;
  return 0;
}

const QuoteToolEnhanced = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [quotes, setQuotes] = useState([]);
  const [draftQuotes, setDraftQuotes] = useState([]);
  const [receivedQuotes, setReceivedQuotes] = useState([]);
  const [teamEnquiriesQuotes, setTeamEnquiriesQuotes] = useState([]);
  const [quotedQuotes, setQuotedQuotes] = useState([]); // Quotes I've submitted bids on
  const [nominatedQuotes, setNominatedQuotes] = useState([]); // Quotes where my bid was nominated
  const [closedQuotes, setClosedQuotes] = useState([]); // Completed/cancelled quotes
  const [assignedToMeQuotes, setAssignedToMeQuotes] = useState([]); // Quotes assigned to me (company admin assigned)
  const [archivedQuotes, setArchivedQuotes] = useState([]); // Not-interested quotes kept for 10 days
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sent');
  const [currentPage, setCurrentPage] = useState(1);
  const [sentSubFilter, setSentSubFilter] = useState('all'); // 'all' | 'mine' | 'team'
  const [nominatedSubFilter, setNominatedSubFilter] = useState('all'); // 'all' | 'by_me' | 'for_me'
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignQuoteFor, setAssignQuoteFor] = useState(null);
  const [assignTeamMembers, setAssignTeamMembers] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSearchQuery, setAssignSearchQuery] = useState('');

  // Search and filters - Enhanced
  const [searchQuery, setSearchQuery] = useState('');
  // Debounce search so typing doesn't refetch the whole list every character.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterQuoteType, setFilterQuoteType] = useState('');
  const [filterPOL, setFilterPOL] = useState('');
  const [filterPOD, setFilterPOD] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterReference, setFilterReference] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [showSaveFilterDialog, setShowSaveFilterDialog] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');

  // Dialogs
  const [showNewQuoteDialog, setShowNewQuoteDialog] = useState(false);
  const [showInquiryCreatedDialog, setShowInquiryCreatedDialog] = useState(false);
  const [quoteSubmitLoading, setQuoteSubmitLoading] = useState(false);
  const [draftSaveLoading, setDraftSaveLoading] = useState(false);
  const [showQuoteDetailDialog, setShowQuoteDetailDialog] = useState(false);
  const [showNominationDialog, setShowNominationDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [showBidComparisonDialog, setShowBidComparisonDialog] = useState(false);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [promoteQuote, setPromoteQuote] = useState(null);
  const [promotePriority, setPromotePriority] = useState('');
  const [promoteUntilDate, setPromoteUntilDate] = useState('');
  const [cloneSourceQuote, setCloneSourceQuote] = useState(null);
  const [editingDraftQuote, setEditingDraftQuote] = useState(null);
  const [editingPostedInquiry, setEditingPostedInquiry] = useState(null);
  const [showVoidConfirmDialog, setShowVoidConfirmDialog] = useState(false);
  const [voidConfirmQuote, setVoidConfirmQuote] = useState(null);
  const [showPaymentDetailsDialog, setShowPaymentDetailsDialog] = useState(false);
  const [showPayNowDialog, setShowPayNowDialog] = useState(false);
  const [showPaymentReceivedDialog, setShowPaymentReceivedDialog] = useState(false);
  const [showHideReceivedConfirmDialog, setShowHideReceivedConfirmDialog] = useState(false);
  const [hideReceivedConfirmQuote, setHideReceivedConfirmQuote] = useState(null);
  const [pendingHiddenReceivedQuotes, setPendingHiddenReceivedQuotes] = useState({});

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [createdInquiry, setCreatedInquiry] = useState(null);
  const [sharingUpdateQuoteId, setSharingUpdateQuoteId] = useState(null);
  const [selectedBid, setSelectedBid] = useState(null);
  const [quoteBids, setQuoteBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(false);
  const [nominatedBidDetail, setNominatedBidDetail] = useState(null);
  const [nominatedBidLoading, setNominatedBidLoading] = useState(false);
  const [submittedBidDetail, setSubmittedBidDetail] = useState(null);
  const [submittedBidLoading, setSubmittedBidLoading] = useState(false);
  const [bidDialogLoading, setBidDialogLoading] = useState(false);
  const [bidSubmitLoading, setBidSubmitLoading] = useState(false);
  const [memberProfile, setMemberProfile] = useState(null);
  const [pointsConfig, setPointsConfig] = useState(null);

  // Quote type selection
  const [selectedQuoteType, setSelectedQuoteType] = useState(null);
  // Current form data in new-quote dialog (e.g. scope) so we can filter charge codes by selected scope
  const [currentFormDataInDialog, setCurrentFormDataInDialog] = useState({ scope: [] });
  const [quoteFormErrors, setQuoteFormErrors] = useState({});
  const apiBase = import.meta.env?.VITE_API_BASE || process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const renderHazardousFileDownloads = useCallback((...sources) => {
    const hazardousFiles = normalizeHazardousFiles(...sources);
    if (hazardousFiles.length === 0) return null;

    return (
      <div className="mt-3 pt-3 border-t border-red-100">
        <Label className="text-xs text-gray-500 block mb-2">MSDS Documents</Label>
        <ul className="space-y-1">
          {hazardousFiles.map((file, index) => {
            const fullUrl = file.url.startsWith('http')
              ? file.url
              : `${apiBase}${file.url.startsWith('/') ? '' : '/'}${file.url}`;
            return (
              <li key={`${file.url}-${index}`} className="flex items-center justify-between gap-2 py-1">
                <span className="truncate text-xs">{file.name || `MSDS ${index + 1}`}</span>
                <a
                  href={fullUrl}
                  download={file.name || `MSDS ${index + 1}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-200 shrink-0"
                  title="Download"
                >
                  <Download size={14} />
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }, [apiBase]);

  const renderInquiryAttachments = useCallback((attachments, compact = false) => {
    if (!Array.isArray(attachments) || attachments.length === 0) return null;
    return (
      <div className={compact ? 'mb-3 flex flex-wrap items-center gap-2' : 'space-y-2'}>
        {!compact && <Label className="text-xs text-gray-500">Inquiry documents</Label>}
        {attachments.map((file, index) => {
          const url = file?.url || file?.file_url;
          if (!url) return null;
          const fullUrl = url.startsWith('http') ? url : `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`;
          const name = file.original_filename || file.name || file.filename || `Document ${index + 1}.pdf`;
          return (
            <a
              key={`${url}-${index}`}
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
              title={`Open ${name}`}
            >
              <FileText size={14} className="shrink-0" />
              <span className="truncate">{name}</span>
              <Download size={13} className="shrink-0" />
            </a>
          );
        })}
      </div>
    );
  }, [apiBase]);

  const { member, user } = useAuth();
  const { hasRestriction, getRestrictionMessage, canCreateQuote, canCreateEnquiry, getRemainingQuotes, getRemainingEnquiries, refreshTierConfig, pointsSystemEnabled: tierPointsSystemEnabled } = useTier();
  const currentMemberId = member?.member_id;
  const pointsSystemEnabled = (pointsConfig?.points_system_enabled ?? true) && tierPointsSystemEnabled;
  const currentUserDisplayName = member?.individual_name || user?.full_name || '';
  const isCompanyAdmin = Boolean(
    memberProfile?.is_company_admin ||
    memberProfile?.has_admin_rights ||
    member?.has_admin_rights
  );
  const canAccessTeamEnquiries = Boolean(
    memberProfile?.company_id || member?.company_id
  );
  const isBidLostQuote = useCallback((quote) => {
    if (!quote?.has_submitted_bid || quote?.my_bid_nominated) return false;
    return ['Completed', 'Expired', 'Cancelled'].includes(quote.status);
  }, []);
  const isQuotedSubmissionVisible = useCallback(
    (quote) => Boolean(quote?.has_submitted_bid && !quote?.my_bid_nominated),
    []
  );

  // Line items for quote enhancement
  const [lineItems, setLineItems] = useState([]);
  const [specialInstructions, setSpecialInstructions] = useState([]);
  const [quotePriority, setQuotePriority] = useState('Normal');
  const [priorityUntilDate, setPriorityUntilDate] = useState('');
  const [responseDeadline, setResponseDeadline] = useState('');
  const [publicSharingEnabled, setPublicSharingEnabled] = useState(true);
  const [hsCode, setHsCode] = useState('');
  const [quoteAttachments, setQuoteAttachments] = useState([]);
  const [quoteAttachmentsUploading, setQuoteAttachmentsUploading] = useState(false);
  const quoteAttachmentInputRef = useRef(null);
  // Captured after a posted inquiry form has hydrated.  It lets us submit only
  // intentional deltas instead of re-saving defaults over legacy data.
  const postedInquiryEditBaselineRef = useRef(null);
  const [deadlineNow, setDeadlineNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setDeadlineNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  // Bid form state
  const [bidLineItems, setBidLineItems] = useState([]);
  const [requesterLineItems, setRequesterLineItems] = useState([]); // Editable copy of requester's items
  const [bidRemarks, setBidRemarks] = useState('');
  // Stores the bid validity date as an ISO date string (YYYY-MM-DD)
  const [bidValidity, setBidValidity] = useState('');
  const [bidSpecialInstructionsAccepted, setBidSpecialInstructionsAccepted] = useState([]); // one boolean per special instruction (mandatory pre-checked)
  const [bidDraftInfo, setBidDraftInfo] = useState(null);
  const [bidDraftLoaded, setBidDraftLoaded] = useState(false);
  const bidDialogInitialSnapshotRef = useRef('');

  // Container types for line items (full list for quote creation)
  const containerTypes = ['All', '20GP', '40GP', '40HC', '45HC', '20RF', '40RF', '20OT', '40OT', '40OTHC', '20FR', '40FR', '40FB'];

  // Line item form
  const [lineItemForm, setLineItemForm] = useState({
    charge_code: '',
    description: '',
    unit: 'Per Container',
    quantity: 1,
    unit_price: 0,
    tax_applicable: false,
    tax_type: '',
    tax_rate: 0,
    is_at_actuals: false
  });

  // Special instruction form
  const [instructionForm, setInstructionForm] = useState({
    instruction_number: '',
    instruction_text: '',
    is_mandatory: true
  });

  useEffect(() => {
    if (!pointsSystemEnabled) {
      setQuotePriority('Normal');
      setPriorityUntilDate('');
      setShowPromoteDialog(false);
      setPromoteQuote(null);
      setPromotePriority('');
      setPromoteUntilDate('');
    }
  }, [pointsSystemEnabled]);

  const resetDraftEditorState = useCallback(() => {
    setLineItems([]);
    setSpecialInstructions([]);
    setQuotePriority('Normal');
    setPriorityUntilDate('');
    setResponseDeadline('');
    setPublicSharingEnabled(true);
    setHsCode('');
    setQuoteAttachments([]);
    setCurrentFormDataInDialog({ scope: [] });
    setEditingDraftQuote(null);
    setEditingPostedInquiry(null);
    postedInquiryEditBaselineRef.current = null;
    draftAutosaveSkipNextRef.current = false;
    draftAutosaveLastSnapshotRef.current = '';
  }, []);

  const handleQuoteAttachmentUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;
    if (quoteAttachments.length + selectedFiles.length > 5) {
      toast.error('You can attach up to 5 PDF documents.');
      return;
    }
    const invalidFile = selectedFiles.find((file) =>
      !file.name.toLowerCase().endsWith('.pdf') || file.size > 10 * 1024 * 1024
    );
    if (invalidFile) {
      toast.error('Each attachment must be a PDF no larger than 10 MB.');
      return;
    }

    try {
      setQuoteAttachmentsUploading(true);
      const response = await uploadsApi.uploadQuoteDocuments(selectedFiles);
      const uploaded = response.data?.files || [];
      setQuoteAttachments((current) => [...current, ...uploaded]);
      toast.success(`${uploaded.length} ${uploaded.length === 1 ? 'document' : 'documents'} attached.`);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to upload PDF documents'));
    } finally {
      setQuoteAttachmentsUploading(false);
    }
  };
  const clearCurrentDraft = useCallback(() => {}, []);

  const upsertDraftQuoteLocal = useCallback((draftQuote) => {
    if (!draftQuote?.quote_id) return;
    setDraftQuotes((prev) => {
      const others = prev.filter((item) => item.quote_id !== draftQuote.quote_id);
      return [draftQuote, ...others];
    });
  }, []);

  // Nomination form
  const [nominationForm, setNominationForm] = useState({
    reason: '',
    rating: 5
  });

  // Completion form
  const [completionForm, setCompletionForm] = useState({
    rating: 5,
    feedback: ''
  });

  // Payment details form (receiver)
  const [paymentDetailsForm, setPaymentDetailsForm] = useState({
    account_name: '',
    bank_name: '',
    account_number: '',
    iban: '',
    swift_code: '',
    bank_address: '',
    deal_amount: '',
    due_amount: '',
    currency: 'USD',
    payment_due_date: ''
  });

  const paymentRequestCurrencies = ['USD', 'EUR', 'GBP', 'INR', 'CNY', 'JPY', 'AED', 'SGD', 'AUD', 'BRL'];
  const [paymentSavedBankDetails, setPaymentSavedBankDetails] = useState([]);
  const [selectedPaymentBankDetailId, setSelectedPaymentBankDetailId] = useState('');

  // Pay Now form (sender - bank transfer proof)
  const [payNowForm, setPayNowForm] = useState({
    payer_account_name: '',
    payer_bank_name: '',
    payer_account_last4: '',
    transaction_reference: '',
    transfer_date: '',
    proof_file: null
  });

  // Normalizes common variants stored in `payment_details.account_name`.
  // Some records miss the required leading "1 ".
  const normalizeRecipientAccountName = (name) => {
    const n = (name || '').trim();
    if (!n) return n;
    const withoutLeading1 = n.toLowerCase().startsWith('1 ') ? n.slice(2).trim() : n;
    if (withoutLeading1.toUpperCase() === 'GLOBE ALLIANCE PRIVATE LIMITED') {
      return '1 GLOBE ALLIANCE PRIVATE LIMITED';
    }
    return n;
  };
  const [paymentDetailsSubmitLoading, setPaymentDetailsSubmitLoading] = useState(false);
  const [paymentDetailsFile, setPaymentDetailsFile] = useState(null);
  const [paymentDetailsFileName, setPaymentDetailsFileName] = useState('');
  const paymentDetailsFileInputRef = useRef(null);
  const [payNowSubmitLoading, setPayNowSubmitLoading] = useState(false);
  const [reminderLoadingQuoteId, setReminderLoadingQuoteId] = useState(null);
  const [rfqCompleteLoadingQuoteId, setRfqCompleteLoadingQuoteId] = useState(null);
  const [rfqAcceptLoadingQuoteId, setRfqAcceptLoadingQuoteId] = useState(null);
  const [rfqReopenLoadingQuoteId, setRfqReopenLoadingQuoteId] = useState(null);
  const [paymentReceivedSubmitLoading, setPaymentReceivedSubmitLoading] = useState(false);
  const [completeSubmitLoading, setCompleteSubmitLoading] = useState(false);
  const [payNowProofFileName, setPayNowProofFileName] = useState('');
  const payNowProofInputRef = useRef(null);
  const pendingQuoteNavigationRef = useRef(null);
  const [pendingQuoteNavigationVersion, setPendingQuoteNavigationVersion] = useState(0);
  const draftAutosaveSkipNextRef = useRef(false);
  const draftAutosaveLastSnapshotRef = useRef('');
  const pendingHiddenReceivedTimersRef = useRef({});

  // Reference data (from API - no dummy data)
  const [chargeCodes, setChargeCodes] = useState([]);
  const [units, setUnits] = useState([]);
  const [oceanPorts, setOceanPorts] = useState([]);
  const [porFpodPorts, setPorFpodPorts] = useState([]);
  const [airports, setAirports] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const hasLoadedQuotesRef = useRef(false);

  const quoteTypes = [
    { id: 'ocean-fcl', name: 'Ocean FCL', icon: Ship, description: 'Full Container Load shipments' },
    { id: 'ocean-lcl', name: 'Ocean LCL', icon: Package, description: 'Less than Container Load' },
    { id: 'air', name: 'Air Freight', icon: Plane, description: 'Air cargo shipments' },
    { id: 'transport', name: 'Transport', icon: Truck, description: 'Inland transportation' },
    { id: 'warehousing', name: 'Warehousing', icon: Warehouse, description: 'Storage solutions' },
    { id: 'breakbulk', name: 'Breakbulk / Ro-Ro', icon: Anchor, description: 'Breakbulk & Roll-on/Roll-off' }
  ];

  const fetchData = useCallback(async () => {
    try {
      if (!hasLoadedQuotesRef.current) {
        setLoading(true);
      }

      // Build common filter params
      const commonFilters = {
        limit: 200,
        ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
        ...(filterQuoteType && { quote_type: filterQuoteType }),
        ...(filterPOL && { pol: filterPOL }),
        ...(filterPOD && { pod: filterPOD }),
        ...(filterDateFrom && { date_from: filterDateFrom }),
        ...(filterDateTo && { date_to: filterDateTo }),
        ...(filterReference && { reference: filterReference })
      };

      // Build query params for sent quotes (my enquiries)
      const sentParams = {
        requester_id: currentMemberId,
        ...commonFilters,
        ...(filterStatus && { status: filterStatus })
      };

      // Build query params for received quotes (available to bid)
      const receivedParams = {
        ...commonFilters
      };

      if (!currentMemberId) return;

      // Only fetch assigned-to-me when we have a member ID (otherwise API would return all quotes)
      const assignedToMePromise = currentMemberId
        ? quotesApi.getQuotes({ assigned_to_member_id: currentMemberId })
        : Promise.resolve({ data: [] });
      const [quotesRes, receivedRes, assignedToMeRes, profileRes, unitsRes, airportsRes, incotermsRes, commoditiesRes] = await Promise.all([
        quotesApi.getQuotes(sentParams),
        quotesApi.getAvailableQuotes(currentMemberId, receivedParams),
        assignedToMePromise,
        profileApi.getProfile(currentMemberId),
        quotesApi.getUnits(),
        directoryApi.getAirports(),
        quotesApi.getIncoterms(),
        quotesApi.getCommodities()
      ]);
      const teamQuotesRes = profileRes.data?.company_id
        ? await quotesApi.getQuotes({
            requester_company_id: profileRes.data.company_id,
            ...commonFilters,
            ...(filterStatus && { status: filterStatus })
          })
        : { data: [] };

      // Categorize quotes – assigned-to-me: only quotes explicitly assigned to current user
      const allSentQuotes = quotesRes.data || [];
      const allTeamQuotes = teamQuotesRes.data || [];
      const allReceivedQuotes = receivedRes.data || [];
      const hiddenEntries = readHiddenReceivedQuotes(currentMemberId);
      const hiddenReceivedQuoteIds = new Set(hiddenEntries.map((e) => e.quote_id));
      const assignedRaw = assignedToMeRes.data || [];
      const matchesStatus = (q) => !filterStatus || q.status === filterStatus;
      setAssignedToMeQuotes(
        assignedRaw.filter(
          (q) => q.assigned_to_member_id === currentMemberId && matchesStatus(q)
        )
      );

      setDraftQuotes(
        allSentQuotes.filter((q) => q.status === 'Draft' && matchesStatus(q))
      );

      // Sent tab: Active quotes I created
      setQuotes(
        allSentQuotes.filter(
          (q) => ['Active', 'Pending'].includes(q.status) && matchesStatus(q)
        )
      );

      setTeamEnquiriesQuotes(
        allTeamQuotes.filter(
          (q) =>
            q.requester_id !== currentMemberId &&
            ['Active', 'Pending'].includes(q.status) &&
            matchesStatus(q)
        )
      );

      // Received tab: Quotes available to bid (not my own, not yet quoted, not nominated — those show in Quoted/Nominated)
      setReceivedQuotes(
        allReceivedQuotes.filter(
          (q) =>
            q.requester_id !== currentMemberId &&
            !q.my_bid_nominated &&
            !q.has_submitted_bid &&
            !hiddenReceivedQuoteIds.has(q.quote_id) &&
            matchesStatus(q)
        )
      );

      // Archive tab: Not-interested quotes (hidden within the last 10 days)
      const hiddenTimestampMap = Object.fromEntries(hiddenEntries.map((e) => [e.quote_id, e.hidden_at]));
      setArchivedQuotes(
        allReceivedQuotes
          .filter((q) => hiddenReceivedQuoteIds.has(q.quote_id))
          .map((q) => ({ ...q, hidden_at: hiddenTimestampMap[q.quote_id] }))
      );

      // Quoted tab: Quotes I've submitted bids on, excluding Completed and nominated (nominated show only in Nominated tab)
      setQuotedQuotes(
        allReceivedQuotes.filter(
          (q) =>
            isQuotedSubmissionVisible(q) &&
            matchesStatus(q)
        )
      );

      // Nominated tab: Quotes where my bid was nominated, excluding Completed (those go to Closed)
      const nominated = [
        ...allSentQuotes.filter((q) => q.status === 'Nominated' && matchesStatus(q)),
        ...allReceivedQuotes.filter(
          (q) => q.my_bid_nominated && q.status !== 'Completed' && matchesStatus(q)
        )
      ];
      setNominatedQuotes(nominated);

      // Closed tab: Completed or Cancelled quotes (sent by me, or received where my bid was nominated)
      const closedFromSent = allSentQuotes.filter(
        (q) => ['Completed', 'Cancelled', 'Expired'].includes(q.status) && matchesStatus(q)
      );
      const closedFromReceived = allReceivedQuotes.filter(
        (q) => q.status === 'Completed' && q.my_bid_nominated && matchesStatus(q)
      );
      setClosedQuotes([...closedFromSent, ...closedFromReceived]);

      setMemberProfile(profileRes.data);
      setUnits(unitsRes.data || []);
      setAirports(airportsRes.data || []);
      setIncoterms(incotermsRes.data || []);
      setCommodities(commoditiesRes.data || []);

      // Load saved filters from localStorage
      const savedFiltersStr = localStorage.getItem('quoteFilters');
      if (savedFiltersStr) {
        try {
          setSavedFilters(JSON.parse(savedFiltersStr));
        } catch (e) {
          console.error('Error loading saved filters:', e);
        }
      }

      // Fetch charge codes and points config from API
      try {
        const [chargeCodesRes, pointsRes] = await Promise.all([
          quotesApi.getChargeCodes(),
          quotesApi.getPointsConfig()
        ]);
        setChargeCodes(chargeCodesRes.data || []);
        setPointsConfig(pointsRes.data);
      } catch (e) {
        console.error('Failed to load charge codes/points config:', e);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load quotes');
    } finally {
      hasLoadedQuotesRef.current = true;
      setLoading(false);
    }
  }, [currentMemberId, debouncedSearchQuery, filterStatus, filterQuoteType, filterPOL, filterPOD, filterDateFrom, filterDateTo, filterReference, isQuotedSubmissionVisible]);

  const openQuoteDetails = useCallback(async (quote) => {
    setSelectedQuote(quote);
    setShowQuoteDetailDialog(true);

    try {
      const response = await quotesApi.getQuote(quote.quote_id);
      const updatedQuote = response.data;
      setSelectedQuote(updatedQuote);

      const updateQuote = (items) => items.map((item) =>
        item.quote_id === updatedQuote.quote_id
          ? { ...item, enquiry_view_count: updatedQuote.enquiry_view_count || 0 }
          : item
      );
      setQuotes(updateQuote);
      setTeamEnquiriesQuotes(updateQuote);
      setNominatedQuotes(updateQuote);
      setClosedQuotes(updateQuote);
      setDraftQuotes(updateQuote);
      setAssignedToMeQuotes(updateQuote);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Unable to load enquiry details'));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const requestedQuote = location.state?.openQuoteDetails;
    if (!requestedQuote) return;

    pendingQuoteNavigationRef.current = {
      quote_id: requestedQuote.quote_id || null,
      reference_number: requestedQuote.reference_number || null,
    };
    // A notification can target /quotes while its lists are already mounted.
    // Trigger the pending-navigation consumer even when those lists did not
    // change as part of the navigation.
    setPendingQuoteNavigationVersion((version) => version + 1);

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const inquiryId = queryParams.get('quote_id') || queryParams.get('inquiry');
    if (!inquiryId || !currentMemberId || loading) return;

    let cancelled = false;
    const openReturnedPublicInquiry = async () => {
      try {
        const response = await quotesApi.getQuote(inquiryId);
        if (cancelled) return;
        const loadedQuote = response.data;
        const ownBid = (loadedQuote.bids || []).find(
          (bid) => bid.bidder_id === currentMemberId
        );
        const companyBid = memberProfile?.company_id
          ? (loadedQuote.bids || []).find(
              (bid) => bid.bidder_company_id === memberProfile.company_id
            )
          : null;
        const hydratedQuote = {
          ...loadedQuote,
          has_submitted_bid: Boolean(ownBid || companyBid),
          my_bid_id: ownBid?.bid_id || null,
          company_bid_by: !ownBid ? companyBid?.bidder_company : null,
          my_bid_nominated:
            Boolean(ownBid?.is_nominated) ||
            loadedQuote.nominated_member_id === currentMemberId,
        };

        if (hydratedQuote.requester_id === currentMemberId) {
          setQuotes((current) => [
            hydratedQuote,
            ...current.filter((item) => item.quote_id !== inquiryId),
          ]);
          setActiveTab('sent');
          setSentSubFilter('mine');
        } else if (hydratedQuote.has_submitted_bid) {
          setQuotedQuotes((current) => [
            hydratedQuote,
            ...current.filter((item) => item.quote_id !== inquiryId),
          ]);
          setActiveTab('quoted');
        } else {
          setReceivedQuotes((current) => [
            hydratedQuote,
            ...current.filter((item) => item.quote_id !== inquiryId),
          ]);
          setActiveTab('received');
        }

        setSelectedQuote(hydratedQuote);
        setShowQuoteDetailDialog(true);
        navigate('/quotes', { replace: true });
      } catch (error) {
        if (!cancelled) {
          toast.error(
            getErrorMessage(
              error.response?.data?.detail,
              'Unable to open the shared inquiry'
            )
          );
          navigate('/quotes', { replace: true });
        }
      }
    };

    openReturnedPublicInquiry();
    return () => {
      cancelled = true;
    };
  }, [
    currentMemberId,
    loading,
    location.search,
    memberProfile?.company_id,
    navigate,
  ]);

  // Keep a debounced version of the search term to avoid refetching on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const searchHighlightTerm = (debouncedSearchQuery || '').trim();
  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlightText = (text, query) => {
    const q = (query || '').trim();
    if (!text || !q) return text;
    const textStr = String(text);
    const qLower = q.toLowerCase();
    const regex = new RegExp(`(${escapeRegExp(q)})`, 'ig');
    const parts = textStr.split(regex);
    return parts.map((part, i) => {
      if (part && part.toLowerCase() === qLower) {
        return (
          <mark key={`${i}-${part}`} className="bg-yellow-200 text-gray-900 px-0.5 rounded">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const addSpecialInstruction = () => {
    if (!instructionForm.instruction_text.trim()) {
      toast.error('Instruction text is required');
      return;
    }
    const nextNumber = `1.${specialInstructions.length + 1}`;
    setSpecialInstructions([...specialInstructions, {
      ...instructionForm,
      instruction_number: nextNumber,
      id: Date.now().toString()
    }]);
    setInstructionForm({ instruction_number: '', instruction_text: '', is_mandatory: true });
    toast.success('Special instruction added');
  };

  const removeInstruction = (id) => {
    setSpecialInstructions(specialInstructions.filter(item => item.id !== id));
  };

  const cloneBidItems = useCallback((items = []) => (
    Array.isArray(items) ? items.map((item) => ({ ...item })) : []
  ), []);

  const getCurrentBidDraftState = useCallback(() => ({
    requesterLineItems: cloneBidItems(requesterLineItems),
    bidLineItems: cloneBidItems(bidLineItems),
    bidRemarks,
    bidValidity,
    bidSpecialInstructionsAccepted: Array.isArray(bidSpecialInstructionsAccepted)
      ? [...bidSpecialInstructionsAccepted]
      : [],
  }), [bidLineItems, bidRemarks, bidSpecialInstructionsAccepted, bidValidity, cloneBidItems, requesterLineItems]);

  const serializeBidDraftState = useCallback((state) => JSON.stringify({
    requesterLineItems: cloneBidItems(state?.requesterLineItems),
    bidLineItems: cloneBidItems(state?.bidLineItems),
    bidRemarks: state?.bidRemarks || '',
    bidValidity: state?.bidValidity || '',
    bidSpecialInstructionsAccepted: Array.isArray(state?.bidSpecialInstructionsAccepted)
      ? [...state.bidSpecialInstructionsAccepted]
      : [],
  }), [cloneBidItems]);

  const applyBidDraftState = useCallback((state) => {
    setRequesterLineItems(cloneBidItems(state?.requesterLineItems));
    setBidLineItems(cloneBidItems(state?.bidLineItems));
    setBidRemarks(state?.bidRemarks || '');
    setBidValidity(state?.bidValidity || '');
    setBidSpecialInstructionsAccepted(
      Array.isArray(state?.bidSpecialInstructionsAccepted)
        ? [...state.bidSpecialInstructionsAccepted]
        : []
    );
  }, [cloneBidItems]);

  const readBidDraft = useCallback((quoteId) => {
    const storageKey = getBidDraftStorageKey(currentMemberId, quoteId);
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      console.error('Failed to read quotation draft:', error);
      return null;
    }
  }, [currentMemberId]);

  const persistBidDraft = useCallback((quoteArg = selectedQuote, stateArg = null, { silent = true } = {}) => {
    const quoteId = quoteArg?.quote_id;
    const storageKey = getBidDraftStorageKey(currentMemberId, quoteId);
    if (!storageKey) return false;

    const state = stateArg || getCurrentBidDraftState();
    const serialized = serializeBidDraftState(state);

    if (serialized === bidDialogInitialSnapshotRef.current) {
      localStorage.removeItem(storageKey);
      setBidDraftInfo(null);
      if (!silent) toast.success('Draft cleared because the form matches the original enquiry state.');
      return false;
    }

    const payload = {
      quote_id: quoteId,
      saved_at: new Date().toISOString(),
      ...state,
    };
    localStorage.setItem(storageKey, JSON.stringify(payload));
    setBidDraftInfo({ quote_id: quoteId, saved_at: payload.saved_at });
    if (!silent) toast.success('Draft saved.');
    return true;
  }, [currentMemberId, getCurrentBidDraftState, selectedQuote, serializeBidDraftState]);

  const deleteBidDraft = useCallback((quoteId, { resetForm = false, silent = false } = {}) => {
    const storageKey = getBidDraftStorageKey(currentMemberId, quoteId || selectedQuote?.quote_id);
    if (storageKey) localStorage.removeItem(storageKey);
    setBidDraftInfo(null);
    setBidDraftLoaded(false);
    if (resetForm && bidDialogInitialSnapshotRef.current) {
      try {
        applyBidDraftState(JSON.parse(bidDialogInitialSnapshotRef.current));
      } catch (error) {
        console.error('Failed to reset quotation draft form:', error);
      }
    }
    if (!silent) toast.success('Draft removed.');
  }, [applyBidDraftState, currentMemberId, selectedQuote?.quote_id]);

  const openSavedBidDraft = useCallback((quoteArg = selectedQuote) => {
    const draft = readBidDraft(quoteArg?.quote_id);
    if (!draft) {
      toast.error('No saved draft found for this quotation.');
      return;
    }
    applyBidDraftState(draft);
    setBidDraftInfo({ quote_id: draft.quote_id, saved_at: draft.saved_at });
    setBidDraftLoaded(true);
    toast.success('Draft loaded.');
  }, [applyBidDraftState, readBidDraft, selectedQuote]);

  // Initialize bid dialog with requester's line items (editable copy)
  // When editing, fetches existing bid and pre-populates the form
  // Sync bid special-instructions accepted when quote has special_instructions (e.g. after load) – all unchecked by default; user must select
  useEffect(() => {
    if (!showBidDialog || !selectedQuote?.special_instructions?.length) return;
    const instr = selectedQuote.special_instructions;
    if (bidSpecialInstructionsAccepted.length !== instr.length) {
      setBidSpecialInstructionsAccepted(instr.map(() => false));
    }
  }, [showBidDialog, selectedQuote?.quote_id, selectedQuote?.special_instructions?.length]);

  useEffect(() => {
    if (!showBidDialog || !selectedQuote?.quote_id || selectedQuote?.has_submitted_bid) return;
    if (!bidDialogInitialSnapshotRef.current || bidDialogInitialSnapshotRef.current === '__loading__') return;
    const currentSnapshot = serializeBidDraftState(getCurrentBidDraftState());
    if (currentSnapshot === bidDialogInitialSnapshotRef.current) return;
    const timer = window.setTimeout(() => {
      persistBidDraft(selectedQuote, getCurrentBidDraftState(), { silent: true });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [getCurrentBidDraftState, persistBidDraft, selectedQuote, serializeBidDraftState, showBidDialog]);

  // When Quote Details modal opens for a quote with a nomination, fetch full quote with bids
  // so the selected vendor pricing remains visible in Closed as well.
  useEffect(() => {
    if (!showQuoteDetailDialog || !selectedQuote?.quote_id) {
      setNominatedBidDetail(null);
      return;
    }
    const nominatedBidId = selectedQuote.nominated_bid_id;
    if (!nominatedBidId) {
      setNominatedBidDetail(null);
      return;
    }
    let cancelled = false;
    setNominatedBidLoading(true);
    setNominatedBidDetail(null);
    quotesApi.getQuote(selectedQuote.quote_id)
      .then((res) => {
        if (cancelled) return;
        const bids = res.data?.bids || [];
        const bid = bids.find((b) => b.bid_id === nominatedBidId);
        setNominatedBidDetail(bid || null);
      })
      .catch(() => {
        if (!cancelled) setNominatedBidDetail(null);
      })
      .finally(() => {
        if (!cancelled) setNominatedBidLoading(false);
      });
    return () => { cancelled = true; };
  }, [showQuoteDetailDialog, selectedQuote?.quote_id, selectedQuote?.nominated_bid_id]);

  useEffect(() => {
    if (!showQuoteDetailDialog || !selectedQuote?.quote_id || !selectedQuote?.has_submitted_bid || !currentMemberId) {
      setSubmittedBidDetail(null);
      setSubmittedBidLoading(false);
      return;
    }

    let cancelled = false;
    setSubmittedBidLoading(true);
    setSubmittedBidDetail(null);

    quotesApi.getQuoteBids(selectedQuote.quote_id)
      .then((res) => {
        if (cancelled) return;
        const bids = Array.isArray(res?.data) ? res.data : [];
        const myBid = bids.find((bid) => bid?.bidder_id === currentMemberId || bid?.bid_id === selectedQuote?.my_bid_id);
        setSubmittedBidDetail(myBid || null);
      })
      .catch(() => {
        if (!cancelled) setSubmittedBidDetail(null);
      })
      .finally(() => {
        if (!cancelled) setSubmittedBidLoading(false);
      });

    return () => { cancelled = true; };
  }, [showQuoteDetailDialog, selectedQuote?.quote_id, selectedQuote?.has_submitted_bid, selectedQuote?.my_bid_id, currentMemberId]);

  const openBidDialog = async (quote) => {
    setSelectedQuote(quote);
    setBidLineItems([]);
    setBidRemarks('');
    setBidValidity(addDaysIsoLocal(30));
    setRequesterLineItems([]);
    setBidSpecialInstructionsAccepted((quote.special_instructions || []).map(() => false));
    setBidDraftLoaded(false);
    bidDialogInitialSnapshotRef.current = '__loading__';
    setShowBidDialog(true);

    const mapLineItem = (item, idx, prefix) => ({
      id: item.id || `${prefix}-${idx}-${Date.now()}`,
      charge_code: item.charge_code || '',
      description: item.description || '',
      unit: item.unit || 'Per Container',
      quantity: item.quantity ?? 1,
      unit_price: item.unit_price ?? 0,
      tax_applicable: item.tax_applicable || false,
      tax_type: (!item.tax_type || item.tax_type === 'VAT') ? '' : item.tax_type,
      tax_rate: item.tax_rate ?? 0,
      is_at_actuals: item.is_at_actuals || false,
      container_type: item.container_type || 'All',
      is_from_requester: item.is_from_requester || false
    });

    let initialBidState = {
      requesterLineItems: [],
      bidLineItems: [],
      bidRemarks: '',
      bidValidity: addDaysIsoLocal(30),
      bidSpecialInstructionsAccepted: (quote.special_instructions || []).map(() => false),
    };

    if (quote.has_submitted_bid && currentMemberId) {
      setBidDialogLoading(true);
      try {
        const bidsRes = await quotesApi.getQuoteBids(quote.quote_id);
        const myBid = (bidsRes.data || []).find(b => b.bidder_id === currentMemberId);
        if (myBid) {
          setBidRemarks(myBid.remarks || myBid.notes || '');
          // Infer validity date from explicit quote_valid_until (if present),
          // otherwise from created_at + validity_days (if available).
          let inferredDate = null;
          if (myBid.quote_valid_until) {
            inferredDate = new Date(myBid.quote_valid_until);
          } else if (myBid.created_at && (myBid.validity_days != null)) {
            const base = new Date(myBid.created_at);
            base.setHours(0, 0, 0, 0);
            base.setDate(base.getDate() + (myBid.validity_days || 0));
            inferredDate = base;
          }
          if (inferredDate && !Number.isNaN(inferredDate.getTime())) {
            setBidValidity(inferredDate.toISOString().slice(0, 10));
          } else {
            setBidValidity('');
          }
          const senderCount = (quote.sender_line_items || []).length;
          const bidItems = myBid.line_items || [];
          const reqItems = bidItems.slice(0, senderCount).map((item, idx) => ({ ...mapLineItem(item, idx, 'req'), is_from_requester: true }));
          const addItems = bidItems.slice(senderCount).map((item, idx) => mapLineItem(item, idx, 'add'));
          setRequesterLineItems(reqItems);
          setBidLineItems(addItems);
          // Restore special instructions as all checked when editing (user had to accept them to submit)
          const instr = quote.special_instructions || [];
          if (instr.length) setBidSpecialInstructionsAccepted(instr.map(() => true));
          initialBidState = {
            requesterLineItems: reqItems,
            bidLineItems: addItems,
            bidRemarks: myBid.remarks || myBid.notes || '',
            bidValidity: inferredDate && !Number.isNaN(inferredDate.getTime()) ? inferredDate.toISOString().slice(0, 10) : '',
            bidSpecialInstructionsAccepted: instr.length ? instr.map(() => true) : [],
          };
        } else {
          // Fallback: no existing bid found, use requester items only
          if (quote.sender_line_items && quote.sender_line_items.length > 0) {
            const fallbackItems = quote.sender_line_items.map((item, idx) => mapLineItem(
              { ...item, unit_price: 0 }, idx, 'req'
            ));
            setRequesterLineItems(fallbackItems);
            initialBidState = {
              ...initialBidState,
              requesterLineItems: fallbackItems,
            };
          }
        }
      } catch (err) {
        toast.error('Failed to load quotation details');
        if (quote.sender_line_items && quote.sender_line_items.length > 0) {
          const fallbackItems = quote.sender_line_items.map((item, idx) => mapLineItem(
            { ...item, unit_price: 0 }, idx, 'req'
          ));
          setRequesterLineItems(fallbackItems);
          initialBidState = {
            ...initialBidState,
            requesterLineItems: fallbackItems,
          };
        }
      } finally {
        setBidDialogLoading(false);
      }
    } else {
      // New bid: create editable copy of requester's line items
      if (quote.sender_line_items && quote.sender_line_items.length > 0) {
        const editableItems = quote.sender_line_items.map((item, idx) => mapLineItem(
          { ...item, unit_price: 0 }, idx, 'req'
        ));
        editableItems.forEach(i => { i.is_from_requester = true; });
        setRequesterLineItems(editableItems);
        initialBidState = {
          ...initialBidState,
          requesterLineItems: editableItems,
        };
      }
    }

    bidDialogInitialSnapshotRef.current = serializeBidDraftState(initialBidState);
    if (!quote.has_submitted_bid) {
      const savedDraft = readBidDraft(quote.quote_id);
      setBidDraftInfo(savedDraft ? { quote_id: savedDraft.quote_id, saved_at: savedDraft.saved_at } : null);
    } else {
      setBidDraftInfo(null);
    }
  };

  const applySubmittedBidToLocalState = useCallback((quoteId, bidId) => {
    const markQuoted = (quote) => {
      if (!quote || quote.quote_id !== quoteId) return quote;
      return {
        ...quote,
        has_submitted_bid: true,
        my_bid_id: bidId || quote.my_bid_id || null,
      };
    };

    let movedQuote = null;

    setReceivedQuotes((prev) => {
      const next = [];
      prev.forEach((quote) => {
        if (quote.quote_id === quoteId) {
          movedQuote = markQuoted(quote);
          return;
        }
        next.push(quote);
      });
      return next;
    });

    setAssignedToMeQuotes((prev) =>
      prev.map((quote) => {
        const updated = markQuoted(quote);
        if (updated !== quote) movedQuote = updated;
        return updated;
      })
    );

    setQuotes((prev) => prev.map(markQuoted));
    setClosedQuotes((prev) => prev.map(markQuoted));
    setNominatedQuotes((prev) => prev.map(markQuoted));
    setSelectedQuote((prev) => (prev?.quote_id === quoteId ? markQuoted(prev) : prev));

    setQuotedQuotes((prev) => {
      const existing = prev.find((quote) => quote.quote_id === quoteId);
      if (existing) {
        return prev.map(markQuoted);
      }
      if (movedQuote && !movedQuote.my_bid_nominated) {
        return [movedQuote, ...prev];
      }
      return prev;
    });
  }, []);

  const handleHideReceivedQuote = useCallback((quote) => {
    if (!quote?.quote_id) return;
    setHideReceivedConfirmQuote(quote);
    setShowHideReceivedConfirmDialog(true);
  }, []);

  const finalizeHideReceivedQuote = useCallback((quote) => {
    if (!currentMemberId || !quote?.quote_id) return;
    const nextHiddenQuoteIds = Array.from(new Set([
      ...readHiddenReceivedQuoteIds(currentMemberId),
      quote.quote_id,
    ]));
    writeHiddenReceivedQuoteIds(currentMemberId, nextHiddenQuoteIds);
    setPendingHiddenReceivedQuotes((prev) => {
      const next = { ...prev };
      delete next[quote.quote_id];
      return next;
    });
    delete pendingHiddenReceivedTimersRef.current[quote.quote_id];
    setReceivedQuotes((prev) => prev.filter((item) => item.quote_id !== quote.quote_id));
    setSelectedQuote((prev) => (prev?.quote_id === quote.quote_id ? null : prev));
  }, [currentMemberId]);

  const confirmHideReceivedQuote = useCallback(() => {
    const quote = hideReceivedConfirmQuote;
    if (!quote?.quote_id) return;
    setShowHideReceivedConfirmDialog(false);
    setHideReceivedConfirmQuote(null);
    if (pendingHiddenReceivedTimersRef.current[quote.quote_id]) {
      window.clearTimeout(pendingHiddenReceivedTimersRef.current[quote.quote_id]);
    }
    setPendingHiddenReceivedQuotes((prev) => ({
      ...prev,
      [quote.quote_id]: {
        quote_id: quote.quote_id,
        reference_number: quote.reference_number || '',
      },
    }));
    pendingHiddenReceivedTimersRef.current[quote.quote_id] = window.setTimeout(() => {
      finalizeHideReceivedQuote(quote);
    }, 5000);
  }, [finalizeHideReceivedQuote, hideReceivedConfirmQuote]);

  const undoHideReceivedQuote = useCallback((quoteId) => {
    const timer = pendingHiddenReceivedTimersRef.current[quoteId];
    if (timer) {
      window.clearTimeout(timer);
      delete pendingHiddenReceivedTimersRef.current[quoteId];
    }
    setPendingHiddenReceivedQuotes((prev) => {
      const next = { ...prev };
      delete next[quoteId];
      return next;
    });
  }, []);

  useEffect(() => () => {
    Object.values(pendingHiddenReceivedTimersRef.current).forEach((timer) => {
      window.clearTimeout(timer);
    });
    pendingHiddenReceivedTimersRef.current = {};
  }, []);

  const restoreArchivedQuote = useCallback((quoteId) => {
    if (!currentMemberId || !quoteId) return;
    const entries = readHiddenReceivedQuotes(currentMemberId).filter((e) => e.quote_id !== quoteId);
    writeHiddenReceivedQuotes(currentMemberId, entries);
    // Move the quote back to received and remove from archive
    setArchivedQuotes((prev) => {
      const restored = prev.find((q) => q.quote_id === quoteId);
      if (restored) {
        setReceivedQuotes((r) => [restored, ...r]);
      }
      return prev.filter((q) => q.quote_id !== quoteId);
    });
    toast.success('Quote restored to Received');
  }, [currentMemberId]);

  // Helpers for card: scope, standard container, special container (from quote or form_data)
  const STANDARD_CONTAINER_TYPES = ['20GP', '20HC', '40GP', '40HC', '45HC'];
  const getQuoteScopeDisplay = (quote) => {
    const scope = quote.form_data?.scope || quote.scope;
    if (!scope || !Array.isArray(scope) || scope.length === 0) return '—';
    return scope.join(', ');
  };
  /** Show full port name with short code in brackets: "Full Name (CODE)" (avoid duplicating if name already contains "(CODE)") */
  const formatPortDisplay = (name, code) => {
    if (!code && !name) return 'N/A';
    const n = (name || '').trim();
    const c = (code || '').trim();
    if (n && c && (n.endsWith(`(${c})`) || n === c)) return n;
    if (n && c && n !== c) return `${n} (${c})`;
    return n || c || 'N/A';
  };
  const formatPortCountryDisplay = (port, country, fallbackCode) => {
    const p = (port || '').trim();
    const c = (country || '').trim();
    const code = (fallbackCode || '').trim();
    if (p && c) return `${p}-${c}`;
    return p || c || code || 'N/A';
  };
  const formatAirportDisplay = (value) => {
    const raw = typeof value === 'string' ? value.trim() : '';
    if (!raw) return '';
    const airport = airports.find((item) => (item?.code || '').toUpperCase() === raw.toUpperCase());
    if (!airport) return raw;

    const code = (airport.code || raw).trim();
    const name = (airport.name || '').trim();
    const nameAndLocode = (airport.nameAndLocode || '').trim();

    if (nameAndLocode && !nameAndLocode.includes('(')) return `${nameAndLocode} (${code})`;
    if (nameAndLocode) return nameAndLocode;
    if (name && name !== code) return `${name} (${code})`;
    return code;
  };
  const getQuoteRouteDisplay = (quote) => {
    const fd = quote?.form_data || {};
    const firstText = (...values) => values.find((v) => typeof v === 'string' && v.trim() !== '') || '';
    const joinRouteParts = (...values) => values
      .filter((v) => typeof v === 'string' && v.trim() !== '')
      .map((v) => v.trim())
      .join('-');

    const isTransportQuote = quote?.quote_type === 'Transport';
    const isWarehousingQuote = quote?.quote_type === 'Warehousing';
    const isBreakbulkQuote = quote?.quote_type === 'Breakbulk';

    const originPort = isBreakbulkQuote
      ? formatPortCountryDisplay(
          firstText(fd?.origin?.port, quote?.origin_port?.name, fd?.polName, fd?.pickupPort, fd?.porName),
          firstText(fd?.origin?.country, quote?.origin_port?.country, fd?.originCountry, fd?.polCountry),
          firstText(quote?.origin_port?.code, fd?.pol, fd?.por)
        )
      : formatPortDisplay(
          firstText(quote?.origin_port?.name, fd?.polName, fd?.origin?.port, fd?.pickupPort, fd?.porName),
          firstText(quote?.origin_port?.code, fd?.pol, fd?.por)
        );
    const destinationPort = isBreakbulkQuote
      ? formatPortCountryDisplay(
          firstText(fd?.destination?.port, quote?.destination_port?.name, fd?.podName, fd?.deliveryPort, fd?.fpodName),
          firstText(fd?.destination?.country, quote?.destination_port?.country, fd?.destinationCountry, fd?.podCountry),
          firstText(quote?.destination_port?.code, fd?.pod, fd?.fpod)
        )
      : formatPortDisplay(
          firstText(quote?.destination_port?.name, fd?.podName, fd?.destination?.port, fd?.deliveryPort, fd?.fpodName),
          firstText(quote?.destination_port?.code, fd?.pod, fd?.fpod)
        );

    const transportOrigin = joinRouteParts(
      fd?.pickupLocation?.city,
      fd?.pickupLocation?.country,
      fd?.pickupLocation?.postalCode
    );
    const transportDestination = joinRouteParts(
      fd?.deliveryLocation?.city,
      fd?.deliveryLocation?.country,
      fd?.deliveryLocation?.postalCode
    );
    const warehousingLocation = joinRouteParts(
      fd?.location?.city,
      fd?.location?.country
    );
    const airOrigin = formatAirportDisplay(firstText(fd?.origin, quote?.origin_city));
    const airDestination = formatAirportDisplay(firstText(fd?.destination, quote?.destination_city));

    const originText = firstText(
      isWarehousingQuote ? warehousingLocation : '',
      isTransportQuote ? transportOrigin : '',
      originPort !== 'N/A' ? originPort : '',
      airOrigin,
      fd?.pickupLocation?.city,
      fd?.pickupLocation?.address
    ) || 'N/A';
    const destinationText = firstText(
      isWarehousingQuote ? warehousingLocation : '',
      isTransportQuote ? transportDestination : '',
      destinationPort !== 'N/A' ? destinationPort : '',
      airDestination,
      fd?.deliveryLocation?.city,
      fd?.deliveryLocation?.address
    ) || '';

    return { originText, destinationText };
  };
  const getQuoteStandardContainerDisplay = (quote) => {
    const containers = quote.containers || quote.form_data?.containers;
    if (!containers) return '—';
    const parts = [];
    if (Array.isArray(containers)) {
      STANDARD_CONTAINER_TYPES.forEach((type) => {
        const c = containers.find((x) => x.container_type === type);
        const qty = c?.quantity ?? 0;
        if (qty > 0) parts.push(`${qty}x ${type}`);
      });
    } else if (typeof containers === 'object') {
      STANDARD_CONTAINER_TYPES.forEach((type) => {
        const qty = containers[type] ?? 0;
        if (qty > 0) parts.push(`${qty}x ${type}`);
      });
    }
    return parts.length > 0 ? parts.join(', ') : '—';
  };
  const getQuoteSpecialContainerDisplay = (quote) => {
    const reefer = quote.reefer || quote.form_data?.reefer || {};
    const oog = quote.oogContainers || quote.form_data?.oogContainers || [];
    const parts = [];
    if (typeof reefer === 'object') {
      Object.entries(reefer).forEach(([type, qty]) => {
        if (qty > 0) parts.push(`${qty}x ${type}`);
      });
    }
    if (Array.isArray(oog) && oog.length > 0) parts.push(`${oog.length} Special Containers`);
    return parts.length > 0 ? parts.join(', ') : '—';
  };
  /** Special container: types only (for default label); full details on hover */
  const getQuoteSpecialContainerTypesOnly = (quote) => {
    const reefer = quote.reefer || quote.form_data?.reefer || {};
    const oog = quote.oogContainers || quote.form_data?.oogContainers || [];
    const types = [];
    if (typeof reefer === 'object') {
      Object.entries(reefer).forEach(([type, qty]) => {
        if (qty > 0) types.push(type);
      });
    }
    if (Array.isArray(oog) && oog.length > 0) types.push('Special Containers');
    return types.length > 0 ? types.join(', ') : '—';
  };
  /** Full details for tooltip: summary + per-OOG details if available */
  const getQuoteSpecialContainerFullDetails = (quote) => {
    const summary = getQuoteSpecialContainerDisplay(quote);
    const oog = quote.oogContainers || quote.form_data?.oogContainers || [];
    if (!Array.isArray(oog) || oog.length === 0) return summary;
    const lines = [summary];
    oog.forEach((c, i) => {
      const type = c.type || 'OOG';
      const gauge = c.gauge === 'out' ? 'Out-Gauge' : 'In-Gauge';
      const dims = [c.length, c.width, c.height].filter(Boolean).length
        ? `${c.length || 0}×${c.width || 0}×${c.height || 0} ${c.unit || 'mm'}`
        : '';
      const weight = c.weight ? `${c.weight} kg` : '';
      const desc = c.cargoDescription ? ` – ${c.cargoDescription}` : '';
      const part = [type, gauge, dims, weight].filter(Boolean).join(', ') + desc;
      if (part.replace(/,?\s*–?\s*$/, '')) lines.push(`  • ${part}`);
    });
    return lines.join('\n');
  };

  const getContainerTypesFromData = (source) => {
    if (!source) return containerTypes;
    const present = new Set(['All']);
    const containers = source.containers || source.form_data?.containers;
    const reefer = source.reefer || source.form_data?.reefer || {};
    const oogContainers = source.oogContainers || source.form_data?.oogContainers || [];

    if (Array.isArray(containers)) {
      containers.forEach((c) => {
        const type = c?.container_type;
        if (type && (c?.quantity ?? 0) > 0) present.add(type);
      });
    } else if (containers && typeof containers === 'object') {
      Object.entries(containers).forEach(([type, qty]) => {
        if (qty > 0) present.add(type);
      });
    }
    if (reefer && typeof reefer === 'object') {
      Object.entries(reefer).forEach(([type, qty]) => {
        if (qty > 0) present.add(type);
      });
    }

    if (Array.isArray(oogContainers)) {
      oogContainers.forEach((container) => {
        const type = container?.type || container?.container_type;
        if (type) present.add(type);
      });
    }

    if (present.size <= 1) return containerTypes;
    return containerTypes.filter((ct) => present.has(ct));
  };

  // Container types allowed in Submit Quotation: use quote/request data if present; otherwise all types
  const getQuoteContainerTypesForBid = (quote) => getContainerTypesFromData(quote);
  const getDialogContainerTypesForBid = () => getContainerTypesFromData(currentFormDataInDialog);

  // Generate compact cargo summary for quote cards
  const getCargoSummary = (quote) => {
    const summary = [];
    const fd = quote?.form_data || {};

    // Container summary (FCL)
    if (quote.containers) {
      const containerParts = [];
      Object.entries(quote.containers).forEach(([type, qty]) => {
        if (qty > 0) containerParts.push(`${qty}x ${type}`);
      });
      if (containerParts.length > 0) {
        summary.push({ icon: '🚢', text: containerParts.join(', '), type: 'containers' });
      }
    }

    // OOG containers
    if (quote.oogContainers && quote.oogContainers.length > 0) {
      summary.push({ icon: '📦', text: `${quote.oogContainers.length} Special Containers`, type: 'oog' });
    }

    // Weight
    if (quote.weight_kg) {
      const tons = (quote.weight_kg / 1000).toFixed(1);
      summary.push({ icon: '⚖️', text: `${tons} tons`, type: 'weight' });
    }

    // Volume CBM (for LCL, Breakbulk)
    if (quote.volume_cbm) {
      summary.push({ icon: '📐', text: `${quote.volume_cbm} CBM`, type: 'volume' });
    }

    // Air tonnage
    if (quote.quote_type === 'Air' && quote.chargeable_weight) {
      summary.push({ icon: '✈️', text: `${quote.chargeable_weight} kg chargeable`, type: 'air' });
    }

    // Breakbulk specifics
    if (quote.quote_type === 'Breakbulk') {
      if (quote.breakbulk_cbm) summary.push({ icon: '📦', text: `${quote.breakbulk_cbm} CBM`, type: 'breakbulk' });
      if (quote.breakbulk_tonnage) summary.push({ icon: '⚓', text: `${quote.breakbulk_tonnage} MT`, type: 'breakbulk' });
    }

    const insuranceDisplay = getInsuranceDisplay(fd);
    if (insuranceDisplay) {
      summary.push({ icon: '🛡️', text: `Insurance ${insuranceDisplay}`, type: 'insurance' });
    }

    return summary;
  };

  // Render cargo summary as compact badges
  const renderCargoSummary = (quote) => {
    const summary = getCargoSummary(quote);
    if (summary.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {summary.map((item, idx) => (
          <span
            key={idx}
            className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded"
          >
            <span className="mr-1">{item.icon}</span>
            {item.text}
          </span>
        ))}
      </div>
    );
  };

  const renderSubmitQuoteModeDetails = (quote) => {
    if (!quote) return null;

    const fd = quote.form_data || {};
    const qt = quote.quote_type || '';
    const isOceanFCL = qt === 'Ocean FCL';
    const isOceanLCL = qt === 'Ocean LCL';
    const isOcean = isOceanFCL || isOceanLCL;
    const isTransport = qt === 'Transport';
    const isAir = qt === 'Air';
    const isWarehousing = qt === 'Warehousing';
    const isBreakbulk = qt === 'Breakbulk';
    const scope = fd.scope || [];
    const scopeDisplayOptions = (() => {
      const modeId = qt === 'Ocean FCL' ? 'ocean-fcl' : qt === 'Ocean LCL' ? 'ocean-lcl' : qt === 'Air' ? 'air' : qt === 'Transport' ? 'transport' : qt === 'Warehousing' ? 'warehousing' : qt === 'Breakbulk' ? 'breakbulk' : null;
      return (modeId && SCOPE_OPTIONS_BY_MODE[modeId]) || [];
    })();
    const containersObj = fd.containers || quote.containers;
    const oogList = fd.oogContainers || quote.oogContainers || [];
    const reefer = fd.reefer || quote.reefer || {};
    const standardContainerTypes = ['20GP', '20HC', '40GP', '40HC', '45HC'];
    const pickup = fd.pickupLocation || {};
    const delivery = fd.deliveryLocation || {};
    const insuranceDisplay = getInsuranceDisplay(fd);
    const packingListInfo = getFileDownloadInfo(fd.packingListFile, 'Packing list');

    return (
      <div className="space-y-4">
        {insuranceDisplay && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-3">Insurance</h4>
            <p className="text-sm">{insuranceDisplay}</p>
          </div>
        )}

        {isTransport && (
          <>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Service Type</h4>
              <p className="font-medium">{fd.serviceType || 'Transport Only'}</p>
            </div>
            {(pickup.address || pickup.city || pickup.country || quote.origin_city) && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Pickup Location</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {pickup.address && <div><Label className="text-xs text-gray-500">Address</Label><p>{pickup.address}</p></div>}
                  {(pickup.city || quote.origin_city) && <div><Label className="text-xs text-gray-500">City</Label><p>{pickup.city || quote.origin_city}</p></div>}
                  {pickup.country && <div><Label className="text-xs text-gray-500">Country</Label><p>{pickup.country}</p></div>}
                  {pickup.postalCode && <div><Label className="text-xs text-gray-500">Postal Code</Label><p>{pickup.postalCode}</p></div>}
                  {fd.pickupDate && <div><Label className="text-xs text-gray-500">Pickup Date</Label><p>{fd.pickupDate}</p></div>}
                </div>
              </div>
            )}
            {(delivery.address || delivery.city || delivery.country || quote.destination_city) && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Delivery Location</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {delivery.address && <div><Label className="text-xs text-gray-500">Address</Label><p>{delivery.address}</p></div>}
                  {(delivery.city || quote.destination_city) && <div><Label className="text-xs text-gray-500">City</Label><p>{delivery.city || quote.destination_city}</p></div>}
                  {delivery.country && <div><Label className="text-xs text-gray-500">Country</Label><p>{delivery.country}</p></div>}
                  {delivery.postalCode && <div><Label className="text-xs text-gray-500">Postal Code</Label><p>{delivery.postalCode}</p></div>}
                  {fd.deliveryDate && <div><Label className="text-xs text-gray-500">Delivery Date</Label><p>{fd.deliveryDate}</p></div>}
                </div>
              </div>
            )}
            {fd.requiresCustomsClearance && fd.customsDetails && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3">Customs Clearance Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {fd.customsDetails.exportCountry && <div><Label className="text-xs text-gray-500">Export Country</Label><p>{fd.customsDetails.exportCountry}</p></div>}
                  {fd.customsDetails.importCountry && <div><Label className="text-xs text-gray-500">Import Country</Label><p>{fd.customsDetails.importCountry}</p></div>}
                  {fd.customsDetails.hsCode && <div><Label className="text-xs text-gray-500">HS Code</Label><p>{fd.customsDetails.hsCode}</p></div>}
                  {fd.customsDetails.customsValue && <div><Label className="text-xs text-gray-500">Invoice Value (USD)</Label><p>{fd.customsDetails.customsValue}</p></div>}
                </div>
              </div>
            )}
          </>
        )}

        {isAir && (
          <>
            {(scope.length > 0 || scopeDisplayOptions.length > 0) && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3">Scope (Select all that apply)</h4>
                <div className="flex flex-wrap gap-2">
                  {scopeDisplayOptions.length > 0
                    ? scopeDisplayOptions.map((opt) => (
                        <span key={opt} className={`inline-flex items-center px-3 py-1 rounded text-sm ${scope.includes(opt) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
                          {scope.includes(opt) ? '✓' : '○'} {opt}
                        </span>
                      ))
                    : scope.map((s) => (
                        <span key={s} className="inline-flex items-center px-3 py-1 rounded text-sm bg-blue-100 text-blue-800">✓ {s}</span>
                      ))}
                </div>
              </div>
            )}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Route Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {formatAirportDisplay(fd.origin || quote.origin_city) && <div><Label className="text-xs text-gray-500">Origin Airport</Label><p>{formatAirportDisplay(fd.origin || quote.origin_city)}</p></div>}
                {formatAirportDisplay(fd.destination || quote.destination_city) && <div><Label className="text-xs text-gray-500">Destination Airport</Label><p>{formatAirportDisplay(fd.destination || quote.destination_city)}</p></div>}
                {fd.pickupAddress && <div className="md:col-span-2"><Label className="text-xs text-gray-500">Pick up Address</Label><p>{fd.pickupAddress}</p></div>}
                {fd.deliveryAddress && <div className="md:col-span-2"><Label className="text-xs text-gray-500">Destination Address</Label><p>{fd.deliveryAddress}</p></div>}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center"><Package size={16} className="mr-2 text-gray-500" />Cargo Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                {(quote.commodity || fd.commodity) && <div><Label className="text-xs text-gray-500">Commodity</Label><p>{quote.commodity || fd.commodity}</p></div>}
                {fd.cargoType && <div><Label className="text-xs text-gray-500">Cargo Type</Label><p>{fd.cargoType}</p></div>}
                {(fd.totalWeight || quote.weight_kg) && <div><Label className="text-xs text-gray-500">Total Weight</Label><p>{fd.totalWeight || quote.weight_kg} {fd.weightUnit || 'kg'}</p></div>}
                {fd.pieces && <div><Label className="text-xs text-gray-500">Pieces</Label><p>{fd.pieces}</p></div>}
                {fd.serviceLevel && <div><Label className="text-xs text-gray-500">Service Level</Label><p>{fd.serviceLevel}</p></div>}
                {fd.incoterm && <div><Label className="text-xs text-gray-500">Incoterm</Label><p>{Array.isArray(fd.incoterm) ? fd.incoterm.join(', ') : fd.incoterm}</p></div>}
              </div>
              {renderQuoteDimensions(fd, 'cm')}
            </div>
          </>
        )}

        {isWarehousing && (
          <>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Warehouse Location</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {fd.location?.city && <div><Label className="text-xs text-gray-500">City</Label><p>{fd.location.city}</p></div>}
                {fd.location?.country && <div><Label className="text-xs text-gray-500">Country</Label><p>{fd.location.country}</p></div>}
                {fd.location?.address && <div className="md:col-span-2"><Label className="text-xs text-gray-500">Address</Label><p>{fd.location.address}</p></div>}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Space Requirements</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                {fd.startDate && <div><Label className="text-xs text-gray-500">Start Date</Label><p>{fd.startDate}</p></div>}
                {fd.duration && <div><Label className="text-xs text-gray-500">Duration</Label><p>{fd.duration} {fd.durationUnit || 'months'}</p></div>}
                {fd.spaceRequired && <div><Label className="text-xs text-gray-500">Space Required</Label><p>{fd.spaceRequired} {fd.spaceUnit || 'sqm'}</p></div>}
                {fd.storageType && <div><Label className="text-xs text-gray-500">Storage Type</Label><p>{fd.storageType}</p></div>}
                {(quote.commodity || fd.commodity) && <div><Label className="text-xs text-gray-500">Commodity</Label><p>{quote.commodity || fd.commodity}</p></div>}
                {fd.palletCount && <div><Label className="text-xs text-gray-500">Pallet Count</Label><p>{fd.palletCount} ({fd.palletType || 'Euro'})</p></div>}
              </div>
            </div>
            {packingListInfo && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3">Documents</h4>
                <a
                  href={packingListInfo.url}
                  download={packingListInfo.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  <Download size={14} className="mr-2" />
                  {packingListInfo.name}
                </a>
              </div>
            )}
          </>
        )}

        {isBreakbulk && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Route & Cargo</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {fd.origin?.port && <div><Label className="text-xs text-gray-500">Origin Port</Label><p>{fd.origin.port} {fd.origin.country && `(${fd.origin.country})`}</p></div>}
              {fd.destination?.port && <div><Label className="text-xs text-gray-500">Destination Port</Label><p>{fd.destination.port} {fd.destination.country && `(${fd.destination.country})`}</p></div>}
              {(quote.commodity || fd.commodity) && <div><Label className="text-xs text-gray-500">Commodity</Label><p>{quote.commodity || fd.commodity}</p></div>}
              {(fd.totalWeight || quote.weight_kg) && <div><Label className="text-xs text-gray-500">Total Weight (MT)</Label><p>{fd.totalWeight || quote.weight_kg}</p></div>}
            </div>
            {renderQuoteDimensions(fd, 'm')}
          </div>
        )}

        {isOcean && (
          <>
            {(scope.length > 0 || scopeDisplayOptions.length > 0) && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3">Scope (Select all that apply)</h4>
                <div className="flex flex-wrap gap-2">
                  {scopeDisplayOptions.length > 0
                    ? scopeDisplayOptions.map((opt) => (
                        <span key={opt} className={`inline-flex items-center px-3 py-1 rounded text-sm ${scope.includes(opt) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
                          {scope.includes(opt) ? '✓' : '○'} {opt}
                        </span>
                      ))
                    : scope.map((s) => (
                        <span key={s} className="inline-flex items-center px-3 py-1 rounded text-sm bg-blue-100 text-blue-800">✓ {s}</span>
                      ))}
                </div>
              </div>
            )}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Port Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {(fd.por || quote.por) && <div><Label className="text-xs text-gray-500">POR (Place of Receipt)</Label><p>{formatPortDisplay(fd.porName, fd.por || quote.por)} {fd.porCountry && `— ${fd.porCountry}`}</p></div>}
                <div><Label className="text-xs text-gray-500">POL (Port of Loading)</Label><p>{formatPortDisplay(quote.origin_port?.name, quote.origin_port?.code) || formatPortDisplay(fd.polName, fd.pol) || 'N/A'}</p></div>
                <div><Label className="text-xs text-gray-500">POD (Port of Discharge)</Label><p>{formatPortDisplay(quote.destination_port?.name, quote.destination_port?.code) || formatPortDisplay(fd.podName, fd.pod) || 'N/A'}</p></div>
                {(fd.fpod || quote.fpod) && <div><Label className="text-xs text-gray-500">FPOD (Final Place of Delivery)</Label><p>{formatPortDisplay(fd.fpodName, fd.fpod || quote.fpod)} {fd.fpodCountry && `— ${fd.fpodCountry}`}</p></div>}
              </div>
            </div>
          </>
        )}

        {isOceanFCL && (
          <>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center"><Ship size={16} className="mr-2 text-gray-500" />Standard Containers</h4>
              <div className="grid grid-cols-3 gap-4">
                {standardContainerTypes.map((type) => {
                  const containerEntry = Array.isArray(containersObj) ? containersObj.find(c => c.container_type === type) : null;
                  const qty = containersObj && typeof containersObj === 'object' && !Array.isArray(containersObj)
                    ? containersObj[type] ?? 0
                    : containerEntry?.quantity ?? 0;
                  return (
                    <div key={type}>
                      <Label className="text-xs text-gray-500">{type}</Label>
                      <p className="font-medium">{qty}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            {oogList.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3">Special Containers</h4>
                <div className="space-y-3">
                  {oogList.map((container, idx) => (
                    <div key={container.id || idx} className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div><Label className="text-xs text-gray-500">Type</Label><p>{container.type || '-'}</p></div>
                        <div><Label className="text-xs text-gray-500">Gauge</Label><p>{container.gauge === 'out' ? 'Out-Gauge' : 'In-Gauge'}</p></div>
                        <div><Label className="text-xs text-gray-500">Weight (kg)</Label><p>{container.weight ?? '-'}</p></div>
                        <div className="md:col-span-2"><Label className="text-xs text-gray-500">Description</Label><p>{container.cargoDescription || '-'}</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Reefer &amp; Special Cargo</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div><Label className="text-xs text-gray-500">20&apos; Reefer</Label><p>{reefer['20RF'] || reefer['20RF'] === 0 ? reefer['20RF'] : '0'}</p></div>
                <div><Label className="text-xs text-gray-500">Temp Range (20&apos;)</Label><p>{(fd.tempRange20 || quote.tempRange20) || '-'}</p></div>
                <div><Label className="text-xs text-gray-500">40&apos; Reefer</Label><p>{reefer['40RF'] || reefer['40RF'] === 0 ? reefer['40RF'] : '0'}</p></div>
                <div><Label className="text-xs text-gray-500">Temp Range (40&apos;)</Label><p>{(fd.tempRange40 || quote.tempRange40) || '-'}</p></div>
              </div>
            </div>
          </>
        )}

        {isOceanLCL && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-3 flex items-center"><Package size={16} className="mr-2 text-gray-500" />Cargo Details</h4>
            {fd.cargoDetails?.length > 0 && (
              <div className="space-y-3">
                {fd.cargoDetails.map((cargo, idx) => (
                  <OceanLclCargoCard key={idx} cargo={cargo} />
                ))}
              </div>
            )}
            {getLclNotesText(quote) && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-3">{getLclNotesText(quote)}</p>}
          </div>
        )}
      </div>
    );
  };

  const handleQuoteTypeSelect = (typeId) => {
    resetDraftEditorState();
    // Block proceeding if High/Medium selected but date missing or insufficient points
    const typeSelectPriorityUntil = sanitizeHtmlDateValue(priorityUntilDate);
    if (quotePriority === 'High' || quotePriority === 'Medium') {
      if (!typeSelectPriorityUntil) {
        toast.error('Please select a date until which you want this priority before choosing a service type.');
        return;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const until = new Date(typeSelectPriorityUntil);
      until.setHours(0, 0, 0, 0);
      if (until < today) {
        toast.error('Priority end date cannot be in the past.');
        return;
      }
      const diffDays = Math.floor((until - today) / (24 * 60 * 60 * 1000));
      const days = Math.max(1, diffDays + 1);
      const baseCost = quotePriority === 'High' ? (pointsConfig?.costs?.priority_high ?? 250) : (pointsConfig?.costs?.priority_medium ?? 100);
      const discount = memberProfile?.tier === 'Founder' ? 50 : memberProfile?.tier === 'Titanium' ? 40 : memberProfile?.tier === 'Rhodium' ? 30 : memberProfile?.tier === 'Platinum' ? 20 : memberProfile?.tier === 'Gold' ? 10 : 0;
      const pointsToDeduct = Math.floor(days * baseCost * (100 - discount) / 100);
      const available = pointsSystemEnabled ? (memberProfile?.reward_points ?? 0) : 0;
      if (pointsSystemEnabled && pointsToDeduct > available) {
        toast.error(`You have ${available} pts but need ${pointsToDeduct} pts for this duration. Please select a shorter period or use Normal priority.`);
        return;
      }
    }
    setSelectedQuoteType(typeId);
  };

  const validateQuoteForm = (formData, quoteType) => {
    const v = (val) => !val || (typeof val === 'string' && !val.trim());
    const invalid = (message, errors = {}) => ({ valid: false, message, errors });
    const todayIso = new Date().toISOString().split('T')[0];
    const scopeOptions = SCOPE_OPTIONS_BY_MODE[quoteType] || [];
    const selectedScopes = Array.isArray(formData.scope)
      ? formData.scope.filter((scope) => scopeOptions.includes(scope))
      : [];
    const isPastDate = (value) => {
      if (v(value)) return false;
      return String(value).slice(0, 10) < todayIso;
    };
    const hasContainers = (fd) => {
      if (fd.containers && typeof fd.containers === 'object') {
        if (Object.values(fd.containers).some(q => q > 0)) return true;
      }
      if (fd.reefer && typeof fd.reefer === 'object') {
        if (Object.values(fd.reefer).some(q => q > 0)) return true;
      }
      if (fd.oogContainers && fd.oogContainers.length > 0) return true;
      return false;
    };
    const getMissingStandardContainerWeightError = (fd) => {
      const standardContainerTypes = ['20GP', '40GP', '40HC'];
      for (const type of standardContainerTypes) {
        const quantity = Number(fd?.containers?.[type] || 0);
        const weight = fd?.containerWeights?.[type];
        if (quantity > 0 && (weight === '' || weight == null || Number(weight) <= 0)) {
          return {
            type,
            field: `containerWeights.${type}`,
            message: `${type} weight is required`
          };
        }
      }
      return null;
    };
    const getMissingReeferTempRangeError = (fd) => {
      const reeferRequirements = [
        { type: '20RF', quantity: Number(fd?.reefer?.['20RF'] || 0), field: 'tempRange20', value: fd?.tempRange20 },
        { type: '40RF', quantity: Number(fd?.reefer?.['40RF'] || 0), field: 'tempRange40', value: fd?.tempRange40 },
      ];
      for (const reefer of reeferRequirements) {
        if (reefer.quantity > 0 && v(reefer.value)) {
          return {
            type: reefer.type,
            field: reefer.field,
            message: `${reefer.type} temperature range is required`
          };
        }
      }
      return null;
    };
    if (scopeOptions.length > 0 && selectedScopes.length === 0) {
      return invalid('Please select at least one scope', { scope: 'Select at least one scope' });
    }
    switch (quoteType) {
      case 'ocean-fcl':
        if (v(formData.commodity)) return invalid('Commodity is required', { commodity: 'Commodity is required' });
        if (v(formData.pol)) return invalid('Port of Loading (POL) is required', { pol: 'POL is required' });
        if (v(formData.pod)) return invalid('Port of Discharge (POD) is required', { pod: 'POD is required' });
        if (v(formData.incoterm)) return invalid('Incoterm is required', { incoterm: 'Select an incoterm' });
        if (v(formData.polFreeTime)) return invalid('POL free time is required', { polFreeTime: 'POL free time is required' });
        if (v(formData.podFreeTime)) return invalid('POD free time is required', { podFreeTime: 'POD free time is required' });
        if (Number(formData.polFreeTime) < 0) return invalid('POL free time cannot be negative', { polFreeTime: 'POL free time cannot be negative' });
        if (Number(formData.podFreeTime) < 0) return invalid('POD free time cannot be negative', { podFreeTime: 'POD free time cannot be negative' });
        if (!hasContainers(formData)) return invalid('Please add at least one container (standard, reefer, or Special Containers)', { containers: 'Add at least one container' });
        {
          const missingWeight = getMissingStandardContainerWeightError(formData);
          if (missingWeight) {
            return invalid(
              `Weight is required for ${missingWeight.type} when quantity is added`,
              { [missingWeight.field]: missingWeight.message }
            );
          }
        }
        {
          const missingReeferTempRange = getMissingReeferTempRangeError(formData);
          if (missingReeferTempRange) {
            return invalid(
              `Temperature range is required for ${missingReeferTempRange.type} when quantity is added`,
              { [missingReeferTempRange.field]: missingReeferTempRange.message }
            );
          }
        }
        break;
      case 'ocean-lcl':
        if (v(formData.commodity)) return invalid('Commodity is required', { commodity: 'Commodity is required' });
        if (v(formData.pol)) return invalid('Port of Loading (POL) is required', { pol: 'POL is required' });
        if (v(formData.pod)) return invalid('Port of Discharge (POD) is required', { pod: 'POD is required' });
        if (v(formData.incoterm)) return invalid('Incoterm is required', { incoterm: 'Select an incoterm' });
        if (v(formData.polFreeTime)) return invalid('POL free time is required', { polFreeTime: 'POL free time is required' });
        if (v(formData.podFreeTime)) return invalid('POD free time is required', { podFreeTime: 'POD free time is required' });
        if (Number(formData.polFreeTime) < 0) return invalid('POL free time cannot be negative', { polFreeTime: 'POL free time cannot be negative' });
        if (Number(formData.podFreeTime) < 0) return invalid('POD free time cannot be negative', { podFreeTime: 'POD free time cannot be negative' });
        if (!formData.cargoDetails?.length || !formData.cargoDetails.some(c => c?.description?.trim())) {
          return invalid('Please add at least one cargo line with description', { cargoDetails: 'Add at least one cargo line description' });
        }
        break;
      case 'air':
        if (v(formData.commodity)) return invalid('Commodity is required', { commodity: 'Commodity is required' });
        if (v(formData.origin)) return invalid('Origin airport is required', { origin: 'Origin airport is required' });
        if (v(formData.destination)) return invalid('Destination airport is required', { destination: 'Destination airport is required' });
        if (v(formData.incoterm)) return invalid('Incoterm is required', { incoterm: 'Select an incoterm' });
        if (selectedScopes.includes('Origin Pick-up & Handling') && v(formData.pickupAddress)) {
          return invalid('Pick up address is required when Origin Pick-up & Handling is selected', { pickupAddress: 'Pick up address is required' });
        }
        if (selectedScopes.includes('Destination Inland') && v(formData.deliveryAddress)) {
          return invalid('Destination address is required when Destination Inland is selected', { deliveryAddress: 'Destination address is required' });
        }
        break;
      case 'transport': {
        const cargoList = formData.cargoDetails || [];
        if (cargoList.length === 0) return invalid('Please add at least one cargo item', { cargoDetails: 'Please add at least one cargo item' });
        const firstCargo = cargoList[0] || {};
        const pl = formData.pickupLocation || {};
        if (v(pl.address)) return invalid('Pickup address is required', { 'pickupLocation.address': 'Address is required' });
        if (v(pl.city)) return invalid('Pickup city is required', { 'pickupLocation.city': 'City is required' });
        if (v(pl.country)) return invalid('Pickup country is required', { 'pickupLocation.country': 'Country is required' });
        if (v(pl.postalCode)) return invalid('Pickup postal code is required', { 'pickupLocation.postalCode': 'Postal code is required' });
        const dl = formData.deliveryLocation || {};
        if (v(dl.address)) return invalid('Delivery address is required', { 'deliveryLocation.address': 'Address is required' });
        if (v(dl.city)) return invalid('Delivery city is required', { 'deliveryLocation.city': 'City is required' });
        if (v(dl.country)) return invalid('Delivery country is required', { 'deliveryLocation.country': 'Country is required' });
        if (v(dl.postalCode)) return invalid('Delivery postal code is required', { 'deliveryLocation.postalCode': 'Postal code is required' });
        if (v(formData.pickupDate)) return invalid('Pickup date is required', { pickupDate: 'Pickup date is required' });
        if (v(formData.deliveryDate)) return invalid('Delivery date is required', { deliveryDate: 'Delivery date is required' });
        if (isPastDate(formData.pickupDate)) return invalid('Pickup date cannot be in the past', { pickupDate: 'Pickup date cannot be in the past' });
        if (isPastDate(formData.deliveryDate)) return invalid('Delivery date cannot be in the past', { deliveryDate: 'Delivery date cannot be in the past' });
        if (v(firstCargo.vehicleType)) return invalid('Vehicle type is required', { 'cargoDetails.0.vehicleType': 'Vehicle type is required' });
        if (v(firstCargo.subVehicleType)) return invalid('Sub vehicle type is required', { 'cargoDetails.0.subVehicleType': 'Sub vehicle type is required' });
        if (v(firstCargo.commodity)) return invalid('Commodity is required', { 'cargoDetails.0.commodity': 'Commodity is required' });
        if (v(firstCargo.cargoWeight)) return invalid('Total weight is required', { 'cargoDetails.0.cargoWeight': 'Total weight is required' });
        if (v(firstCargo.cargoVolume)) return invalid('Volume is required', { 'cargoDetails.0.cargoVolume': 'Volume is required' });
        if (v(firstCargo.dimensions?.length)) return invalid('Cargo length is required', { 'cargoDetails.0.dimensions.length': 'Length is required' });
        if (v(firstCargo.dimensions?.width)) return invalid('Cargo width is required', { 'cargoDetails.0.dimensions.width': 'Width is required' });
        if (v(firstCargo.dimensions?.height)) return invalid('Cargo height is required', { 'cargoDetails.0.dimensions.height': 'Height is required' });
        break;
      }
      case 'warehousing':
        if (v(formData.commodity)) return invalid('Commodity is required', { commodity: 'Commodity is required' });
        const loc = formData.location || {};
        if (v(loc.city)) return invalid('Warehouse city is required', { 'location.city': 'City is required' });
        if (v(loc.country)) return invalid('Warehouse country is required', { 'location.country': 'Country is required' });
        if (v(formData.startDate)) return invalid('Start date is required', { startDate: 'Start date is required' });
        if (isPastDate(formData.startDate)) return invalid('Start date cannot be in the past', { startDate: 'Start date cannot be in the past' });
        const dur = formData.duration;
        if (dur === '' || dur == null || (typeof dur === 'number' && dur <= 0)) return invalid('Duration is required', { duration: 'Duration is required' });
        const sp = formData.spaceRequired;
        if (sp === '' || sp == null || (typeof sp === 'number' && sp <= 0)) return invalid('Space required is required', { spaceRequired: 'Space required is required' });
        break;
      case 'breakbulk':
        if (v(formData.commodity)) return invalid('Commodity is required', { commodity: 'Commodity is required' });
        const orig = formData.origin || {};
        const dest = formData.destination || {};
        if (v(orig.port)) return invalid('Origin port is required', { 'origin.port': 'Origin port is required' });
        if (v(orig.country)) return invalid('Origin country is required', { 'origin.country': 'Origin country is required' });
        if (v(dest.port)) return invalid('Destination port is required', { 'destination.port': 'Destination port is required' });
        if (v(dest.country)) return invalid('Destination country is required', { 'destination.country': 'Destination country is required' });
        break;
      default:
        if (v(formData.commodity)) return invalid('Commodity is required', { commodity: 'Commodity is required' });
    }
    return { valid: true };
  };

  const clearQuoteFormError = useCallback((fieldKeys = []) => {
    const keys = Array.isArray(fieldKeys) ? fieldKeys : [fieldKeys];
    if (keys.length === 0) return;
    setQuoteFormErrors((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      keys.forEach((key) => {
        if (key && Object.prototype.hasOwnProperty.call(next, key)) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const buildQuotePayload = (formData, status = 'Active') => {
    const quoteTypeMap = {
      'ocean-fcl': 'Ocean FCL',
      'ocean-lcl': 'Ocean LCL',
      'air': 'Air',
      'transport': 'Transport',
      'warehousing': 'Warehousing',
      'breakbulk': 'Breakbulk'
    };

    let containersArray = null;
    if (formData.containers && typeof formData.containers === 'object') {
      containersArray = Object.entries(formData.containers)
        .filter(([_, qty]) => qty > 0)
        .map(([type, qty]) => ({
          container_type: type,
          quantity: qty,
          weight_kg: formData.containerWeights?.[type] !== '' && formData.containerWeights?.[type] != null
            ? parseFloat(formData.containerWeights[type])
            : null,
          in_gauge: true
        }));
      if (containersArray.length === 0) containersArray = null;
    }

    let cargoDesc = Array.isArray(formData.specialRequirements)
      ? [...(formData.specialRequirements || []), formData.notes].filter(Boolean).join('; ')
      : (formData.specialRequirements || formData.cargoDescription || formData.notes || '');

    if (selectedQuoteType === 'ocean-lcl' && Array.isArray(formData.cargoDetails) && formData.cargoDetails.length > 0) {
      const lclLines = formData.cargoDetails.map((c, li) => {
        const rows = getLclCargoDimensionRows(c);
        const rowBits = rows.map((r, ri) => {
          const dim = [r.length, r.width, r.height].filter((x) => x !== '' && x != null && String(x).trim() !== '').join('Ã—');
          const u = r.unit || 'cm';
          const st = formatLclRowStackability(r.stackability);
          return `S${ri + 1}: ${r.pieces || 0} pcs${dim ? ` ${dim}${u}` : ''}; ${st}`;
        }).join(' | ');
        return `Item ${li + 1} (${(c.description || '').trim() || 'cargo'}): ${rowBits}`;
      }).join(' â€” ');
      const fragile = Array.isArray(formData.specialHandling) && formData.specialHandling.includes('Fragile')
        ? `Fragile: ${formData.fragileHandling === 'specific' ? `${formData.fragileBoxCount || '?'} boxes` : 'all cargo'}`
        : '';
      cargoDesc = [cargoDesc, lclLines, fragile].filter(Boolean).join('\n');
    }
    if (selectedQuoteType === 'ocean-lcl') {
      cargoDesc = buildLclCargoDescription(formData);
    }

    const originCity = formData.pickupLocation?.city || (typeof formData.origin === 'string' ? formData.origin : formData.origin?.port) || formData.pickupCity || formData.location?.city;
    const destCity = formData.deliveryLocation?.city || (typeof formData.destination === 'string' ? formData.destination : formData.destination?.port) || formData.deliveryCity;
    const weightKg = formData.cargoWeight ? parseFloat(formData.cargoWeight) : (formData.totalWeight ? parseFloat(formData.totalWeight) : null) || (formData.weight ? parseFloat(formData.weight) : null);
    const volumeCbm = formData.cargoVolume ? parseFloat(formData.cargoVolume) : (formData.volume ? parseFloat(formData.volume) : null);
    const { msds, ...formDataForStorage } = formData;
    const formDataSnapshot = { ...formDataForStorage };

    return {
      quote_type: quoteTypeMap[selectedQuoteType] || 'Ocean FCL',
      commodity: formData.commodity || 'General Cargo',
      hs_code: hsCode || null,
      attachments: quoteAttachments,
      requester_id: currentMemberId,
      cargo_description: cargoDesc,
      origin_port: formData.pol ? { code: formData.pol, name: (formData.polName || formData.pol).trim() || formData.pol, country: formData.polCountry || '' } : null,
      destination_port: formData.pod ? { code: formData.pod, name: (formData.podName || formData.pod).trim() || formData.pod, country: formData.podCountry || '' } : null,
      origin_city: originCity || null,
      destination_city: destCity || null,
      priority: pointsSystemEnabled ? quotePriority : 'Normal',
      ...(pointsSystemEnabled && quotePriority !== 'Normal' && sanitizeHtmlDateValue(priorityUntilDate) && { priority_until: sanitizeHtmlDateValue(priorityUntilDate) }),
      ...(responseDeadline && { response_deadline: new Date(responseDeadline).toISOString() }),
      public_sharing_enabled: publicSharingEnabled,
      incoterm: Array.isArray(formData.incoterm) ? (formData.incoterm[0] || 'FOB') : (formData.incoterm || 'FOB'),
      is_hazardous: formData.isHaz || formData.isHazardous || false,
      hazardous_class: formData.hazClass || null,
      status,
      sender_line_items: lineItems.map(item => ({
        ...item,
        unit: item.unit || 'Per Container',
        description: item.description || '',
        tax_type: item.tax_type || null,
        container_type: item.container_type || 'All'
      })),
      special_instructions: specialInstructions.map((inst, idx) => ({
        instruction_number: `1.${idx + 1}`,
        instruction_text: inst.instruction_text || '',
        is_mandatory: inst.is_mandatory !== false
      })),
      containers: containersArray,
      weight_kg: weightKg,
      volume_cbm: volumeCbm,
      form_data: formDataSnapshot
    };
  };

  const buildPostedInquiryUpdatePayload = (formData) => {
    const originalQuote = editingPostedInquiry;
    const baseline = postedInquiryEditBaselineRef.current;
    if (!originalQuote || !baseline) return null;

    const candidate = buildQuotePayload(formData, 'Active');
    const updatePayload = {};
    POSTED_INQUIRY_EDITABLE_FIELDS.forEach((field) => {
      if (field === 'form_data' || candidate[field] === undefined) return;
      if (
        !formValuesEqual(candidate[field], baseline.payload[field])
        && !formValuesEqual(candidate[field], originalQuote[field])
      ) {
        updatePayload[field] = candidate[field];
      }
    });

    const formDataPatch = buildFormDataPatch(baseline.formData, formData);
    if (formDataPatch !== undefined) {
      const mergedFormData = mergeFormDataPatch(
        isPlainFormObject(originalQuote.form_data) ? originalQuote.form_data : {},
        formDataPatch
      );
      if (!formValuesEqual(mergedFormData, originalQuote.form_data || {})) {
        updatePayload.form_data = mergedFormData;
      }
    }
    // QuoteCreate deliberately remains the API contract for this endpoint.
    // Supply only its required context fields alongside the true delta. The
    // backend ignores requester_id and suppresses unchanged whitelisted values,
    // while retaining Pydantic validation for every value actually submitted.
    return {
      requester_id: currentMemberId || originalQuote.requester_id || 'member',
      quote_type: updatePayload.quote_type || originalQuote.quote_type,
      commodity: updatePayload.commodity || originalQuote.commodity || 'General Cargo',
      ...updatePayload,
    };
  };

  const handleSaveDraft = async () => {
    try {
      if (editingPostedInquiry?.quote_id) {
        toast.error('Posted inquiries cannot be saved as drafts. Save the inquiry updates instead.');
        return;
      }
      if (!selectedQuoteType) {
        toast.error('Please select a quote type first.');
        return;
      }

      const formData = currentFormDataInDialog || { scope: [] };
      const hasMeaningfulDraftContent =
        lineItems.length > 0 ||
        specialInstructions.length > 0 ||
        quotePriority !== 'Normal' ||
        !!sanitizeHtmlDateValue(priorityUntilDate) ||
        !!responseDeadline ||
        !!hsCode ||
        quoteAttachments.length > 0 ||
        Object.entries(formData).some(([key, value]) => {
          if (key === 'scope') return Array.isArray(value) && value.length > 0;
          if (Array.isArray(value)) return value.length > 0;
          if (value && typeof value === 'object') {
            return Object.values(value).some((nested) => {
              if (Array.isArray(nested)) return nested.length > 0;
              return nested !== '' && nested != null && nested !== false;
            });
          }
          return value !== '' && value != null && value !== false;
        });

      if (!hasMeaningfulDraftContent) {
        toast.error('Add some quote details before saving a draft.');
        return;
      }

      setDraftSaveLoading(true);
      const quoteData = buildQuotePayload(formData, 'Draft');
      let savedDraft = null;

      if (editingDraftQuote?.quote_id) {
        try {
          const response = await quotesApi.updateDraftQuote(editingDraftQuote.quote_id, quoteData, currentMemberId);
          savedDraft = response?.data;
        } catch (error) {
          if (error?.response?.status !== 404) throw error;
          // Fallback for backend instances still running without the dedicated draft endpoint loaded.
          const response = await quotesApi.updateQuote(editingDraftQuote.quote_id, quoteData);
          savedDraft = response?.data;
        }
        toast.success('Draft updated.');
      } else {
        const response = await quotesApi.createQuote(quoteData, currentMemberId);
        savedDraft = response?.data;
        toast.success('Draft saved.');
      }

      if (savedDraft?.quote_id) {
        setEditingDraftQuote(savedDraft);
        upsertDraftQuoteLocal(savedDraft);
      }

      draftAutosaveLastSnapshotRef.current = JSON.stringify({
        selectedQuoteType,
        currentFormDataInDialog: formData,
        lineItems,
        specialInstructions,
        quotePriority,
        priorityUntilDate,
        responseDeadline,
        publicSharingEnabled,
        hsCode,
        quoteAttachments,
        editingDraftQuoteId: savedDraft?.quote_id || editingDraftQuote?.quote_id || null
      });

      setShowNewQuoteDialog(false);
      setSelectedQuoteType(null);
      setCloneSourceQuote(null);
      resetDraftEditorState();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save draft'));
    } finally {
      setDraftSaveLoading(false);
    }
  };

  useEffect(() => {
    if (!showNewQuoteDialog || !selectedQuoteType || quoteSubmitLoading || editingPostedInquiry?.quote_id) return;

    const formData = currentFormDataInDialog || { scope: [] };
    const hasMeaningfulDraftContent =
      lineItems.length > 0 ||
      specialInstructions.length > 0 ||
      quotePriority !== 'Normal' ||
      !!sanitizeHtmlDateValue(priorityUntilDate) ||
      !!responseDeadline ||
      !!hsCode ||
      quoteAttachments.length > 0 ||
      Object.entries(formData).some(([key, value]) => {
        if (key === 'scope') return Array.isArray(value) && value.length > 0;
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') {
          return Object.values(value).some((nested) => {
            if (Array.isArray(nested)) return nested.length > 0;
            return nested !== '' && nested != null && nested !== false;
          });
        }
        return value !== '' && value != null && value !== false;
      });

    if (!hasMeaningfulDraftContent) return;

    const snapshot = JSON.stringify({
      selectedQuoteType,
      currentFormDataInDialog: formData,
      lineItems,
      specialInstructions,
      quotePriority,
      priorityUntilDate,
      responseDeadline,
      publicSharingEnabled,
      hsCode,
      quoteAttachments,
      editingDraftQuoteId: editingDraftQuote?.quote_id || null
    });

    if (draftAutosaveSkipNextRef.current) {
      draftAutosaveSkipNextRef.current = false;
      draftAutosaveLastSnapshotRef.current = snapshot;
      return;
    }

    if (snapshot === draftAutosaveLastSnapshotRef.current || draftSaveLoading) return;

    const timer = setTimeout(async () => {
      try {
        setDraftSaveLoading(true);
        const quoteData = buildQuotePayload(formData, 'Draft');
        let savedDraft = null;

        if (editingDraftQuote?.quote_id) {
          try {
            const response = await quotesApi.updateDraftQuote(editingDraftQuote.quote_id, quoteData, currentMemberId);
            savedDraft = response?.data;
          } catch (error) {
            if (error?.response?.status !== 404) throw error;
            const response = await quotesApi.updateQuote(editingDraftQuote.quote_id, quoteData);
            savedDraft = response?.data;
          }
        } else {
          const response = await quotesApi.createQuote(quoteData, currentMemberId);
          savedDraft = response?.data;
        }

        if (savedDraft?.quote_id) {
          setEditingDraftQuote(savedDraft);
          upsertDraftQuoteLocal(savedDraft);
          draftAutosaveLastSnapshotRef.current = JSON.stringify({
            selectedQuoteType,
            currentFormDataInDialog: formData,
            lineItems,
            specialInstructions,
            quotePriority,
            priorityUntilDate,
            responseDeadline,
            publicSharingEnabled,
            hsCode,
            quoteAttachments,
            editingDraftQuoteId: savedDraft.quote_id
          });
        }
      } catch (error) {
        console.error('Draft autosave failed:', error);
      } finally {
        setDraftSaveLoading(false);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [
    buildQuotePayload,
    currentFormDataInDialog,
    currentMemberId,
    draftSaveLoading,
    editingDraftQuote,
    editingPostedInquiry,
    lineItems,
    hsCode,
    priorityUntilDate,
    quoteAttachments,
    publicSharingEnabled,
    responseDeadline,
    quotePriority,
    quoteSubmitLoading,
    selectedQuoteType,
    showNewQuoteDialog,
    specialInstructions,
    upsertDraftQuoteLocal
  ]);

  const handleQuoteFormSubmit = async (formData) => {
    try {
      const isPostedInquiryEdit = Boolean(editingPostedInquiry?.quote_id);
      const isLegacyPostedInquiry = isPostedInquiryEdit && (
        !isPlainFormObject(editingPostedInquiry?.form_data)
        || Object.keys(editingPostedInquiry.form_data).length === 0
      );
      // Legacy inquiries can predate required modern form fields. Their
      // unrepresented values are preserved by the delta payload below, so do
      // not make an unrelated correction impossible solely for missing history.
      const validation = isLegacyPostedInquiry
        ? { valid: true }
        : validateQuoteForm(formData, selectedQuoteType);
      if (!validation.valid) {
        setQuoteFormErrors(validation.errors || {});
        toast.error(validation.message);
        return;
      }
      setQuoteFormErrors({});
      const hasChangedPostedDeadline = isPostedInquiryEdit && responseDeadlineChanged(
        editingPostedInquiry.response_deadline,
        responseDeadline
      );
      if (!isPostedInquiryEdit || hasChangedPostedDeadline) {
        const responseDeadlineDate = responseDeadline ? new Date(responseDeadline) : null;
        if (!responseDeadlineDate || Number.isNaN(responseDeadlineDate.getTime())) {
          toast.error('Please select the last date and time to submit quotations.');
          return;
        }
        if (responseDeadlineDate.getTime() <= Date.now()) {
          toast.error('Quotation submission deadline must be in the future.');
          return;
        }
      }
      if (isPostedInquiryEdit) {
        const quoteData = buildPostedInquiryUpdatePayload(formData);
        if (!quoteData) {
          toast.error('Inquiry editor is still loading. Please try again in a moment.');
          return;
        }
        setQuoteSubmitLoading(true);
        const response = await quotesApi.updatePostedInquiry(
          editingPostedInquiry.quote_id,
          quoteData
        );
        const updatedInquiry = response?.data;
        if (updatedInquiry?.inquiry_update_applied === false) {
          toast.info('No inquiry details changed.');
        } else {
          toast.success('Inquiry updated. Relevant members have been notified.');
        }
        setShowNewQuoteDialog(false);
        setSelectedQuoteType(null);
        setCloneSourceQuote(null);
        resetDraftEditorState();
        fetchData();
        return;
      }
      // Validate priority + date + points for High/Medium
      const safePriorityUntilSubmit = sanitizeHtmlDateValue(priorityUntilDate);
      if (quotePriority === 'High' || quotePriority === 'Medium') {
        if (!safePriorityUntilSubmit) {
          toast.error('Please select a date until which you want this priority.');
          return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const until = new Date(safePriorityUntilSubmit);
        until.setHours(0, 0, 0, 0);
        if (until < today) {
          toast.error('Priority end date cannot be in the past.');
          return;
        }
        const diffDays = Math.floor((until - today) / (24 * 60 * 60 * 1000));
        const days = Math.max(1, diffDays + 1);
        const baseCost = quotePriority === 'High' ? (pointsConfig?.costs?.priority_high ?? 250) : (pointsConfig?.costs?.priority_medium ?? 100);
        const discount = memberProfile?.tier === 'Founder' ? 50 : memberProfile?.tier === 'Titanium' ? 40 : memberProfile?.tier === 'Rhodium' ? 30 : memberProfile?.tier === 'Platinum' ? 20 : memberProfile?.tier === 'Gold' ? 10 : 0;
        const pointsToDeduct = Math.floor(days * baseCost * (100 - discount) / 100);
        const available = pointsSystemEnabled ? (memberProfile?.reward_points ?? 0) : 0;
        if (pointsSystemEnabled && pointsToDeduct > available) {
          toast.error(`Insufficient points. You need ${pointsToDeduct} pts for this duration, but you have ${available} pts. Please select a shorter period or use Normal priority.`);
          return;
        }
      }

      {
        setQuoteSubmitLoading(true);
        const quoteData = buildQuotePayload(formData, 'Active');
        let savedInquiry = null;
        if (editingDraftQuote?.quote_id) {
          try {
            const response = await quotesApi.publishDraftQuote(editingDraftQuote.quote_id, quoteData, currentMemberId);
            savedInquiry = response?.data;
          } catch (error) {
            if (error?.response?.status !== 404) throw error;
            // Fallback for backend instances still running without the dedicated publish endpoint loaded.
            const response = await quotesApi.updateQuote(editingDraftQuote.quote_id, quoteData);
            savedInquiry = response?.data;
          }
          toast.success('Draft submitted successfully!');
        } else {
          const response = await quotesApi.createQuote(quoteData, currentMemberId);
          savedInquiry = response?.data;
          toast.success('Inquiry created successfully!');
        }
        if (savedInquiry?.quote_id) {
          setCreatedInquiry(savedInquiry);
          setShowInquiryCreatedDialog(true);
        }
        setShowNewQuoteDialog(false);
        setSelectedQuoteType(null);
        resetDraftEditorState();
        setCloneSourceQuote(null);
        fetchData();
        await refreshTierConfig?.();
        try {
          const profileRes = await profileApi.getProfile(currentMemberId);
          setMemberProfile(profileRes.data);
        } catch (_) { }
        return;
      }

      setQuoteSubmitLoading(true);
      // Map the quote type to backend format
      const quoteTypeMap = {
        'ocean-fcl': 'Ocean FCL',
        'ocean-lcl': 'Ocean LCL',
        'air': 'Air',
        'transport': 'Transport',
        'warehousing': 'Warehousing',
        'breakbulk': 'Breakbulk'
      };

      // Transform containers dict to array format expected by backend
      let containersArray = null;
      if (formData.containers && typeof formData.containers === 'object') {
        containersArray = Object.entries(formData.containers)
          .filter(([_, qty]) => qty > 0)
          .map(([type, qty]) => ({
            container_type: type,
            quantity: qty,
            weight_kg: formData.containerWeights?.[type] !== '' && formData.containerWeights?.[type] != null
              ? parseFloat(formData.containerWeights[type])
              : null,
            in_gauge: true
          }));
        if (containersArray.length === 0) containersArray = null;
      }

      // cargo_description: must be string (Transport sends specialRequirements as array)
      let cargoDesc = Array.isArray(formData.specialRequirements)
        ? [...(formData.specialRequirements || []), formData.notes].filter(Boolean).join('; ')
        : (formData.specialRequirements || formData.cargoDescription || formData.notes || '');

      if (selectedQuoteType === 'ocean-lcl' && Array.isArray(formData.cargoDetails) && formData.cargoDetails.length > 0) {
        const lclLines = formData.cargoDetails.map((c, li) => {
          const rows = getLclCargoDimensionRows(c);
          const rowBits = rows.map((r, ri) => {
            const dim = [r.length, r.width, r.height].filter((x) => x !== '' && x != null && String(x).trim() !== '').join('×');
            const u = r.unit || 'cm';
            const st = formatLclRowStackability(r.stackability);
            return `S${ri + 1}: ${r.pieces || 0} pcs${dim ? ` ${dim}${u}` : ''}; ${st}`;
          }).join(' | ');
          return `Item ${li + 1} (${(c.description || '').trim() || 'cargo'}): ${rowBits}`;
        }).join(' — ');
        const fragile = Array.isArray(formData.specialHandling) && formData.specialHandling.includes('Fragile')
          ? `Fragile: ${formData.fragileHandling === 'specific' ? `${formData.fragileBoxCount || '?'} boxes` : 'all cargo'}`
          : '';
        cargoDesc = [cargoDesc, lclLines, fragile].filter(Boolean).join('\n');
      }
      if (selectedQuoteType === 'ocean-lcl') {
        cargoDesc = buildLclCargoDescription(formData);
      }

      // Origin/destination: support all quote types
      const originCity = formData.pickupLocation?.city || (typeof formData.origin === 'string' ? formData.origin : formData.origin?.port) || formData.pickupCity || formData.location?.city;
      const destCity = formData.deliveryLocation?.city || (typeof formData.destination === 'string' ? formData.destination : formData.destination?.port) || formData.deliveryCity;
      const weightKg = formData.cargoWeight ? parseFloat(formData.cargoWeight) : (formData.totalWeight ? parseFloat(formData.totalWeight) : null) || (formData.weight ? parseFloat(formData.weight) : null);
      const volumeCbm = formData.cargoVolume ? parseFloat(formData.cargoVolume) : (formData.volume ? parseFloat(formData.volume) : null);

      // Build form_data snapshot (exclude file objects)
      const { msds, ...formDataForStorage } = formData;
      const formDataSnapshot = { ...formDataForStorage };

      const quoteData = {
        quote_type: quoteTypeMap[selectedQuoteType] || 'Ocean FCL',
        commodity: formData.commodity || 'General Cargo',
        requester_id: currentMemberId,
        cargo_description: cargoDesc,
        origin_port: formData.pol ? { code: formData.pol, name: (formData.polName || formData.pol).trim() || formData.pol, country: formData.polCountry || '' } : null,
        destination_port: formData.pod ? { code: formData.pod, name: (formData.podName || formData.pod).trim() || formData.pod, country: formData.podCountry || '' } : null,
        origin_city: originCity || null,
        destination_city: destCity || null,
        priority: pointsSystemEnabled ? quotePriority : 'Normal',
        ...(pointsSystemEnabled && quotePriority !== 'Normal' && sanitizeHtmlDateValue(priorityUntilDate) && { priority_until: sanitizeHtmlDateValue(priorityUntilDate) }),
        incoterm: Array.isArray(formData.incoterm) ? (formData.incoterm[0] || 'FOB') : (formData.incoterm || 'FOB'),
        is_hazardous: formData.isHaz || false,
        hazardous_class: formData.hazClass || null,
        status: 'Active',
        sender_line_items: lineItems.map(item => ({
          ...item,
          unit: item.unit || 'Per Container',
          description: item.description || '',
          tax_type: item.tax_type || null,
          container_type: item.container_type || 'All'
        })),
        special_instructions: specialInstructions.map((inst, idx) => ({
          instruction_number: `1.${idx + 1}`,
          instruction_text: inst.instruction_text || '',
          is_mandatory: inst.is_mandatory !== false
        })),
        containers: containersArray,
        weight_kg: weightKg,
        volume_cbm: volumeCbm,
        form_data: formDataSnapshot
      };

      await quotesApi.createQuote(quoteData, currentMemberId);
      clearCurrentDraft();
      toast.success('Quote created successfully!');
      setShowNewQuoteDialog(false);
      setSelectedQuoteType(null);
      resetDraftEditorState();
      fetchData();
      await refreshTierConfig?.();
      // Refresh member profile to update points display
      try {
        const profileRes = await profileApi.getProfile(currentMemberId);
        setMemberProfile(profileRes.data);
      } catch (_) { }
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to create quote'));
    } finally {
      setQuoteSubmitLoading(false);
    }
  };

  const quoteTypeToId = {
    'Ocean FCL': 'ocean-fcl',
    'Ocean LCL': 'ocean-lcl',
    'Air': 'air',
    'Transport': 'transport',
    'Warehousing': 'warehousing',
    'Breakbulk': 'breakbulk'
  };

  const handleCloneQuote = (quote) => {
    draftAutosaveSkipNextRef.current = true;
    draftAutosaveLastSnapshotRef.current = '';
    setEditingDraftQuote(null);
    setEditingPostedInquiry(null);
    const typeId = quoteTypeToId[quote.quote_type] || 'ocean-fcl';
    setCloneSourceQuote(quote);
    setSelectedQuoteType(typeId);
    setCurrentFormDataInDialog(quote.form_data || {});
    toast.info('Cloning quote – Edit details and submit to create a new quote.');
    setQuotePriority(pointsSystemEnabled ? (quote.priority || 'Normal') : 'Normal');
    setPriorityUntilDate(pointsSystemEnabled ? sanitizeHtmlDateValue(quote.priority_until ?? '') : '');
    setResponseDeadline('');
    setPublicSharingEnabled(true);
    setHsCode(quote.hs_code || '');
    setQuoteAttachments(quote.attachments || []);
    setLineItems((quote.sender_line_items || []).map((item, idx) => ({
      ...item,
      id: `clone-${Date.now()}-${idx}`
    })));
    setSpecialInstructions((quote.special_instructions || []).map((inst, idx) => ({
      ...inst,
      id: `clone-inst-${Date.now()}-${idx}`
    })));
    setShowNewQuoteDialog(true);
  };

  const handleEditDraft = (quote) => {
    draftAutosaveSkipNextRef.current = true;
    draftAutosaveLastSnapshotRef.current = '';
    setEditingPostedInquiry(null);
    const typeId = quoteTypeToId[quote.quote_type] || 'ocean-fcl';
    setEditingDraftQuote(quote);
    setCloneSourceQuote(null);
    setSelectedQuoteType(typeId);
    setCurrentFormDataInDialog(quote.form_data || {});
    setQuotePriority(pointsSystemEnabled ? (quote.priority || 'Normal') : 'Normal');
    setPriorityUntilDate(pointsSystemEnabled ? sanitizeHtmlDateValue(quote.priority_until ?? '') : '');
    setResponseDeadline(toLocalDateTimeInputValue(quote.response_deadline));
    // Legacy drafts did not have this setting and must remain private unless
    // their owner explicitly enables sharing.
    setPublicSharingEnabled(quote.public_sharing_enabled === true);
    setHsCode(quote.hs_code || '');
    setQuoteAttachments(quote.attachments || []);
    setLineItems((quote.sender_line_items || []).map((item, idx) => ({
      ...item,
      id: item.id || `draft-line-${Date.now()}-${idx}`
    })));
    setSpecialInstructions((quote.special_instructions || []).map((inst, idx) => ({
      ...inst,
      id: inst.id || `draft-inst-${Date.now()}-${idx}`
    })));
    setShowNewQuoteDialog(true);
  };

  const handleEditPostedInquiry = (quote) => {
    draftAutosaveSkipNextRef.current = true;
    draftAutosaveLastSnapshotRef.current = '';
    postedInquiryEditBaselineRef.current = null;
    const initialFormData = buildPostedInquiryInitialFormData(quote);
    const typeId = quoteTypeToId[quote.quote_type] || 'ocean-fcl';
    setEditingDraftQuote(null);
    setEditingPostedInquiry(quote);
    setCloneSourceQuote(null);
    setSelectedQuoteType(typeId);
    setCurrentFormDataInDialog(initialFormData);
    setQuotePriority(pointsSystemEnabled ? (quote.priority || 'Normal') : 'Normal');
    setPriorityUntilDate(pointsSystemEnabled ? sanitizeHtmlDateValue(quote.priority_until ?? '') : '');
    setResponseDeadline(toLocalDateTimeInputValue(quote.response_deadline));
    setPublicSharingEnabled(quote.public_sharing_enabled === true);
    setHsCode(quote.hs_code || '');
    setQuoteAttachments(quote.attachments || []);
    setLineItems((quote.sender_line_items || []).map((item, idx) => ({
      ...item,
      id: item.id || `inquiry-line-${Date.now()}-${idx}`
    })));
    setSpecialInstructions((quote.special_instructions || []).map((inst, idx) => ({
      ...inst,
      id: inst.id || `inquiry-inst-${Date.now()}-${idx}`
    })));
    setShowNewQuoteDialog(true);
  };

  const openPromoteDialog = (quote) => {
    if (!pointsSystemEnabled) return;
    setPromoteQuote(quote);
    setPromotePriority(quote?.priority === 'High' || quote?.priority === 'Medium' ? quote.priority : '');
    setPromoteUntilDate(getPromotionStartDateValue(quote));
    setShowPromoteDialog(true);
  };

  const openVoidConfirmDialog = (quote) => {
    setVoidConfirmQuote(quote);
    setShowVoidConfirmDialog(true);
  };

  const handleVoidConfirm = async () => {
    if (!voidConfirmQuote) return;
    try {
      if (voidConfirmQuote.status === 'Draft') {
        await quotesApi.deleteDraftQuote(voidConfirmQuote.quote_id, currentMemberId);
        toast.success('Draft deleted successfully');
      } else {
        const res = await quotesApi.toggleVoid(voidConfirmQuote.quote_id, currentMemberId);
        const isVoided = res.data?.is_voided ?? !voidConfirmQuote.is_voided;
        toast.success(isVoided ? 'Quote voided – visible only to you' : 'Quote published – visible to all');
      }
      setShowVoidConfirmDialog(false);
      setVoidConfirmQuote(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update quote visibility'));
    }
  };

  const handlePromoteConfirm = async () => {
    if (!pointsSystemEnabled) return;
    if (!promoteQuote) return;
    if (!promotePriority) {
      toast.error('Please select a priority level (High or Medium).');
      return;
    }
    if (!promoteUntilDate) {
      toast.error('Please select a date until which you want this priority.');
      return;
    }
    const minimumDate = getPromotionStartDateValue(promoteQuote);
    const minimum = new Date(`${minimumDate}T00:00:00`);
    const until = new Date(promoteUntilDate);
    until.setHours(0, 0, 0, 0);
    if (until < minimum) {
      toast.error(`Priority end date must be on or after ${minimumDate}.`);
      return;
    }
    const diffDays = Math.floor((until - minimum) / (24 * 60 * 60 * 1000));
    const days = Math.max(1, diffDays + 1);
    const baseCost = promotePriority === 'High' ? (pointsConfig?.costs?.priority_high ?? 250) : (pointsConfig?.costs?.priority_medium ?? 100);
    const discount = memberProfile?.tier === 'Founder' ? 50 : memberProfile?.tier === 'Titanium' ? 40 : memberProfile?.tier === 'Rhodium' ? 30 : memberProfile?.tier === 'Platinum' ? 20 : memberProfile?.tier === 'Gold' ? 10 : 0;
    const pointsToDeduct = Math.floor(days * baseCost * (100 - discount) / 100);
    const available = pointsSystemEnabled ? (memberProfile?.reward_points ?? 0) : 0;
    if (pointsSystemEnabled && pointsToDeduct > available) {
      toast.error(`Insufficient points. You need ${pointsToDeduct} pts for this duration, but you have ${available} pts.`);
      return;
    }
    try {
      await quotesApi.changePriority(promoteQuote.quote_id, promotePriority, currentMemberId, promoteUntilDate);
      toast.success(
        pointsSystemEnabled
          ? `Quote promoted to ${promotePriority} priority until ${promoteUntilDate}! ${pointsToDeduct} points deducted.`
          : `Quote promoted to ${promotePriority} priority until ${promoteUntilDate}!`
      );
      setShowPromoteDialog(false);
      setPromoteQuote(null);
      setPromotePriority('');
      setPromoteUntilDate('');
      fetchData();
      const profileRes = await profileApi.getProfile(currentMemberId);
      setMemberProfile(profileRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to promote quote'));
    }
  };

  // Save current filters
  const saveCurrentFilter = () => {
    if (!newFilterName.trim()) {
      toast.error('Please enter a filter name');
      return;
    }

    const newFilter = {
      id: Date.now(),
      name: newFilterName,
      status: filterStatus,
      quoteType: filterQuoteType,
      pol: filterPOL,
      pod: filterPOD,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
      reference: filterReference
    };

    const updatedFilters = [...savedFilters, newFilter];
    setSavedFilters(updatedFilters);
    localStorage.setItem('quoteFilters', JSON.stringify(updatedFilters));
    setShowSaveFilterDialog(false);
    setNewFilterName('');
    toast.success('Filter saved!');
  };

  // Apply saved filter
  const applySavedFilter = (filter) => {
    setFilterStatus(filter.status || '');
    setFilterQuoteType(filter.quoteType || '');
    setFilterPOL(filter.pol || '');
    setFilterPOD(filter.pod || '');
    setFilterDateFrom(filter.dateFrom || '');
    setFilterDateTo(filter.dateTo || '');
    setFilterReference(filter.reference || '');
    toast.info(`Applied filter: ${filter.name}`);
  };

  // Delete saved filter
  const deleteSavedFilter = (filterId) => {
    const updatedFilters = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updatedFilters);
    localStorage.setItem('quoteFilters', JSON.stringify(updatedFilters));
    toast.success('Filter deleted');
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterStatus('');
    setFilterQuoteType('');
    setFilterPOL('');
    setFilterPOD('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterReference('');
    setSearchQuery('');
    setDebouncedSearchQuery('');
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeTab,
    sentSubFilter,
    nominatedSubFilter,
    debouncedSearchQuery,
    filterStatus,
    filterQuoteType,
    filterPOL,
    filterPOD,
    filterDateFrom,
    filterDateTo,
    filterReference
  ]);

  // Fetch bids and open comparison dialog (user selects members inside via checkboxes, then clicks Compare)
  const handleOpenBidComparison = async (quote) => {
    setSelectedQuote(quote);
    setLoadingBids(true);
    try {
      const bidsRes = await quotesApi.getQuoteBids(quote.quote_id);
      setQuoteBids(bidsRes.data || []);
      setShowBidComparisonDialog(true);
    } catch (error) {
      console.error('Error fetching bids:', error);
      toast.error('Failed to load bids');
    } finally {
      setLoadingBids(false);
    }
  };

  // Handle nomination from BidComparison component
  const handleNominateFromComparison = async (bidId, nominationData) => {
    try {
      await quotesApi.nominateBid(
        selectedQuote.quote_id,
        bidId,
        nominationData.reason,
        nominationData.rating,
        currentMemberId,
        nominationData.reasonCategory,
        nominationData.otherReason
      );
      toast.success(selectedQuote?.status === 'Nominated' ? 'Nominated bidder updated successfully!' : 'Bid nominated successfully!');
      setShowBidComparisonDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to nominate bid'));
    }
  };

  const handleNominate = async () => {
    if (!nominationForm.reason || nominationForm.reason.length < 10) {
      toast.error('Please provide a reason for nomination (minimum 10 characters)');
      return;
    }

    try {
      await quotesApi.nominateBid(
        selectedQuote.quote_id,
        selectedBid.bid_id,
        nominationForm.reason,
        nominationForm.rating,
        currentMemberId
      );
      toast.success('Bid nominated successfully!');
      setShowNominationDialog(false);
      setNominationForm({ reason: '', rating: 5 });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to nominate bid'));
    }
  };

  const handleComplete = async () => {
    setCompleteSubmitLoading(true);
    try {
      const isRequester = selectedQuote.requester_id === currentMemberId;
      const result = await quotesApi.completeQuote(
        selectedQuote.quote_id,
        isRequester ? completionForm.rating : null,
        isRequester ? completionForm.feedback : null,
        !isRequester ? completionForm.rating : null,
        !isRequester ? completionForm.feedback : null,
        currentMemberId
      );
      const isNowCompleted = result?.data?.status === 'Completed';
      toast.success(isNowCompleted ? 'Quote completed! It will appear in the Closed tab.' : 'Rating submitted!');
      setShowCompletionDialog(false);
      setCompletionForm({ rating: 5, feedback: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to submit rating'));
    } finally {
      setCompleteSubmitLoading(false);
    }
  };

  const handleSubmitPaymentDetails = async () => {
    if (!isCompanyAdmin && !selectedPaymentBankDetailId) {
      toast.error('Please select a saved company bank account.');
      return;
    }

    if (!isCompanyAdmin && selectedPaymentBankDetailId === '__new__') {
      toast.error('Only company admin can add new bank details.');
      return;
    }

    if (!selectedQuote?.quote_id || !paymentDetailsForm.account_name || !paymentDetailsForm.bank_name ||
      !paymentDetailsForm.account_number || !paymentDetailsForm.due_amount || !paymentDetailsForm.payment_due_date) {
      toast.error('Please fill Account Name, Bank Name, Account Number, Due Amount, and Payment by date.');
      return;
    }
    setPaymentDetailsSubmitLoading(true);
    try {
      const selectedSavedBank = paymentSavedBankDetails.find((details) => `saved:${details.detail_id}` === selectedPaymentBankDetailId);
      const bankDetailLabel = selectedSavedBank?.label || getPaymentBankDetailsLabel(paymentDetailsForm, 'Payment bank details');
      let attachmentUrl = undefined;
      if (paymentDetailsFile) {
        const uploadRes = await uploadsApi.uploadFile(paymentDetailsFile, 'document', selectedQuote.quote_id, currentMemberId);
        const base = process.env.REACT_APP_BACKEND_URL || '';
        attachmentUrl = uploadRes.data?.file_url || uploadRes.data?.url || uploadRes.data?.file?.url;
        if (attachmentUrl && attachmentUrl.startsWith('/')) attachmentUrl = base + attachmentUrl;
      }
      await quotesApi.submitQuotePaymentDetails(selectedQuote.quote_id, {
        account_name: paymentDetailsForm.account_name,
        bank_name: paymentDetailsForm.bank_name,
        account_number: paymentDetailsForm.account_number,
        iban: paymentDetailsForm.iban || undefined,
        swift_code: paymentDetailsForm.swift_code || undefined,
        bank_address: paymentDetailsForm.bank_address || undefined,
        amount: parseFloat(paymentDetailsForm.due_amount),
        currency: paymentDetailsForm.currency || 'USD',
        payment_due_date: paymentDetailsForm.payment_due_date,
        attachment_url: attachmentUrl,
        bank_detail_label: bankDetailLabel,
        save_bank_detail: isCompanyAdmin,
      }, currentMemberId);

      toast.success('Payment details submitted. Requester can now pay.');
      setShowPaymentDetailsDialog(false);
      setPaymentDetailsForm({ account_name: '', bank_name: '', account_number: '', iban: '', swift_code: '', bank_address: '', deal_amount: '', due_amount: '', currency: 'USD', payment_due_date: '' });
      setPaymentSavedBankDetails([]);
      setSelectedPaymentBankDetailId('');
      setPaymentDetailsFile(null);
      setPaymentDetailsFileName('');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to submit payment details'));
    }
    setPaymentDetailsSubmitLoading(false);
  };

  const handlePayNowSubmit = async () => {
    if (!selectedQuote?.quote_payment_id) {
      toast.error('Payment details not found for this quote.');
      return;
    }
    const { payer_account_name, payer_bank_name, payer_account_last4, transaction_reference, transfer_date, proof_file } = payNowForm;
    if (!payer_account_name?.trim() || !payer_bank_name?.trim() || !transaction_reference?.trim() || !transfer_date || !proof_file) {
      toast.error('Please fill all fields and upload proof of transfer.');
      return;
    }
    if (transfer_date > getTodayDateValue()) {
      toast.error('Transfer date cannot be in the future.');
      return;
    }
    const last4 = String(payer_account_last4 || '').replace(/\D/g, '').slice(-4);
    if (last4.length !== 4) {
      toast.error('Please enter the last 4 digits of your account number.');
      return;
    }
    setPayNowSubmitLoading(true);
    try {
      let proofUrl = null;
      if (proof_file) {
        const uploadRes = await uploadsApi.uploadFile(proof_file, 'payment_proof', selectedQuote.quote_payment_id, currentMemberId);
        const base = process.env.REACT_APP_BACKEND_URL || '';
        proofUrl = uploadRes.data?.file_url || uploadRes.data?.url || uploadRes.data?.file?.url;
        if (proofUrl && proofUrl.startsWith('/')) proofUrl = base + proofUrl;
      }
      await paymentsApi.submitBankTransferDetails(selectedQuote.quote_payment_id, {
        payer_account_name: payer_account_name.trim(),
        payer_bank_name: payer_bank_name.trim(),
        payer_account_last4: last4,
        transaction_reference: transaction_reference.trim(),
        transfer_date,
        proof_url: proofUrl,
      });
      toast.success('Payment details submitted. Verification within 24 hours.');
      setShowPayNowDialog(false);
      setPayNowForm({ payer_account_name: '', payer_bank_name: '', payer_account_last4: '', transaction_reference: '', transfer_date: '', proof_file: null });
      setPayNowProofFileName('');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to submit payment details'));
    }
    setPayNowSubmitLoading(false);
  };

  const handlePaymentReceivedSubmit = async () => {
    setPaymentReceivedSubmitLoading(true);
    try {
      await quotesApi.paymentReceived(
        selectedQuote.quote_id,
        currentMemberId,
        completionForm.rating,
        completionForm.feedback
      );
      toast.success('Payment received confirmed. Thank you for your rating!');
      setShowPaymentReceivedDialog(false);
      setCompletionForm({ rating: 5, feedback: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to confirm'));
    } finally {
      setPaymentReceivedSubmitLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      Draft: { className: 'bg-gray-100 text-gray-700', label: 'Draft' },
      Active: { className: 'bg-green-100 text-green-700', label: 'Active' },
      Pending: { className: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
      Assigned: { className: 'bg-blue-100 text-blue-700', label: 'Assigned' },
      Nominated: { className: 'bg-purple-100 text-purple-700', label: 'Nominated' },
      Completed: { className: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
      Cancelled: { className: 'bg-red-100 text-red-700', label: 'Cancelled' },
      Closed: { className: 'bg-gray-100 text-gray-700', label: 'Closed' }
    };
    return badges[status] || badges.Draft;
  };

  const getQuoteDisplayStatusBadge = (quote) => {
    if (quote?.status === 'Nominated' && quote?.requester_id !== currentMemberId && !quote?.my_bid_nominated) {
      return {
        className: 'bg-amber-100 text-amber-800',
        label: 'Not accepting any quotes'
      };
    }
    return getStatusBadge(quote?.status);
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      Normal: { className: 'bg-gray-100 text-gray-600', label: 'Normal' },
      Medium: { className: 'bg-amber-100 text-amber-700', label: 'Medium' },
      High: { className: 'bg-red-100 text-red-700', label: 'High' }
    };
    return badges[priority] || badges.Normal;
  };

  const calculateTotals = (items) => {
    let subtotal = 0;
    let taxAmount = 0;
    let atActuals = 0;

    (items || []).forEach(item => {
      if (item.is_at_actuals) {
        atActuals += (item.unit_price || 0) * (item.quantity || 1);
      } else {
        const lineTotal = (item.unit_price || 0) * (item.quantity || 1);
        subtotal += lineTotal;
        if (item.tax_applicable && item.tax_rate) {
          taxAmount += lineTotal * (item.tax_rate / 100);
        }
      }
    });

    return {
      subtotal: subtotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      atActuals: atActuals.toFixed(2),
      total: (subtotal + taxAmount + atActuals).toFixed(2)
    };
  };

  const getQuotePaymentRequestAmount = useCallback((quote, bid = null) => {
    if (!quote) return '';

    const nominatedTotal = bid?.line_items?.length
      ? calculateTotals(bid.line_items).total
      : (quote?.nominated_bid_id && selectedQuote?.quote_id === quote.quote_id && nominatedBidDetail?.line_items?.length
          ? calculateTotals(nominatedBidDetail.line_items).total
          : null);

    const fallbackTotal =
      quote?.payment_details?.amount ??
      quote?.nominated_bid_total ??
      quote?.my_bid_total ??
      quote?.total_amount ??
      quote?.total;

    const amount = nominatedTotal ?? fallbackTotal;
    if (amount == null || amount === '') return '';
    return String(amount);
  }, [nominatedBidDetail, selectedQuote]);

  const getQuotePaymentRequestCurrency = useCallback((quote, bid = null) => {
    if (!quote && !bid) return 'USD';

    return (
      bid?.currency ||
      quote?.payment_details?.currency ||
      quote?.currency ||
      quote?.nominated_bid_currency ||
      quote?.my_bid_currency ||
      'USD'
    );
  }, []);

  const paymentBankDetailDropdownOptions = useMemo(() => {
    const options = [];
    paymentSavedBankDetails.forEach((details) => {
      if (!details.detail_id) return;
      options.push({
        value: `saved:${details.detail_id}`,
        label: details.label || getPaymentBankDetailsLabel(details)
      });
    });
    if (isCompanyAdmin) {
      options.push({
        value: '__new__',
        label: 'Add new bank details'
      });
    }
    return options;
  }, [isCompanyAdmin, paymentSavedBankDetails]);

  const handlePaymentBankDetailSelect = useCallback((value) => {
    setSelectedPaymentBankDetailId(value);
    if (value === '__new__' && isCompanyAdmin) {
      setPaymentDetailsForm((prev) => buildPaymentDetailsFormFromBankDetails(prev, {}));
      return;
    }

    const selectedDetails = paymentSavedBankDetails.find((details) => `saved:${details.detail_id}` === value);

    if (selectedDetails) {
      setPaymentDetailsForm((prev) => buildPaymentDetailsFormFromBankDetails(prev, selectedDetails));
    }
  }, [isCompanyAdmin, paymentSavedBankDetails]);

  const isBankDetailsReadOnly = !isCompanyAdmin;

  const openPaymentDetailsDialog = useCallback(async (quote, { closeQuoteDetail = false } = {}) => {
    if (!quote) return;

    const nominatedBidPromise = quote?.quote_id && quote?.nominated_bid_id
      ? quotesApi.getQuoteBids(quote.quote_id)
        .then((bidsRes) => (bidsRes?.data || []).find((bid) => bid.bid_id === quote.nominated_bid_id) || null)
        .catch((error) => {
          console.error('Failed to load nominated bid total for payment details:', error);
          return null;
        })
      : Promise.resolve(null);
    const savedDetailsPromise = currentMemberId
      ? quotesApi.getPaymentBankDetails(currentMemberId)
        .then((res) => (res?.data?.details || []).map(normalizeSavedPaymentBankDetails))
        .catch((error) => {
          console.error('Failed to load saved payment bank details:', error);
          return [];
        })
      : Promise.resolve([]);

    const [nominatedBid, savedBankDetails] = await Promise.all([
      nominatedBidPromise,
      savedDetailsPromise
    ]);
    const defaultSavedBank = savedBankDetails[0] || {};
    const hasSavedBankDetails = hasPaymentBankDetails(defaultSavedBank);
    const defaultBankDetails = hasSavedBankDetails ? defaultSavedBank : {};

    setSelectedQuote(quote);
    setPaymentSavedBankDetails(savedBankDetails);
    setSelectedPaymentBankDetailId(
      hasSavedBankDetails
        ? `saved:${defaultSavedBank.detail_id}`
        : (isCompanyAdmin ? '__new__' : '')
    );
    const requestAmount = getQuotePaymentRequestAmount(quote, nominatedBid);
    const requestCurrency = getQuotePaymentRequestCurrency(quote, nominatedBid);
    setPaymentDetailsForm({
      account_name: defaultBankDetails.account_name || '',
      bank_name: defaultBankDetails.bank_name || '',
      account_number: defaultBankDetails.account_number || '',
      iban: defaultBankDetails.iban || '',
      swift_code: defaultBankDetails.swift_code || '',
      bank_address: defaultBankDetails.bank_address || '',
      deal_amount: requestAmount,
      due_amount: requestAmount,
      currency: requestCurrency,
      payment_due_date: ''
    });
    if (closeQuoteDetail) {
      setShowQuoteDetailDialog(false);
    }
    setShowPaymentDetailsDialog(true);
  }, [currentMemberId, getQuotePaymentRequestAmount, getQuotePaymentRequestCurrency, isCompanyAdmin]);

  // Handle opening quote-specific chat thread (not direct user chat)
  const handleOpenQuoteChat = (quote) => {
    const isRequester = quote.requester_id === currentMemberId;
    const otherMemberId = isRequester
      ? quote.nominated_member_id || quote.bidder_id
      : quote.requester_id;
    const companyName = isRequester
      ? quote.nominated_company || quote.bidder_company || 'Member'
      : quote.requester_company || quote.requester_company_name || 'Member';
    const otherParticipantName = isRequester
      ? (quote.nominated_name || quote.nominated_company || quote.bidder_company || 'Member')
      : (quote.requester_name || quote.requester_company || quote.requester_company_name || 'Member');

    if (!otherMemberId) {
      toast.error('No chat partner available for this quote');
      return;
    }

    navigate('/chat', {
      state: {
        openQuoteChat: {
          quote_id: quote.quote_id,
          quote_reference: quote.reference_number || quote.quote_id,
          member_id: otherMemberId,
          company_name: companyName,
          other_participant_name: otherParticipantName
        }
      }
    });
    toast.success(`Opening chat thread for quote ${quote.reference_number || quote.quote_id}`);
  };

  const handleMessageBidderFromComparison = (bid) => {
    if (!selectedQuote?.quote_id || selectedQuote.requester_id !== currentMemberId) {
      toast.error('Only the inquiry creator can message a quoted member');
      return;
    }
    if (!bid?.bid_id || !bid?.bidder_id) {
      toast.error('The selected submitted quote is unavailable');
      return;
    }

    setShowBidComparisonDialog(false);
    navigate('/chat', {
      state: {
        openQuoteChat: {
          quote_id: selectedQuote.quote_id,
          quote_reference: selectedQuote.reference_number || selectedQuote.quote_id,
          bid_id: bid.bid_id,
          member_id: bid.bidder_id,
          company_name: bid.bidder_company || 'Member',
          other_participant_name: bid.bidder_name || bid.bidder_company || 'Member'
        }
      }
    });
  };

  // Nominated sub-filter: by_me = I'm requester (I nominated someone), for_me = my bid was nominated
  const nominatedByMe = nominatedQuotes.filter(q => q.requester_id === currentMemberId);
  const nominatedForMe = nominatedQuotes.filter(q => q.requester_id !== currentMemberId && q.my_bid_nominated);
  const nominatedFiltered =
    activeTab !== 'nominated' ? []
      : nominatedSubFilter === 'by_me' ? nominatedByMe
        : nominatedSubFilter === 'for_me' ? nominatedForMe
          : nominatedQuotes;

  const visibleTeamEnquiriesQuotes = useMemo(
    () => (canAccessTeamEnquiries ? teamEnquiriesQuotes : []),
    [canAccessTeamEnquiries, teamEnquiriesQuotes]
  );
  const allSentQuotes = [...quotes, ...visibleTeamEnquiriesQuotes].filter((quote, index, list) =>
    index === list.findIndex((item) => item.quote_id === quote.quote_id)
  );
  const sentFiltered =
    activeTab !== 'sent' ? []
      : sentSubFilter === 'mine' ? quotes
        : sentSubFilter === 'team' ? visibleTeamEnquiriesQuotes
          : allSentQuotes;

  // Filter quotes based on active tab.
  const rawDisplayQuotes = activeTab === 'sent'
    ? sentFiltered
    : activeTab === 'drafts'
      ? draftQuotes
    : activeTab === 'received'
      ? receivedQuotes
      : activeTab === 'quoted'
        ? quotedQuotes
        : activeTab === 'nominated'
          ? nominatedFiltered
          : activeTab === 'closed'
            ? closedQuotes
            : activeTab === 'assignedToMe'
            ? assignedToMeQuotes
            : activeTab === 'archived'
              ? archivedQuotes
              : quotes;
  const displayQuotes = [...rawDisplayQuotes].sort((a, b) => {
    if (activeTab === 'received') {
      const priorityDiff = getQuotePriorityRank(b) - getQuotePriorityRank(a);
      if (priorityDiff !== 0) return priorityDiff;
    }
    return new Date(b.created_at) - new Date(a.created_at);
  });
  const QUOTES_PER_PAGE = 10;
  const totalPages = Math.max(1, Math.ceil(displayQuotes.length / QUOTES_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedQuotes = displayQuotes.slice(
    (safeCurrentPage - 1) * QUOTES_PER_PAGE,
    safeCurrentPage * QUOTES_PER_PAGE
  );
  const pageStart = displayQuotes.length === 0 ? 0 : ((safeCurrentPage - 1) * QUOTES_PER_PAGE) + 1;
  const pageEnd = Math.min(safeCurrentPage * QUOTES_PER_PAGE, displayQuotes.length);

  // Get counts for each tab
  const tabCounts = {
    drafts: draftQuotes.length,
    sent: allSentQuotes.length,
    received: receivedQuotes.length,
    quoted: quotedQuotes.length,
    nominated: nominatedQuotes.length,
    closed: closedQuotes.length,
    assignedToMe: assignedToMeQuotes.length,
    archived: archivedQuotes.length
  };

  useEffect(() => {
    const pending = pendingQuoteNavigationRef.current;
    if (!pending) return;

    const quoteMatches = (quote) =>
      (pending.quote_id && quote.quote_id === pending.quote_id) ||
      (pending.reference_number && quote.reference_number === pending.reference_number);

    const tabOrder = [
      ['received', receivedQuotes],
      ['quoted', quotedQuotes],
      ['nominated', nominatedQuotes],
      ['closed', closedQuotes],
      ['assignedToMe', assignedToMeQuotes],
      ['archived', archivedQuotes],
      ['sent', quotes],
      ['sent-team', visibleTeamEnquiriesQuotes],
      ['drafts', draftQuotes],
    ];

    const match = tabOrder.find(([, list]) => list.some(quoteMatches));
    if (!match) return;

    const [tab, list] = match;
    const matchedQuote = list.find(quoteMatches);
    if (!matchedQuote) return;

    pendingQuoteNavigationRef.current = null;
    setActiveTab(tab === 'sent-team' ? 'sent' : tab);
    if (tab === 'sent-team') {
      setSentSubFilter('team');
    } else if (tab === 'sent') {
      setSentSubFilter('mine');
    }
    openQuoteDetails(matchedQuote);
  }, [receivedQuotes, quotedQuotes, nominatedQuotes, closedQuotes, assignedToMeQuotes, archivedQuotes, visibleTeamEnquiriesQuotes, quotes, draftQuotes, openQuoteDetails, pendingQuoteNavigationVersion]);

  const applyPublicSharingState = useCallback((quoteId, sharingState) => {
    const updateQuote = (quote) =>
      quote.quote_id === quoteId
        ? {
            ...quote,
            public_sharing_enabled: sharingState.public_sharing_enabled,
            public_share_token:
              sharingState.public_share_token ?? quote.public_share_token,
            can_manage_public_sharing:
              sharingState.can_manage_public_sharing ?? quote.can_manage_public_sharing,
            can_copy_public_link:
              sharingState.can_copy_public_link ?? quote.can_copy_public_link,
            public_sharing_updated_at:
              sharingState.updated_at ?? quote.public_sharing_updated_at,
          }
        : quote;
    const updateList = (items) => items.map(updateQuote);

    setQuotes(updateList);
    setDraftQuotes(updateList);
    setTeamEnquiriesQuotes(updateList);
    setNominatedQuotes(updateList);
    setClosedQuotes(updateList);
    setAssignedToMeQuotes(updateList);
    setSelectedQuote((quote) => (quote ? updateQuote(quote) : quote));
    setCreatedInquiry((quote) => (quote ? updateQuote(quote) : quote));
  }, []);

  const handlePublicSharingChange = useCallback(async (quote, enabled) => {
    if (!quote?.quote_id) return;
    try {
      setSharingUpdateQuoteId(quote.quote_id);
      const response = await quotesApi.updatePublicSharing(quote.quote_id, enabled);
      applyPublicSharingState(quote.quote_id, response.data);
      toast.success(
        enabled
          ? 'Public inquiry link enabled'
          : 'Public inquiry link disabled'
      );
    } catch (error) {
      toast.error(
        getErrorMessage(
          error.response?.data?.detail,
          'Failed to update public sharing'
        )
      );
    } finally {
      setSharingUpdateQuoteId(null);
    }
  }, [applyPublicSharingState]);

  const copyPublicInquiryLink = useCallback(async (shareToken) => {
    const publicUrl = getPublicInquiryUrl(shareToken);
    if (!publicUrl) {
      toast.error('Enable public sharing to create a link');
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public inquiry link copied');
    } catch (error) {
      const input = document.createElement('textarea');
      input.value = publicUrl;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      toast.success('Public inquiry link copied');
    }
  }, []);

  const renderPendingHiddenReceivedRow = (quote) => (
    <Card
      key={quote.quote_id}
      className="border-amber-200 bg-amber-50/70"
      data-testid={`quote-hide-pending-${quote.quote_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-amber-900">
            Quote {quote.reference_number || quote.quote_id} marked as not interested. Would you like to undo this action?
          </p>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
            onClick={() => undoHideReceivedQuote(quote.quote_id)}
          >
            Undo
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render a quote card with full details - used across all tabs
  const renderQuoteCard = (quote, tabType) => {
    const statusBadge = getQuoteDisplayStatusBadge(quote);
    const priorityBadge = getPriorityBadge(quote.priority);
    const cargoSummary = getCargoSummary(quote);
    const isTeamEnquiry = tabType === 'teamEnquiries';
    const isSentByMe = quote.requester_id === currentMemberId;
    const isCurrentUsersCompanyInquiry = Boolean(
      memberProfile?.company_id &&
      quote.requester_company_id === memberProfile.company_id
    );
    const canManagePublicSharing = Boolean(
      quote.can_manage_public_sharing === true ||
      isSentByMe ||
      (isCompanyAdmin && isCurrentUsersCompanyInquiry)
    );
    const canCopyPublicLink = Boolean(
      quote.can_copy_public_link === true ||
      (isSentByMe && quote.public_sharing_enabled && quote.public_share_token)
    );
    const canEditPostedInquiry = quote.can_edit_posted_inquiry === true;
    const bidLost = isBidLostQuote(quote);
    const highlightTerm = searchHighlightTerm;
    const priorityUntilLabel = formatPriorityUntilLabel(quote.priority_until);
    const responseDeadlineInfo = getResponseDeadlineInfo(quote, deadlineNow);

    return (
      <Card
        key={quote.quote_id}
        className={`hover:shadow-md transition-shadow ${quote.is_promoted ? 'border-2 border-amber-400 bg-amber-50/30' : ''}`}
        data-testid={`quote-card-${quote.quote_id}`}
      >
        <CardContent className="p-4">
          {/* Header Row */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center flex-wrap gap-2">
              {quote.reference_number && (
                <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  <Hash size={12} className="inline mr-1" />
                  {highlightText(quote.reference_number, highlightTerm)}
                </span>
              )}
              <h3 className="font-semibold text-gray-900">{quote.quote_type}</h3>
              <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
              {quote.last_edited_at && (
                <Badge className="bg-sky-100 text-sky-800">
                  <Edit size={12} className="mr-1" />
                  Updated
                </Badge>
              )}
              {pointsSystemEnabled && (
                <Badge className={priorityBadge.className}>{priorityBadge.label}</Badge>
              )}
              {pointsSystemEnabled && quote.is_promoted && (
                <Badge className="bg-amber-100 text-amber-700">
                  <ArrowUp size={12} className="mr-1" />
                  Promoted
                </Badge>
              )}
              {quote.is_voided && (
                <Badge className="bg-gray-200 text-gray-700">
                  <EyeOff size={12} className="mr-1" />
                  Voided
                </Badge>
              )}
              {quote.has_submitted_bid && !quote.my_bid_nominated && !bidLost && (
                <Badge className="bg-green-100 text-green-700">
                  <Check size={12} className="mr-1" />
                  {quote.company_bid_by && !quote.my_bid_id ? 'Company Bid Submitted' : 'Bid Submitted'}
                </Badge>
              )}
              {quote.my_price_rank && ['quoted', 'nominated'].includes(tabType) && (
                <Badge
                  className="border border-indigo-200 bg-indigo-50 text-indigo-800"
                  data-testid={`quotation-price-rank-${quote.quote_id}`}
                  title="Your confidential price position. Lower quoted totals rank first."
                >
                  Your price rank: {quote.my_price_rank}
                </Badge>
              )}
              {bidLost && (
                <Badge className="bg-amber-100 text-amber-800">
                  <X size={12} className="mr-1" />
                  Bid Lost
                </Badge>
              )}
              {quote.my_bid_nominated && (
                <Badge className="bg-purple-100 text-purple-700">
                  <CheckCircle size={12} className="mr-1" />
                  Your Bid Nominated
                </Badge>
              )}
              {quote.rfq_accepted_at && (
                <Badge className="bg-blue-100 text-blue-700">
                  <CheckCircle size={12} className="mr-1" />
                  Shipment Completed
                </Badge>
              )}
              {quote.payment_received_at && (
                <Badge className="bg-emerald-100 text-emerald-700">
                  <CheckCircle size={12} className="mr-1" />
                  Payment Completed
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 flex items-center">
                <Clock size={12} className="mr-1" />
                {new Date(quote.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
              </span>
              {tabType === 'received' && memberProfile?.is_company_admin && memberProfile?.company_id && (
                <Button
                  size="sm"
                  className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white border-0 ml-1"
                  onClick={async () => {
                    setAssignQuoteFor(quote);
                    setAssignLoading(true);
                    try {
                      const res = await teamApi.getTeamMembers(memberProfile.company_id, false);
                      setAssignTeamMembers(res.data || []);
                      setShowAssignDialog(true);
                    } catch (e) {
                      toast.error('Failed to load team members');
                    } finally {
                      setAssignLoading(false);
                    }
                  }}
                >
                  Assign
                </Button>
              )}
            </div>
          </div>

          {/* Main Details Grid - All in one row (7 cols on large); Scope/containers wrap with increased row height, no tooltip */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm mb-3 items-start min-h-[3.5rem]">
            {/* Route */}
            <div className="col-span-2 min-w-0">
              <span className="text-gray-500 text-xs block">Route</span>
              {quote.quote_type === 'Warehousing' ? (
                <div className="flex items-center font-medium">
                  <MapPin size={14} className="mr-1 text-green-600 shrink-0" />
                  {highlightText(getQuoteRouteDisplay(quote).originText, highlightTerm)}
                </div>
              ) : (
                <div className="flex items-center font-medium">
                  <MapPin size={14} className="mr-1 text-green-600 shrink-0" />
                  {highlightText(getQuoteRouteDisplay(quote).originText, highlightTerm)}
                  <span className="mx-2 text-gray-400">-&gt;</span>
                  <MapPin size={14} className="mr-1 text-red-600 shrink-0" />
                  {highlightText(getQuoteRouteDisplay(quote).destinationText, highlightTerm)}
                </div>
              )}
            </div>

            {/* Commodity */}
            <div className="min-w-0">
              <span className="text-gray-500 text-xs block">Commodity</span>
              <span className="font-medium">{highlightText(quote.commodity || 'General', highlightTerm)}</span>
            </div>

            {/* Incoterm */}
            <div className="min-w-0">
              <span className="text-gray-500 text-xs block">Incoterm</span>
              <span className="font-medium">{Array.isArray(quote.incoterm) ? quote.incoterm.join(', ') : (quote.incoterm || 'FOB')}</span>
            </div>

            {/* Scope - full content visible, no tooltip */}
            <div className="min-w-0 min-h-[2.5rem]">
              <span className="text-gray-500 text-xs block">Scope</span>
              <span className="font-medium block break-words">{getQuoteScopeDisplay(quote)}</span>
            </div>

            {quote.quote_type !== 'Air' && (
              <>
                {/* Standard container - full content visible, no tooltip */}
                <div className="min-w-0 min-h-[2.5rem]">
                  <span className="text-gray-500 text-xs block">Standard container</span>
                  <span className="font-medium block break-words">{getQuoteStandardContainerDisplay(quote)}</span>
                </div>
                {/* Special container - show types only; full details on hover */}
                <div className="min-w-0 min-h-[2.5rem]">
                  <span className="text-gray-500 text-xs block">Special container</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className={
                            getQuoteSpecialContainerTypesOnly(quote) !== '—'
                              ? 'font-medium block break-words text-blue-600 hover:text-blue-800 hover:underline cursor-help underline-offset-2 decoration-blue-500/70 transition-colors'
                              : 'font-medium block break-words text-gray-600'
                          }
                        >
                          {getQuoteSpecialContainerTypesOnly(quote)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap text-left">
                        {getQuoteSpecialContainerFullDetails(quote)}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </>
            )}
          </div>

          {/* Creator identity stays visible in every inquiry list, including team enquiries. */}
          <div className="mb-3 rounded bg-blue-50 p-2">
            <InquiryCreatorIdentity
              name={quote.requester_name || quote.requester_company || quote.requester_company_name}
              profilePicture={quote.requester_profile_picture}
              companyName={quote.requester_company || quote.requester_company_name}
              compact
            />
          </div>

          {quote.last_edited_at && (
            <div className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              <Edit size={13} className="shrink-0" />
              <span>
                Updated {new Date(quote.last_edited_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                {quote.last_edited_by_name ? ` by ${quote.last_edited_by_name}` : ''}
                {quote.last_edit_summary ? ` — ${quote.last_edit_summary}` : ''}
              </span>
            </div>
          )}

          {/* Nominated provider - For "Nominated by me": show company name and member name (in brackets) */}
          {tabType === 'nominated' && isSentByMe && (quote.nominated_company || quote.nominated_name || quote.nominated_member_id) && (
            <div className="flex items-center gap-4 p-2 bg-purple-50 rounded text-sm mb-3">
              <div className="flex items-center">
                <User size={14} className="mr-1 text-purple-600" />
                <span className="text-gray-500">Nominated:</span>
                <span className="font-medium ml-1">
                  {quote.nominated_company || quote.nominated_member_id}
                  {quote.nominated_name ? ` (${quote.nominated_name})` : quote.nominated_company && quote.nominated_member_id ? ` (${quote.nominated_member_id})` : ''}
                </span>
              </div>
            </div>
          )}

          {/* Cargo Summary Badges */}
          {cargoSummary.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {cargoSummary.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded"
                >
                  <span className="mr-1">{item.icon}</span>
                  {item.text}
                </span>
              ))}
              {quote.is_hazardous && (
                <span className="inline-flex items-center text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded">
                  ⚠️ Hazardous {quote.hazardous_class ? `(Class ${quote.hazardous_class})` : ''}
                </span>
              )}
            </div>
          )}

          {/* Cargo Description */}
          {getQuoteDisplayCargoDescription(quote) && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
              {getQuoteDisplayCargoDescription(quote)}
            </p>
          )}

          {quote.hs_code && (
            <div className="mb-3 text-sm text-slate-600">
              <span className="font-medium text-slate-800">HS code:</span> {quote.hs_code}
            </div>
          )}
          {renderInquiryAttachments(quote.attachments, true)}

          {pointsSystemEnabled && priorityUntilLabel && quote.priority !== 'Normal' && (
            <div className="mb-3 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900">
              <span className="font-medium">Priority until:</span> {priorityUntilLabel}
            </div>
          )}

          {responseDeadlineInfo && (
            <div className={`mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-3 py-2 text-sm ${responseDeadlineInfo.isClosed ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900'}`}>
              <Clock size={15} />
              <span className="font-medium">Responses close:</span>
              <span>{responseDeadlineInfo.dateLabel}</span>
              <Badge className={responseDeadlineInfo.isClosed ? 'bg-red-100 text-red-800 hover:bg-red-100' : 'bg-amber-100 text-amber-800 hover:bg-amber-100'}>
                {responseDeadlineInfo.countdown}
              </Badge>
            </div>
          )}

          {(isSentByMe || isTeamEnquiry) && quote.status !== 'Draft' && (
            <div className="mb-3 flex items-center gap-1.5 text-sm text-slate-600" data-testid={`enquiry-view-count-${quote.quote_id}`}>
              <Eye size={14} className="text-blue-600" />
              Seen by {quote.enquiry_view_count || 0} {(quote.enquiry_view_count || 0) === 1 ? 'member' : 'members'}
            </div>
          )}

          {(canManagePublicSharing || canCopyPublicLink) &&
            (tabType === 'sent' || isTeamEnquiry) && (
            <div className="mb-3 flex flex-col gap-3 border border-slate-200 bg-slate-50 px-3 py-3 md:flex-row md:items-center md:justify-between">
              {canManagePublicSharing ? (
                <div className="flex items-center gap-3">
                  <Switch
                    id={`public-sharing-${quote.quote_id}`}
                    checked={quote.public_sharing_enabled === true}
                    onCheckedChange={(checked) =>
                      handlePublicSharingChange(quote, checked)
                    }
                    disabled={sharingUpdateQuoteId === quote.quote_id}
                  />
                  <label
                    htmlFor={`public-sharing-${quote.quote_id}`}
                    className="cursor-pointer"
                  >
                    <span className="block text-sm font-medium text-slate-900">
                      Anyone with the link can view
                    </span>
                    <span className="block text-xs text-slate-500">
                      {quote.public_sharing_enabled
                        ? 'Public sharing is active'
                        : 'Public link is disabled'}
                    </span>
                  </label>
                  {sharingUpdateQuoteId === quote.quote_id && (
                    <Loader2 size={15} className="animate-spin text-blue-600" />
                  )}
                </div>
              ) : (
                <div>
                  <span className="block text-sm font-medium text-slate-900">
                    Public sharing is active
                  </span>
                  <span className="block text-xs text-slate-500">
                    Your team can copy this inquiry link.
                  </span>
                </div>
              )}
              {canCopyPublicLink && quote.public_share_token && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => copyPublicInquiryLink(quote.public_share_token)}
                  >
                    <Copy size={14} className="mr-1" />
                    Copy Link
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      window.open(
                        getPublicInquiryUrl(quote.public_share_token),
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  >
                    <Globe size={14} className="mr-1" />
                    View Public Page
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 pt-3 border-t">
            <Button
              size="sm"
              variant="outline"
              onClick={() => openQuoteDetails(quote)}
            >
              <Eye size={14} className="mr-1" />
              View Details
            </Button>
            {tabType === 'drafts' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEditDraft(quote)}
              >
                <Edit size={14} className="mr-1" />
                Edit Draft
              </Button>
            )}
            {canEditPostedInquiry && tabType !== 'drafts' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEditPostedInquiry(quote)}
              >
                <Edit size={14} className="mr-1" />
                Edit Inquiry
              </Button>
            )}
            {isSentByMe && !['closed', 'drafts'].includes(tabType) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCloneQuote(quote)}
                title="Clone this quote to create a new one with the same details"
              >
                <Copy size={14} className="mr-1" />
                Clone
              </Button>
            )}

            {/* Message Requester - after View Details to keep sequence: View Details, Message, Download Proof, Payment Received */}
            {!isSentByMe && !isTeamEnquiry && (
              <Button size="sm" variant="outline" asChild>
                <Link
                  to="/chat"
                  state={{
                    openQuoteChat: {
                      quote_id: quote.quote_id,
                      quote_reference: quote.reference_number || quote.quote_id,
                      member_id: quote.requester_id,
                      company_name: quote.requester_company || 'Member',
                      other_participant_name: quote.requester_name || quote.requester_company || 'Member'
                    }
                  }}
                  onClick={() => setSelectedQuote(quote)}
                >
                  <MessageSquare size={14} className="mr-1" />
                  Message Requester
                </Link>
              </Button>
            )}

            {/* Payment proof download: receiver only, when sender has submitted proof - after Message to keep sequence */}
            {!isSentByMe &&
              !isTeamEnquiry &&
              quote.nominated_member_id === currentMemberId &&
              quote.quote_payment_id &&
              quote.payment_submitted_at && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await quotesApi.getPaymentProof(quote.quote_id);
                    const url = res.data?.url;
                    if (url) {
                      const base = process.env.REACT_APP_BACKEND_URL || '';
                      const fullUrl = url.startsWith('http') ? url : `${base.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
                      window.open(fullUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      toast.error('Proof URL not available');
                    }
                  } catch (e) {
                    if (e?.response?.status === 404) {
                      toast.info('Payment proof not yet uploaded by sender');
                    } else {
                      toast.error(getErrorMessage(e?.response?.data?.detail, 'Failed to load payment proof'));
                    }
                  }
                }}
                title="Download proof of transfer uploaded by sender"
              >
                <Download size={14} className="mr-1" />
                Payment proof download
              </Button>
            )}

            {/* Bid actions for received quotes */}
            {!isSentByMe && !isTeamEnquiry && quote.status === 'Active' && !quote.has_submitted_bid && (
              <>
                {responseDeadlineInfo?.isClosed ? (
                  <Button size="sm" variant="outline" disabled className="border-red-200 text-red-600 opacity-100">
                    <Clock size={14} className="mr-1" />
                    Submission Closed
                  </Button>
                ) : canCreateQuote() ? (
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => openBidDialog(quote)}
                    data-testid={`submit-bid-btn-${quote.quote_id}`}
                  >
                    <Send size={14} className="mr-1" />
                    Submit Quotation
                  </Button>
                ) : (
                  <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                    Quotation limit reached ({getRemainingQuotes() === 0 && '0 left'}). <Link to="/upgrade" className="font-medium underline">Upgrade your plan</Link> to submit more.
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => handleHideReceivedQuote(quote)}
                >
                  <EyeOff size={14} className="mr-1" />
                  Not Interested
                </Button>
              </>
            )}

            {/* Edit bid for already quoted; disable once requester nominates any bid */}
            {quote.has_submitted_bid && tabType === 'quoted' && !bidLost && quote.status !== 'Nominated' && !responseDeadlineInfo?.isClosed && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => openBidDialog(quote)}
              >
                <Edit size={14} className="mr-1" />
                Edit Quotation
              </Button>
            )}

            {/* Actions for quotes I sent */}
            {isSentByMe && (
              <>
                {quote.responses_count > 0 && tabType !== 'closed' && (
                  <Button
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={async () => {
                      setSelectedQuote(quote);
                      setLoadingBids(true);
                      try {
                        const bidsRes = await quotesApi.getQuoteBids(quote.quote_id);
                        setQuoteBids(bidsRes.data);
                        setShowBidComparisonDialog(true);
                      } catch (error) {
                        toast.error('Failed to load bids');
                      } finally {
                        setLoadingBids(false);
                      }
                    }}
                  >
                    <Scale size={14} className="mr-1" />
                    {['Nominated', 'Completed'].includes(quote.status) ? `View Bids (${quote.responses_count})` : `Compare Bids (${quote.responses_count})`}
                  </Button>
                )}

                {pointsSystemEnabled && !quote.is_promoted && quote.status === 'Active' && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openPromoteDialog(quote)}
                        >
                          <ArrowUp size={14} className="mr-1" />
                          Promote
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Promote to High or Medium priority for better visibility (costs points)</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Post-nomination: Requester actions (Mail Reminder, Message, Accept Shipment Completion, Pay Now, Complete) - show for requester even after Shipment Completed */}
                {quote.status === 'Nominated' && isSentByMe && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={reminderLoadingQuoteId === quote.quote_id}
                      onClick={async () => {
                        setReminderLoadingQuoteId(quote.quote_id);
                        try {
                          await quotesApi.sendQuoteReminder(quote.quote_id, currentMemberId);
                          toast.success('Reminder sent');
                        } catch (error) {
                          toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send reminder'));
                        } finally {
                          setReminderLoadingQuoteId(null);
                        }
                      }}
                    >
                      {reminderLoadingQuoteId === quote.quote_id ? (
                        <>
                          <Loader2 size={14} className="mr-1 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={14} className="mr-1" />
                          Mail Reminder
                        </>
                      )}
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <Link
                        to="/chat"
                        state={{
                          openQuoteChat: {
                            quote_id: quote.quote_id,
                            quote_reference: quote.reference_number || quote.quote_id,
                            member_id: quote.nominated_member_id,
                            company_name: quote.nominated_company || 'Quoted by',
                            other_participant_name: quote.nominated_name || quote.nominated_company || 'Quoted by'
                          }
                        }}
                        onClick={() => setSelectedQuote(quote)}
                      >
                        <MessageSquare size={14} className="mr-1" />
                        Message
                      </Link>
                    </Button>
                    {quote.rfq_completed_at && !quote.rfq_accepted_at && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={rfqReopenLoadingQuoteId === quote.quote_id || rfqAcceptLoadingQuoteId === quote.quote_id}
                          onClick={async () => {
                            setRfqReopenLoadingQuoteId(quote.quote_id);
                            try {
                              await quotesApi.rfqReopen(quote.quote_id, currentMemberId);
                              toast.success('RFQ reopened');
                              fetchData();
                            } catch (error) {
                              toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to reopen RFQ'));
                            } finally {
                              setRfqReopenLoadingQuoteId(null);
                            }
                          }}
                        >
                          {rfqReopenLoadingQuoteId === quote.quote_id ? (
                            <>
                              <Loader2 size={14} className="mr-1 animate-spin" />
                              Reopening...
                            </>
                          ) : (
                            <>
                              <RotateCcw size={14} className="mr-1" />
                              Reopen
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          disabled={rfqAcceptLoadingQuoteId === quote.quote_id || rfqReopenLoadingQuoteId === quote.quote_id}
                          onClick={async () => {
                            setRfqAcceptLoadingQuoteId(quote.quote_id);
                            try {
                              await quotesApi.rfqAccept(quote.quote_id, currentMemberId);
                              toast.success('Shipment completion accepted');
                              fetchData();
                            } catch (error) {
                              toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to accept RFQ'));
                            } finally {
                              setRfqAcceptLoadingQuoteId(null);
                            }
                          }}
                        >
                          {rfqAcceptLoadingQuoteId === quote.quote_id ? (
                            <>
                              <Loader2 size={14} className="mr-1 animate-spin" />
                              Accepting...
                            </>
                          ) : (
                            <>
                              <CheckCircle size={14} className="mr-1" />
                              Accept Shipment Completion
                            </>
                          )}
                        </Button>
                      </>
                    )}
                    {quote.rfq_accepted_at && (quote.payment_details || quote.quote_payment_id) && !quote.payment_submitted_at && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => {
                          setSelectedQuote(quote);
                          setShowPayNowDialog(true);
                        }}
                      >
                        <DollarSign size={14} className="mr-1" />
                        Pay Now
                      </Button>
                    )}
                    {quote.payment_submitted_at && quote.payment_received_at && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => {
                          setSelectedQuote(quote);
                          setCompletionForm({ rating: 5, feedback: '' });
                          setShowCompletionDialog(true);
                        }}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Complete
                      </Button>
                    )}
                  </>
                )}

                {/* Void / Publish - at right end, visible only to creator; hide for nominated/closed/completed quotes */}
                {!['Completed', 'Cancelled', 'Closed', 'Expired', 'Nominated'].includes(quote.status) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={`ml-auto ${quote.status === 'Draft'
                      ? 'bg-red-100 hover:bg-red-200 text-red-800 border-red-300'
                      : quote.is_voided
                        ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border-emerald-300'
                        : 'bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300'}`}
                    onClick={() => openVoidConfirmDialog(quote)}
                    title={quote.status === 'Draft'
                      ? 'Delete draft permanently'
                      : quote.is_voided
                        ? 'Publish – make visible to all'
                        : 'Void – visible only to you'}
                  >
                    {quote.status === 'Draft' ? (
                      <>
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </>
                    ) : quote.is_voided ? (
                      <>
                        <Globe size={14} className="mr-1" />
                        Publish
                      </>
                    ) : (
                      <>
                        <EyeOff size={14} className="mr-1" />
                        Void
                      </>
                    )}
                  </Button>
                )}
              </>
            )}

            {/* Post-nomination: Receiver (provider) actions - Shipment Completed, Request Payment Details, Payment Received, Message */}
            {!isSentByMe && !isTeamEnquiry && quote.status === 'Nominated' && quote.my_bid_nominated && (
              <>
                {!quote.rfq_completed_at && (
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={rfqCompleteLoadingQuoteId === quote.quote_id}
                    onClick={async () => {
                      setRfqCompleteLoadingQuoteId(quote.quote_id);
                      try {
                        await quotesApi.rfqComplete(quote.quote_id, currentMemberId);
                        toast.success('Shipment marked as completed');
                        fetchData();
                      } catch (error) {
                        toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to complete RFQ'));
                      } finally {
                        setRfqCompleteLoadingQuoteId(null);
                      }
                    }}
                  >
                    {rfqCompleteLoadingQuoteId === quote.quote_id ? (
                      <>
                        <Loader2 size={14} className="mr-1 animate-spin" />
                        Completing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={14} className="mr-1" />
                        Shipment Completed
                      </>
                    )}
                  </Button>
                )}
                {quote.rfq_accepted_at && !quote.payment_details && !quote.quote_payment_id && (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => {
                      openPaymentDetailsDialog(quote);
                    }}
                  >
                    <DollarSign size={14} className="mr-1" />
                    Request Payment Details
                  </Button>
                )}
                {quote.payment_submitted_at && !quote.payment_received_at && (
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => {
                      setSelectedQuote(quote);
                      setCompletionForm({ rating: 5, feedback: '' });
                      setShowPaymentReceivedDialog(true);
                    }}
                  >
                    <CheckCircle size={14} className="mr-1" />
                    Payment Received
                  </Button>
                )}
              </>
            )}

            {/* Chat button - opens quote-specific thread - REMOVED: Message Requester moved up to keep sequence View Details, Message, Download Proof, Payment Received */}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderPagination = () => {
    if (displayQuotes.length <= QUOTES_PER_PAGE) return null;

    return (
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white/90 px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600">
          Showing {pageStart}-{pageEnd} of {displayQuotes.length} quotes
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={safeCurrentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          >
            Previous
          </Button>
          <span className="min-w-24 text-center text-sm font-medium text-slate-700">
            Page {safeCurrentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={safeCurrentPage === totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const renderQuoteForm = () => {
    let initialData = editingPostedInquiry
      ? buildPostedInquiryInitialFormData(editingPostedInquiry)
      : editingDraftQuote?.form_data || (selectedQuoteType ? currentFormDataInDialog : null);
    if (!initialData || Object.keys(initialData).length === 0) {
      initialData = cloneSourceQuote?.form_data || null;
    }
    if (cloneSourceQuote && !initialData) {
      const op = cloneSourceQuote.origin_port;
      const dp = cloneSourceQuote.destination_port;
      initialData = {
        commodity: cloneSourceQuote.commodity,
        pol: op?.code || op?.name,
        polCountry: op?.country,
        pod: dp?.code || dp?.name,
        podCountry: dp?.country,
        origin: op?.code || op?.name,
        originCountry: op?.country,
        destination: dp?.code || dp?.name,
        destinationCountry: dp?.country,
        incoterm: cloneSourceQuote.incoterm,
        specialRequirements: cloneSourceQuote.cargo_description,
        isHaz: cloneSourceQuote.is_hazardous,
        isHazardous: cloneSourceQuote.is_hazardous,
        hazClass: cloneSourceQuote.hazardous_class
      };
    }
    const formProps = {
      onSubmit: handleQuoteFormSubmit,
      onCancel: () => setSelectedQuoteType(null),
      oceanPorts,
      porFpodPorts,
      airports,
      incoterms,
      commodities,
      initialData: initialData && Object.keys(initialData).length > 0 ? initialData : null,
      formId: 'quote-request-form',
      scopeOptions: SCOPE_OPTIONS_BY_MODE[selectedQuoteType],
      validationErrors: quoteFormErrors,
      clearValidationError: clearQuoteFormError,
      onFormDataChange: (data) => {
        const nextFormData = data || { scope: [] };
        setCurrentFormDataInDialog(nextFormData);
        // Warehousing reports a scope-only compatibility event before its full
        // form snapshot. Wait for the latter so the baseline never mistakes
        // modern defaults for user changes.
        const isScopeOnlyUpdate = Object.keys(nextFormData).length === 1
          && Object.prototype.hasOwnProperty.call(nextFormData, 'scope');
        if (
          editingPostedInquiry
          && !postedInquiryEditBaselineRef.current
          && !isScopeOnlyUpdate
        ) {
          postedInquiryEditBaselineRef.current = {
            formData: cloneFormValue(nextFormData),
            payload: buildQuotePayload(nextFormData, 'Active'),
          };
        }
      }
    };
    const formInstanceKey = editingPostedInquiry
      ? `edit-inquiry-${editingPostedInquiry.quote_id}`
      : editingDraftQuote
        ? `edit-draft-${editingDraftQuote.quote_id}`
        : cloneSourceQuote
          ? `clone-${cloneSourceQuote.quote_id}`
          : 'new';

    switch (selectedQuoteType) {
      case 'ocean-fcl':
        return <OceanFreightForm key={formInstanceKey} {...formProps} />;
      case 'ocean-lcl':
        return <LCLForm key={formInstanceKey} {...formProps} />;
      case 'air':
        return <AirFreightForm key={formInstanceKey} {...formProps} />;
      case 'transport':
        return <TransportForm key={formInstanceKey} {...formProps} />;
      case 'warehousing':
        return <WarehousingForm key={formInstanceKey} {...formProps} />;
      case 'breakbulk':
        return <BreakbulkForm key={formInstanceKey} {...formProps} />;
      default:
        return null;
    }
  };

  if (hasRestriction('quotes')) {
    return <RestrictedOverlay message={getRestrictionMessage('quotes')} />;
  }

  if (!currentMemberId) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-gray-600 mb-2">Please sign in to access the Quote Tool.</p>
        <Button onClick={() => navigate('/login')}>Sign In</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading quotes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quote-tool">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div
          className="sticky top-0 z-30 -mx-4 mb-6 border-b border-slate-200 bg-white shadow-sm"
        >
          <div className="space-y-6 px-4 pb-5 pt-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Quote Tool</h1>
                <p className="text-gray-600 mt-1">
                  Request and manage freight quotes with your network partners
                  {pointsSystemEnabled && memberProfile && (
                    <span className="ml-2 text-blue-600 font-medium">
                      ({memberProfile.reward_points?.toLocaleString() || 0} points available)
                    </span>
                  )}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                  onClick={() => {
                    setCloneSourceQuote(null);
                    resetDraftEditorState();
                    setShowNewQuoteDialog(true);
                  }}
                  disabled={!canCreateEnquiry()}
                  data-testid="new-quote-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Create New Inquiry
                </Button>
                {!canCreateEnquiry() && (
                  <p className="text-sm text-amber-700 bg-amber-50 px-3 py-1.5 rounded max-w-xs text-right">
                    Quote request limit reached. <Link to="/upgrade" className="font-medium underline">Upgrade your plan</Link> to create more.
                  </p>
                )}
              </div>
            </div>

            {/* Search and Filters */}
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search by reference, commodity, port..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="quote-search-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter size={16} className="mr-1" />
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </Button>
                  {(searchQuery || filterQuoteType || filterStatus || filterPOL || filterPOD || filterDateFrom || filterDateTo || filterReference) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                    >
                      <X size={16} className="mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
              </div>

              {/* Enhanced Filters Panel */}
              {showFilters && (
                <Card className="p-4 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Quote Type</Label>
                      <select
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                        value={filterQuoteType}
                        onChange={(e) => setFilterQuoteType(e.target.value)}
                        data-testid="filter-quote-type"
                      >
                        <option value="">All Types</option>
                        <option value="Ocean FCL">Ocean FCL</option>
                        <option value="Ocean LCL">Ocean LCL</option>
                        <option value="Air">Air Freight</option>
                        <option value="Transport">Transport</option>
                        <option value="Warehousing">Warehousing</option>
                        <option value="Breakbulk">Breakbulk</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Status</Label>
                      <select
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        data-testid="filter-status"
                      >
                        <option value="">All Status</option>
                        <option value="Draft">Draft</option>
                        <option value="Active">Active</option>
                        <option value="Pending">Pending</option>
                        <option value="Nominated">Nominated</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                        <option value="Expired">Expired</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Reference</Label>
                      <Input
                        className="mt-1 text-sm"
                        placeholder="OGA-2026-..."
                        value={filterReference}
                        onChange={(e) => setFilterReference(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">POL</Label>
                      <Input
                        className="mt-1 text-sm"
                        placeholder="Origin port..."
                        value={filterPOL}
                        onChange={(e) => setFilterPOL(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">POD</Label>
                      <Input
                        className="mt-1 text-sm"
                        placeholder="Dest port..."
                        value={filterPOD}
                        onChange={(e) => setFilterPOD(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Date From</Label>
                      <Input
                        type="date"
                        className="mt-1 text-sm"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Date To</Label>
                      <Input
                        type="date"
                        className="mt-1 text-sm"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowSaveFilterDialog(true)}
                      >
                        <Star size={14} className="mr-1" />
                        Save Filter
                      </Button>
                    </div>
                  </div>

                  {/* Saved Filters */}
                  {savedFilters.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <Label className="text-xs text-gray-500 mb-2 block">Saved Filters</Label>
                      <div className="flex flex-wrap gap-2">
                        {savedFilters.map((filter) => (
                          <div key={filter.id} className="flex items-center gap-1 bg-white border rounded-full px-3 py-1">
                            <button
                              className="text-sm text-blue-600 hover:text-blue-800"
                              onClick={() => applySavedFilter(filter)}
                            >
                              {filter.name}
                            </button>
                            <button
                              className="text-gray-400 hover:text-red-500 ml-1"
                              onClick={() => deleteSavedFilter(filter.id)}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              )}
            </div>

            {/* Tabs - quote states */}
            <TabsList className="mt-4 !grid !h-auto w-full max-w-6xl grid-cols-2 gap-1 bg-slate-100/90 sm:!h-9 sm:grid-cols-8 sm:gap-0">
              <TabsTrigger value="drafts" data-testid="tab-drafts" className="!flex !min-h-10 min-w-0 !justify-start gap-1.5 !whitespace-normal !px-2 !py-2 text-left text-xs sm:!min-h-0 sm:!justify-center sm:gap-1 sm:!whitespace-nowrap sm:!px-3 sm:!py-1 md:text-sm">
                <FileText size={14} className="shrink-0" />
                <span>Draft</span>
                <span className="shrink-0">({tabCounts.drafts})</span>
              </TabsTrigger>
              <TabsTrigger value="sent" data-testid="tab-sent" className="!flex !min-h-10 min-w-0 !justify-start gap-1.5 !whitespace-normal !px-2 !py-2 text-left text-xs sm:!min-h-0 sm:!justify-center sm:gap-1 sm:!whitespace-nowrap sm:!px-3 sm:!py-1 md:text-sm">
                <SendHorizontal size={14} className="shrink-0" />
                <span>Sent</span>
                <span className="shrink-0">({tabCounts.sent})</span>
              </TabsTrigger>
              <TabsTrigger value="received" data-testid="tab-received" className="!flex !min-h-10 min-w-0 !justify-start gap-1.5 !whitespace-normal !px-2 !py-2 text-left text-xs sm:!min-h-0 sm:!justify-center sm:gap-1 sm:!whitespace-nowrap sm:!px-3 sm:!py-1 md:text-sm">
                <Inbox size={14} className="shrink-0" />
                <span>Received</span>
                <span className="shrink-0">({tabCounts.received})</span>
              </TabsTrigger>
              <TabsTrigger value="quoted" data-testid="tab-quoted" className="!flex !min-h-10 min-w-0 !justify-start gap-1.5 !whitespace-normal !px-2 !py-2 text-left text-xs sm:!min-h-0 sm:!justify-center sm:gap-1 sm:!whitespace-nowrap sm:!px-3 sm:!py-1 md:text-sm">
                <FileText size={14} className="shrink-0" />
                <span>Sent Quotations</span>
                <span className="shrink-0">({tabCounts.quoted})</span>
              </TabsTrigger>
              <TabsTrigger value="nominated" data-testid="tab-nominated" className="!flex !min-h-10 min-w-0 !justify-start gap-1.5 !whitespace-normal !px-2 !py-2 text-left text-xs sm:!min-h-0 sm:!justify-center sm:gap-1 sm:!whitespace-nowrap sm:!px-3 sm:!py-1 md:text-sm">
                <CheckCircle size={14} className="shrink-0" />
                <span>Nominated</span>
                <span className="shrink-0">({tabCounts.nominated})</span>
              </TabsTrigger>
              <TabsTrigger value="closed" data-testid="tab-closed" className="!flex !min-h-10 min-w-0 !justify-start gap-1.5 !whitespace-normal !px-2 !py-2 text-left text-xs sm:!min-h-0 sm:!justify-center sm:gap-1 sm:!whitespace-nowrap sm:!px-3 sm:!py-1 md:text-sm">
                <X size={14} className="shrink-0" />
                <span>Closed</span>
                <span className="shrink-0">({tabCounts.closed})</span>
              </TabsTrigger>
              <TabsTrigger value="assignedToMe" data-testid="tab-assigned-to-me" className="!flex !min-h-10 min-w-0 !justify-start gap-1.5 !whitespace-normal !px-2 !py-2 text-left text-xs sm:!min-h-0 sm:!justify-center sm:gap-1 sm:!whitespace-nowrap sm:!px-3 sm:!py-1 md:text-sm [&_svg]:text-gray-700 [&_svg]:opacity-100">
                <User size={14} className="shrink-0" strokeWidth={2} />
                <span>Assign To Me</span>
                <span className="shrink-0">({tabCounts.assignedToMe})</span>
              </TabsTrigger>
              <TabsTrigger value="archived" data-testid="tab-archived" className="!flex !min-h-10 min-w-0 !justify-start gap-1.5 !whitespace-normal !px-2 !py-2 text-left text-xs sm:!min-h-0 sm:!justify-center sm:gap-1 sm:!whitespace-nowrap sm:!px-3 sm:!py-1 md:text-sm">
                <Archive size={14} className="shrink-0" />
                <span>Archive</span>
                <span className="shrink-0">({tabCounts.archived})</span>
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value={activeTab} className="mt-0">
          {activeTab === 'drafts' && (
            draftQuotes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No draft quotes yet</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Save a quote as draft to continue it later.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {paginatedQuotes.map((quote) => renderQuoteCard(quote, 'drafts'))}
                {renderPagination()}
              </div>
            )
          )}

          {/* SENT TAB - My Quote Requests */}
          {activeTab === 'sent' && (
            <>
              {memberProfile?.company_id && canAccessTeamEnquiries && (
                <div className="mb-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={sentSubFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setSentSubFilter('all')}
                    className={sentSubFilter === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  >
                    All Enquiries ({allSentQuotes.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={sentSubFilter === 'mine' ? 'default' : 'outline'}
                    onClick={() => setSentSubFilter('mine')}
                    className={sentSubFilter === 'mine' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  >
                    My Enquiries ({quotes.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={sentSubFilter === 'team' ? 'default' : 'outline'}
                    onClick={() => setSentSubFilter('team')}
                    className={sentSubFilter === 'team' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  >
                    Team Enquiries ({visibleTeamEnquiriesQuotes.length})
                  </Button>
                </div>
              )}

              {sentFiltered.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <SendHorizontal size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">
                    {sentSubFilter === 'team' ? 'No team enquiries yet' : sentSubFilter === 'all' ? 'No enquiries yet' : 'No sent quote requests yet'}
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    {sentSubFilter === 'team'
                      ? 'Enquiries created by teammates in your company will appear here'
                      : 'Click "Create New Inquiry" to create your first enquiry'}
                  </p>
                  {sentSubFilter !== 'team' && (
                    <Button
                      className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        setCloneSourceQuote(null);
                        resetDraftEditorState();
                        setShowNewQuoteDialog(true);
                      }}
                    >
                      <Plus size={16} className="mr-2" />
                      Create New Inquiry
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {paginatedQuotes.map((quote) => {
                  const isOwnedByCurrentMember = quote.requester_id === currentMemberId;
                  const shouldRenderAsTeamEnquiry =
                    !isOwnedByCurrentMember && (sentSubFilter === 'team' || sentSubFilter === 'all');

                  return renderQuoteCard(quote, shouldRenderAsTeamEnquiry ? 'teamEnquiries' : 'sent');
                })}
                {renderPagination()}
              </div>
            )}
            </>
          )}

          {/* RECEIVED TAB - Available Quotes to Bid On */}
          {activeTab === 'received' && (
            receivedQuotes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Inbox size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No quote requests available to bid on</p>
                  <p className="text-sm text-gray-400 mt-2">
                    When other members create quote requests, they will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {paginatedQuotes.map((quote) => (
                  pendingHiddenReceivedQuotes[quote.quote_id]
                    ? renderPendingHiddenReceivedRow(quote)
                    : renderQuoteCard(quote, 'received')
                ))}
                {renderPagination()}
              </div>
            )
          )}

          {/* QUOTED TAB - Quotes I've submitted bids on */}
          {activeTab === 'quoted' && (
            quotedQuotes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No quotations submitted yet</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Go to the Received tab to submit quotations on available requests
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {paginatedQuotes.map((quote) => renderQuoteCard(quote, 'quoted'))}
                {renderPagination()}
              </div>
            )
          )}

          {/* NOMINATED TAB - Quotes where my bid was selected or I nominated someone */}
          {activeTab === 'nominated' && (
            <>
              {nominatedQuotes.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    variant={nominatedSubFilter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNominatedSubFilter('all')}
                    className={nominatedSubFilter === 'all' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  >
                    All ({nominatedQuotes.length})
                  </Button>
                  <Button
                    variant={nominatedSubFilter === 'by_me' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNominatedSubFilter('by_me')}
                    className={nominatedSubFilter === 'by_me' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  >
                    Nominated by me ({nominatedByMe.length})
                  </Button>
                  <Button
                    variant={nominatedSubFilter === 'for_me' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNominatedSubFilter('for_me')}
                    className={nominatedSubFilter === 'for_me' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  >
                    Nominated to me ({nominatedForMe.length})
                  </Button>
                </div>
              )}
              {nominatedQuotes.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <CheckCircle size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No nominated quotes yet</p>
                    <p className="text-sm text-gray-400 mt-2">
                      When your bid is selected as the winner, it will appear here
                    </p>
                  </CardContent>
                </Card>
              ) : displayQuotes.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-500">No quotes match the selected filter</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {paginatedQuotes.map((quote) => renderQuoteCard(quote, 'nominated'))}
                  {renderPagination()}
                </div>
              )}
            </>
          )}

          {/* CLOSED TAB - Completed/Cancelled quotes */}
          {activeTab === 'closed' && (
            closedQuotes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <X size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No closed quotes</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Completed or cancelled quotes will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {paginatedQuotes.map((quote) => renderQuoteCard(quote, 'closed'))}
                {renderPagination()}
              </div>
            )
          )}

          {/* ASSIGN TO ME TAB - Quotes assigned to me by company admin */}
          {activeTab === 'assignedToMe' && (
            assignedToMeQuotes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <User size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No quotes assigned to you</p>
                  <p className="text-sm text-gray-400 mt-2">
                    When your company admin assigns you a quote, it will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {paginatedQuotes.map((quote) => renderQuoteCard(quote, 'assignedToMe'))}
                {renderPagination()}
              </div>
            )
          )}

          {/* ARCHIVE TAB - Not-interested quotes kept for 10 days */}
          {activeTab === 'archived' && (
            archivedQuotes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Archive size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No archived quotes</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Quotes you mark as "Not Interested" will appear here for 10 days before being removed.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {paginatedQuotes.map((quote) => {
                  const daysLeft = Math.max(0, Math.ceil((ARCHIVE_EXPIRY_DAYS * 24 * 60 * 60 * 1000 - (Date.now() - (quote.hidden_at || Date.now()))) / (24 * 60 * 60 * 1000)));
                  return (
                    <Card key={quote.quote_id} className="border-gray-200 bg-gray-50/50">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900">{quote.reference_number || quote.quote_id}</span>
                              <Badge variant="outline" className="text-xs">{quote.quote_type}</Badge>
                              <Badge className="bg-gray-100 text-gray-600 text-xs">
                                <Clock size={10} className="mr-1" />
                                {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500 mt-1 truncate">
                              {quote.commodity || ''}{quote.commodity && (quote.origin_port?.name || quote.destination_port?.name) ? ' · ' : ''}
                              {quote.origin_port?.name || ''}{quote.origin_port?.name && quote.destination_port?.name ? ' → ' : ''}{quote.destination_port?.name || ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => restoreArchivedQuote(quote.quote_id)}
                            >
                              <RotateCcw size={14} className="mr-1" />
                              Restore
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openQuoteDetails(quote)}
                            >
                              <Eye size={14} className="mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {renderPagination()}
              </div>
            )
          )}
        </TabsContent>
      </Tabs>

      {/* Save Filter Dialog */}
      <Dialog open={showSaveFilterDialog} onOpenChange={setShowSaveFilterDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save Filter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Filter Name</Label>
              <Input
                value={newFilterName}
                onChange={(e) => setNewFilterName(e.target.value)}
                placeholder="e.g., Ocean FCL from Shanghai"
              />
            </div>
            <div className="text-sm text-gray-500">
              <p>Current filters:</p>
              <ul className="mt-1 space-y-1">
                {filterQuoteType && <li>Type: {filterQuoteType}</li>}
                {filterStatus && <li>Status: {filterStatus}</li>}
                {filterPOL && <li>POL: {filterPOL}</li>}
                {filterPOD && <li>POD: {filterPOD}</li>}
                {filterDateFrom && <li>From: {filterDateFrom}</li>}
                {filterDateTo && <li>To: {filterDateTo}</li>}
                {filterReference && <li>Ref: {filterReference}</li>}
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSaveFilterDialog(false)}>Cancel</Button>
              <Button onClick={saveCurrentFilter}>Save Filter</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showHideReceivedConfirmDialog} onOpenChange={(open) => {
        setShowHideReceivedConfirmDialog(open);
        if (!open) {
          setHideReceivedConfirmQuote(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Not Interested</DialogTitle>
            <DialogDescription>
              Are you sure you are not interested in this quote?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowHideReceivedConfirmDialog(false);
                setHideReceivedConfirmQuote(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmHideReceivedQuote}
            >
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Quote Dialog */}
      <Dialog open={showNewQuoteDialog} onOpenChange={(open) => {
        setShowNewQuoteDialog(open);
        if (open) {
          if (!selectedQuoteType) {
            resetDraftEditorState();
          }
        } else {
          setSelectedQuoteType(null);
          setCloneSourceQuote(null);
          resetDraftEditorState();
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedQuoteType
                ? (editingPostedInquiry
                  ? `Edit Inquiry - ${quoteTypes.find(t => t.id === selectedQuoteType)?.name}`
                  : editingDraftQuote
                  ? `Edit Draft - ${quoteTypes.find(t => t.id === selectedQuoteType)?.name}`
                  : cloneSourceQuote
                    ? `Clone Quote - ${quoteTypes.find(t => t.id === selectedQuoteType)?.name}`
                    : `New ${quoteTypes.find(t => t.id === selectedQuoteType)?.name} Quote`)
                : 'Select Quote Type'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Create or edit a freight quote: choose priority and service type, complete the form, then add charge lines and submit.
            </DialogDescription>
          </DialogHeader>

          {!selectedQuoteType ? (
            /* Quote Type Selection - Quote Priority + Select Service Type (like earlier) */
            <div className="space-y-6">
              {/* Quote Priority */}
              {pointsSystemEnabled && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <Label className="font-semibold">Quote Priority</Label>
                <p className="text-sm text-gray-500 mb-3">
                  Higher priority quotes get more visibility.
                  {pointsSystemEnabled ? ' Use your points to boost visibility.' : ''}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'High', label: 'High', costPerDay: pointsSystemEnabled ? '250 pts' : '', desc: 'Maximum visibility' },
                    { value: 'Medium', label: 'Medium', costPerDay: pointsSystemEnabled ? '100 pts' : '', desc: 'Enhanced visibility' },
                    { value: 'Normal', label: 'Normal', cost: 'Free', desc: 'Standard visibility' }
                  ].map((p) => (
                    <label
                      key={p.value}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${quotePriority === p.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                        }`}
                    >
                      <input
                        type="radio"
                        name="priority"
                        value={p.value}
                        checked={quotePriority === p.value}
                        onChange={(e) => {
                          setQuotePriority(e.target.value);
                          if (e.target.value === 'Normal') setPriorityUntilDate('');
                        }}
                        className="sr-only"
                      />
                      <div className="text-sm font-medium">{p.label}</div>
                      {(p.costPerDay || p.cost) && (
                        <div className="text-xs text-blue-600 font-medium">{p.costPerDay || p.cost}</div>
                      )}
                      <div className="text-xs text-gray-400">{p.desc}</div>
                    </label>
                  ))}
                </div>
                {pointsSystemEnabled && memberProfile && (
                  <p className="text-xs text-gray-500 mt-2">
                    Your tier discount: {memberProfile.tier === 'Founder' ? '50%' : memberProfile.tier === 'Titanium' ? '40%' : memberProfile.tier === 'Rhodium' ? '30%' : memberProfile.tier === 'Platinum' ? '20%' : memberProfile.tier === 'Gold' ? '10%' : '0%'} off
                  </p>
                )}
                {(quotePriority === 'High' || quotePriority === 'Medium') && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <Label className="text-sm">Priority until (date)</Label>
                    <p className="text-xs text-gray-500 mb-1">
                      Select until when you want this priority.
                      {pointsSystemEnabled ? ' Points will be deducted based on the number of days.' : ''}
                    </p>
                    <Input
                      type="date"
                      value={sanitizeHtmlDateValue(priorityUntilDate)}
                      min={getTodayDateValue()}
                      onChange={(e) => setPriorityUntilDate(getSafeFutureDateValue(e.target.value))}
                      className="mt-1 max-w-[200px]"
                    />
                    {pointsSystemEnabled && sanitizeHtmlDateValue(priorityUntilDate) && memberProfile && (() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const until = new Date(sanitizeHtmlDateValue(priorityUntilDate));
                      until.setHours(0, 0, 0, 0);
                      const diffDays = Math.floor((until - today) / (24 * 60 * 60 * 1000));
                      const days = Math.max(1, diffDays + 1);
                      const baseCost = quotePriority === 'High' ? (pointsConfig?.costs?.priority_high ?? 250) : (pointsConfig?.costs?.priority_medium ?? 100);
                      const discount = memberProfile?.tier === 'Founder' ? 50 : memberProfile?.tier === 'Titanium' ? 40 : memberProfile?.tier === 'Rhodium' ? 30 : memberProfile?.tier === 'Platinum' ? 20 : memberProfile?.tier === 'Gold' ? 10 : 0;
                      const pts = Math.floor(days * baseCost * (100 - discount) / 100);
                      const ok = pts <= (memberProfile?.reward_points ?? 0);
                      return (
                        <p className={`text-xs mt-1 ${ok ? 'text-green-600' : 'text-red-600'}`}>
                          {pts} pts {ok ? 'will be deducted' : `needed (you have ${memberProfile?.reward_points ?? 0})`}
                        </p>
                      );
                    })()}
                  </div>
                )}
              </div>
              )}

              {/* Select Service Type */}
              <div>
                <Label className="font-semibold mb-3 block">Select Service Type</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {quoteTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <Card
                        key={type.id}
                        className="cursor-pointer hover:shadow-lg hover:border-blue-500 transition-all"
                        onClick={() => handleQuoteTypeSelect(type.id)}
                        data-testid={`quote-type-${type.id}`}
                      >
                        <CardContent className="p-4 text-center">
                          <Icon size={32} className="mx-auto text-blue-600 mb-2" />
                          <h3 className="font-semibold text-gray-900">{type.name}</h3>
                          <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            /* Quote Form with Line Items */
            <div className="space-y-6">
              {/* Back Button */}
              {!editingPostedInquiry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedQuoteType(null)}
                >
                  ← Back to Quote Types
                </Button>
              )}

              {/* Priority - editable in form view (for clone or change) */}
              {pointsSystemEnabled && !editingPostedInquiry && (
              <div className="p-3 bg-gray-50 rounded-lg border">
                <Label className="font-semibold text-sm">Quote Priority</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['High', 'Medium', 'Normal'].map((p) => (
                    <label
                      key={p}
                      className={`px-3 py-1.5 border rounded-md cursor-pointer text-sm transition-all ${quotePriority === p ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      <input
                        type="radio"
                        name="formPriority"
                        value={p}
                        checked={quotePriority === p}
                        onChange={(e) => {
                          setQuotePriority(e.target.value);
                          if (e.target.value === 'Normal') setPriorityUntilDate('');
                        }}
                        className="sr-only"
                      />
                      {p}
                    </label>
                  ))}
                </div>
                {(quotePriority === 'High' || quotePriority === 'Medium') && (
                  <div className="mt-2">
                    <Label className="text-xs">Priority until</Label>
                    <Input
                      type="date"
                      value={sanitizeHtmlDateValue(priorityUntilDate)}
                      min={getTodayDateValue()}
                      onChange={(e) => setPriorityUntilDate(getSafeFutureDateValue(e.target.value))}
                      className="mt-1 max-w-[200px]"
                    />
                  </div>
                )}
                {pointsSystemEnabled && quotePriority !== 'Normal' && sanitizeHtmlDateValue(priorityUntilDate) && memberProfile && (() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const until = new Date(sanitizeHtmlDateValue(priorityUntilDate));
                  until.setHours(0, 0, 0, 0);
                  const diffDays = Math.floor((until - today) / (24 * 60 * 60 * 1000));
                  const days = Math.max(1, diffDays + 1);
                  const baseCost = quotePriority === 'High' ? (pointsConfig?.costs?.priority_high ?? 250) : (pointsConfig?.costs?.priority_medium ?? 100);
                  const discount = memberProfile?.tier === 'Founder' ? 50 : memberProfile?.tier === 'Titanium' ? 40 : memberProfile?.tier === 'Rhodium' ? 30 : memberProfile?.tier === 'Platinum' ? 20 : memberProfile?.tier === 'Gold' ? 10 : 0;
                  const pts = Math.floor(days * baseCost * (100 - discount) / 100);
                  const ok = pts <= (memberProfile?.reward_points ?? 0);
                  return (
                    <p className={`text-xs mt-1 ${ok ? 'text-green-600' : 'text-red-600'}`}>
                      {pts} pts {ok ? 'will be deducted' : `needed (you have ${memberProfile?.reward_points ?? 0})`}
                    </p>
                  );
                })()}
              </div>
              )}

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <Label htmlFor="quote-response-deadline" className="font-semibold text-sm">
                  Last date and time to submit quotations
                  {(!editingPostedInquiry || responseDeadlineChanged(
                    editingPostedInquiry.response_deadline,
                    responseDeadline
                  )) && <span className="text-red-500"> *</span>}
                </Label>
                <p className="mt-1 text-xs text-amber-800">
                  Members cannot submit or edit quotations after this deadline.
                </p>
                <Input
                  id="quote-response-deadline"
                  type="datetime-local"
                  value={responseDeadline}
                  min={getMinimumResponseDeadline()}
                  onChange={(event) => setResponseDeadline(event.target.value)}
                  className="mt-2 max-w-xs bg-white"
                  required={
                    !editingPostedInquiry
                    || responseDeadlineChanged(
                      editingPostedInquiry.response_deadline,
                      responseDeadline
                    )
                  }
                />
              </div>

              <div className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="quote-hs-code" className="font-semibold text-sm">HS code (optional)</Label>
                  <p className="text-xs text-slate-600">Enter numbers only, up to 15 digits.</p>
                  <Input
                    id="quote-hs-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={15}
                    value={hsCode}
                    onChange={(event) => setHsCode(event.target.value.replace(/\D/g, '').slice(0, 15))}
                    placeholder="e.g. 850440"
                    className="max-w-xs bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold text-sm">Supporting PDFs (optional)</Label>
                  <p className="text-xs text-slate-600">Attach up to 5 PDF documents, 10 MB each.</p>
                  <input
                    ref={quoteAttachmentInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    className="hidden"
                    onChange={handleQuoteAttachmentUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={quoteAttachmentsUploading || quoteAttachments.length >= 5}
                    onClick={() => quoteAttachmentInputRef.current?.click()}
                  >
                    {quoteAttachmentsUploading ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Upload size={15} className="mr-2" />}
                    {quoteAttachmentsUploading ? 'Uploading...' : 'Attach PDFs'}
                  </Button>
                </div>
                {quoteAttachments.length > 0 && (
                  <div className="space-y-2 md:col-span-2">
                    {quoteAttachments.map((file, index) => (
                      <div key={`${file.url}-${index}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2 text-sm text-slate-700">
                          <FileText size={16} className="shrink-0 text-red-600" />
                          <span className="truncate">{file.original_filename || file.filename}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 shrink-0 p-0 text-red-600"
                          title="Remove attachment"
                          onClick={() => setQuoteAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Original Quote Form Fields - MOVED TO TOP */}
              <div className="border rounded-lg p-4 bg-white">
                {renderQuoteForm()}
              </div>

              {/* Special Instructions Section - MOVED TO BOTTOM */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center">
                    <FileText size={16} className="mr-2" />
                    Special Instructions
                  </h4>
                </div>
                <p className="text-xs text-amber-700 mb-3">
                  If an instruction is marked mandatory, the forwarder must accept it before submitting the quotation.
                </p>

                <div className="grid grid-cols-6 gap-2 mb-3">
                  <div>
                    <Label className="text-xs">#</Label>
                    <Input
                      className="text-sm h-8 bg-gray-100"
                      value={`1.${specialInstructions.length + 1}`}
                      readOnly
                      tabIndex={-1}
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Instruction *</Label>
                    <Input
                      className="text-sm h-8"
                      value={instructionForm.instruction_text}
                      onChange={(e) => setInstructionForm({ ...instructionForm, instruction_text: e.target.value })}
                      placeholder="Enter special instruction..."
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={instructionForm.is_mandatory}
                        onChange={(e) => setInstructionForm({ ...instructionForm, is_mandatory: e.target.checked })}
                        className="mr-2"
                      />
                      Mandatory
                    </label>
                  </div>
                  <div className="flex items-end">
                    <Button size="sm" onClick={addSpecialInstruction} className="w-full">
                      <Plus size={14} />
                    </Button>
                  </div>
                </div>

                {specialInstructions.length > 0 && (
                  <div className="space-y-1">
                    {specialInstructions.map((instruction, idx) => (
                      <div key={instruction.id} className="flex items-center justify-between p-2 bg-white rounded text-sm border">
                        <span>
                          <strong>1.{idx + 1}:</strong> {instruction.instruction_text}
                          {instruction.is_mandatory && <Badge className="ml-2 bg-red-100 text-red-700 text-xs">Mandatory</Badge>}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 h-6 w-6 p-0"
                          onClick={() => removeInstruction(instruction.id)}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {editingPostedInquiry ? (
                <div className="border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                  Saving this edit records your name and timestamp, then notifies the company admin, existing bidders, and the original lane audience. Public-link settings are managed separately from the Sent tab.
                </div>
              ) : (
                <div className="border border-blue-200 bg-blue-50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label htmlFor="new-inquiry-public-sharing" className="font-semibold text-slate-900">
                        Anyone with the link can view
                      </Label>
                      <p className="mt-1 text-xs text-slate-600">
                        A read-only public inquiry link will be created. You can disable it later from the Sent tab.
                      </p>
                    </div>
                    <Switch
                      id="new-inquiry-public-sharing"
                      checked={publicSharingEnabled}
                      onCheckedChange={setPublicSharingEnabled}
                      aria-label="Anyone with the link can view"
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs text-blue-800">
                    <Globe size={14} />
                    {publicSharingEnabled
                      ? 'Public sharing will be enabled when this inquiry is created.'
                      : 'Only signed-in members will be able to view this inquiry.'}
                  </div>
                </div>
              )}

              {/* Submit Buttons - At Very Bottom */}
              <div className="flex flex-col gap-3 pt-4 border-t mt-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedQuoteType(null);
                      setShowNewQuoteDialog(false);
                    }}
                    disabled={draftSaveLoading || quoteSubmitLoading}
                  >
                    Close
                  </Button>
                </div>
                <div className="flex justify-end space-x-3">
                  {!editingPostedInquiry && (
                    <Button
                      variant="outline"
                      onClick={handleSaveDraft}
                      disabled={draftSaveLoading || quoteSubmitLoading}
                    >
                      {draftSaveLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Draft'
                      )}
                    </Button>
                  )}
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={quoteSubmitLoading || draftSaveLoading}
                    onClick={() => {
                      const form = document.getElementById('quote-request-form');
                      if (form) {
                        form.requestSubmit();
                      } else {
                        toast.error('Form not found. Please try again.');
                      }
                    }}
                    data-testid="submit-quote-btn"
                  >
                    {quoteSubmitLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      editingPostedInquiry ? 'Save Inquiry Updates' : 'Create Inquiry'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Inquiry Created Confirmation */}
      <Dialog
        open={showInquiryCreatedDialog}
        onOpenChange={(open) => {
          setShowInquiryCreatedDialog(open);
          if (!open) setCreatedInquiry(null);
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center text-slate-900">
              <CheckCircle size={22} className="mr-2 text-emerald-600" />
              Inquiry Created
            </DialogTitle>
            <DialogDescription>
              Your freight inquiry is now available to eligible members.
            </DialogDescription>
          </DialogHeader>

          {createdInquiry && (
            <div className="space-y-4">
              <div className="grid gap-3 border-y border-slate-200 py-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase text-slate-500">Reference</p>
                  <p className="mt-1 font-mono text-sm font-semibold">
                    {createdInquiry.reference_number || createdInquiry.quote_id}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500">Service</p>
                  <p className="mt-1 text-sm font-semibold">{createdInquiry.quote_type}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs uppercase text-slate-500">Deadline to quote</p>
                  <p className="mt-1 text-sm font-semibold">
                    {createdInquiry.response_deadline
                      ? new Date(createdInquiry.response_deadline).toLocaleString([], {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : 'Not set'}
                  </p>
                </div>
              </div>

              <div className="border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="created-inquiry-public-sharing" className="font-semibold">
                      Enable public sharing
                    </Label>
                    <p className="mt-1 text-xs text-slate-600">
                      Anyone with this link can view the read-only inquiry.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {sharingUpdateQuoteId === createdInquiry.quote_id && (
                      <Loader2 size={15} className="animate-spin text-blue-600" />
                    )}
                    <Switch
                      id="created-inquiry-public-sharing"
                      checked={createdInquiry.public_sharing_enabled === true}
                      onCheckedChange={(checked) =>
                        handlePublicSharingChange(createdInquiry, checked)
                      }
                      disabled={sharingUpdateQuoteId === createdInquiry.quote_id}
                    />
                  </div>
                </div>

                {createdInquiry.public_sharing_enabled &&
                  createdInquiry.public_share_token && (
                    <div className="mt-4">
                      <Label htmlFor="created-inquiry-public-link" className="text-xs">
                        Public link
                      </Label>
                      <div className="mt-1 flex gap-2">
                        <Input
                          id="created-inquiry-public-link"
                          readOnly
                          value={getPublicInquiryUrl(createdInquiry.public_share_token)}
                          className="bg-white text-sm"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="shrink-0 bg-white"
                          onClick={() =>
                            copyPublicInquiryLink(createdInquiry.public_share_token)
                          }
                        >
                          <Copy size={15} className="mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                  )}
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowInquiryCreatedDialog(false);
                    setCreatedInquiry(null);
                  }}
                >
                  Close
                </Button>
                {createdInquiry.public_sharing_enabled &&
                  createdInquiry.public_share_token && (
                    <Button
                      className="bg-blue-600 text-white hover:bg-blue-700"
                      onClick={() =>
                        window.open(
                          getPublicInquiryUrl(createdInquiry.public_share_token),
                          '_blank',
                          'noopener,noreferrer'
                        )
                      }
                    >
                      <Globe size={15} className="mr-2" />
                      View Public Inquiry
                    </Button>
                  )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Nomination Dialog */}
      <Dialog open={showNominationDialog} onOpenChange={setShowNominationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nominate Bid</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Reason and rating are mandatory for nomination. This will contribute to member ratings.
            </p>
            <div>
              <Label>Reason for Selection *</Label>
              <Textarea
                value={nominationForm.reason}
                onChange={(e) => setNominationForm({ ...nominationForm, reason: e.target.value })}
                placeholder="Explain why you chose this bid (minimum 10 characters)..."
                rows={3}
              />
            </div>
            <div>
              <Label>Rating *</Label>
              <div className="flex items-center space-x-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setNominationForm({ ...nominationForm, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      size={28}
                      className={star <= nominationForm.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                    />
                  </button>
                ))}
                <span className="text-sm text-gray-600 ml-2">{nominationForm.rating}/5</span>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowNominationDialog(false)}>Cancel</Button>
              <Button onClick={handleNominate} className="bg-blue-600 hover:bg-blue-700 text-white">
                Confirm Nomination
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Completion Dialog */}
      <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Quote & Rate Partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Rate your experience. Both parties need to rate for completion.
            </p>
            <div>
              <Label>Transaction Rating *</Label>
              <div className="flex items-center space-x-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setCompletionForm({ ...completionForm, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      size={28}
                      className={star <= completionForm.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                    />
                  </button>
                ))}
                <span className="text-sm text-gray-600 ml-2">{completionForm.rating}/5</span>
              </div>
            </div>
            <div>
              <Label>Feedback</Label>
              <Textarea
                value={completionForm.feedback}
                onChange={(e) => setCompletionForm({ ...completionForm, feedback: e.target.value })}
                placeholder="Share your experience..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCompletionDialog(false)} disabled={completeSubmitLoading}>Cancel</Button>
              <Button onClick={handleComplete} disabled={completeSubmitLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {completeSubmitLoading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Rating'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Payment Details Dialog (receiver) - scrollable to prevent clipping */}
      <Dialog
        open={showPaymentDetailsDialog}
        onOpenChange={(open) => {
          setShowPaymentDetailsDialog(open);
          if (!open) {
            setPaymentSavedBankDetails([]);
            setSelectedPaymentBankDetailId('');
            setPaymentDetailsFile(null);
            setPaymentDetailsFileName('');
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b bg-white">
            <DialogTitle>Request Payment Details</DialogTitle>
            <p className="text-sm text-gray-600 mt-1">Enter your bank details so the requester can pay you.</p>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-6 py-4 min-h-0">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Saved bank details</Label>
                <SearchableSelect
                  options={paymentBankDetailDropdownOptions}
                  value={selectedPaymentBankDetailId}
                  onChange={handlePaymentBankDetailSelect}
                  placeholder="Select or add bank details"
                  className="h-10 bg-white"
                />
                <p className="text-xs text-gray-500">
                  {isCompanyAdmin
                    ? 'Select saved company bank details or add/update details for reuse.'
                    : 'Select saved company bank details. Editing is restricted to company admin.'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Account Name *</Label>
                <Input
                  className="w-full border border-gray-200"
                  value={paymentDetailsForm.account_name}
                  onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, account_name: e.target.value })}
                  placeholder="e.g. One Globe Alliance LLC"
                  readOnly={isBankDetailsReadOnly}
                  disabled={isBankDetailsReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Bank Name *</Label>
                <Input
                  className="w-full border border-gray-200"
                  value={paymentDetailsForm.bank_name}
                  onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, bank_name: e.target.value })}
                  placeholder="e.g. HSBC Bank"
                  readOnly={isBankDetailsReadOnly}
                  disabled={isBankDetailsReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Account Number *</Label>
                <Input
                  className="w-full border border-gray-200"
                  value={paymentDetailsForm.account_number}
                  onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, account_number: e.target.value })}
                  placeholder="e.g. 1234567890123456"
                  readOnly={isBankDetailsReadOnly}
                  disabled={isBankDetailsReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">IBAN</Label>
                <Input
                  className="w-full border border-gray-200"
                  value={paymentDetailsForm.iban}
                  onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, iban: e.target.value })}
                  placeholder="e.g. AE07 0331 2345 6789 0123 456"
                  readOnly={isBankDetailsReadOnly}
                  disabled={isBankDetailsReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">SWIFT Code</Label>
                <Input
                  className="w-full border border-gray-200"
                  value={paymentDetailsForm.swift_code}
                  onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, swift_code: e.target.value })}
                  placeholder="e.g. HSBCAEADXXX"
                  readOnly={isBankDetailsReadOnly}
                  disabled={isBankDetailsReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Bank Address</Label>
                <Input
                  className="w-full border border-gray-200"
                  value={paymentDetailsForm.bank_address}
                  onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, bank_address: e.target.value })}
                  placeholder="e.g. Dubai, United Arab Emirates"
                  readOnly={isBankDetailsReadOnly}
                  disabled={isBankDetailsReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Deal Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full border border-gray-200 bg-gray-50 text-gray-700"
                  value={paymentDetailsForm.deal_amount}
                  readOnly
                  disabled
                  placeholder="Calculated from total quote amount"
                />
                <p className="text-xs text-gray-500">Auto-filled from the total quote amount.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Due Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full border border-gray-200"
                  value={paymentDetailsForm.due_amount}
                  onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, due_amount: e.target.value })}
                  placeholder="Enter due amount"
                />
                <p className="text-xs text-gray-500">Final requested amount will use this value.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Currency</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm"
                  value={paymentDetailsForm.currency || 'USD'}
                  onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, currency: e.target.value })}
                >
                  {paymentRequestCurrencies.map((currencyCode) => (
                    <option key={currencyCode} value={currencyCode}>
                      {currencyCode}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Payment by date *</Label>
                <div className="relative">
                    <Input
                      type="date"
                      className="w-full border border-gray-200 pr-10"
                      value={paymentDetailsForm.payment_due_date}
                      min={getTodayDateValue()}
                      onChange={(e) => setPaymentDetailsForm({ ...paymentDetailsForm, payment_due_date: getSafeFutureDateValue(e.target.value) })}
                    />
                  <Calendar size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Attachment (optional)</Label>
                <div
                  className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50/80 p-6 cursor-pointer hover:bg-gray-100/80 transition-colors"
                  onClick={() => paymentDetailsFileInputRef.current?.click()}
                >
                  <input
                    ref={paymentDetailsFileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
                        if (!allowed.includes(file.type)) {
                          toast.error('Please upload PDF, JPG, PNG, DOC, DOCX, XLS, or XLSX only.');
                          return;
                        }
                        setPaymentDetailsFile(file);
                        setPaymentDetailsFileName(file.name);
                      }
                    }}
                  />
                  <Upload size={24} className="text-gray-500" />
                  <span className="text-sm text-gray-500 text-center">
                    {paymentDetailsFileName || 'Click to upload (PDF, JPG, PNG, DOC, XLS)'}
                  </span>
                  {paymentDetailsFile && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPaymentDetailsFile(null); setPaymentDetailsFileName(''); paymentDetailsFileInputRef.current && (paymentDetailsFileInputRef.current.value = ''); }}
                      className="mt-1 text-gray-500 hover:text-red-600 p-1 text-xs font-medium"
                      aria-label="Remove file"
                    >
                      Remove file
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 px-6 py-4 border-t bg-white shrink-0">
            <Button variant="outline" onClick={() => { setShowPaymentDetailsDialog(false); setPaymentSavedBankDetails([]); setSelectedPaymentBankDetailId(''); setPaymentDetailsFile(null); setPaymentDetailsFileName(''); }}>Cancel</Button>
            <Button onClick={handleSubmitPaymentDetails} disabled={paymentDetailsSubmitLoading} className="bg-blue-600 hover:bg-blue-700 text-white">
              {paymentDetailsSubmitLoading ? <><Loader2 size={16} className="mr-2 animate-spin" />Submitting...</> : 'Submit Payment Details'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pay Now Dialog (requester - bank transfer) */}
      <Dialog open={showPayNowDialog} onOpenChange={setShowPayNowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Building2 size={20} className="mr-2" />
              Bank Transfer
            </DialogTitle>
          </DialogHeader>
          {selectedQuote && (selectedQuote.payment_details || selectedQuote.quote_payment_id) && (
            <>
              <div className="space-y-2">
                <Label className="font-semibold">Transfer To</Label>
                <div className="rounded-lg border bg-muted/40 p-4 space-y-1.5 text-sm">
                  {selectedQuote.payment_details ? (
                    <>
                      <p><span className="text-muted-foreground">Account Name:</span> {normalizeRecipientAccountName(selectedQuote.payment_details.account_name)}</p>
                      <p><span className="text-muted-foreground">Bank Name:</span> {selectedQuote.payment_details.bank_name}</p>
                      <p><span className="text-muted-foreground">Account Number:</span> {selectedQuote.payment_details.account_number}</p>
                      {selectedQuote.payment_details.iban && <p><span className="text-muted-foreground">IBAN:</span> {selectedQuote.payment_details.iban}</p>}
                      {selectedQuote.payment_details.swift_code && <p><span className="text-muted-foreground">SWIFT Code:</span> {selectedQuote.payment_details.swift_code}</p>}
                      {selectedQuote.payment_details.bank_address && <p><span className="text-muted-foreground">Bank Address:</span> {selectedQuote.payment_details.bank_address}</p>}
                      <p className="font-medium text-blue-700 mt-2">Amount: {selectedQuote.payment_details.currency || 'USD'} {(selectedQuote.payment_details.amount || 0).toLocaleString()}</p>
                      {selectedQuote.payment_details.attachment_url && (
                        <p className="mt-2">
                          <a
                            href={(selectedQuote.payment_details.attachment_url || '').startsWith('http') ? selectedQuote.payment_details.attachment_url : (process.env.REACT_APP_BACKEND_URL || '') + selectedQuote.payment_details.attachment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            <FileText size={14} />
                            Download attachment
                          </a>
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground">Loading...</p>
                  )}
                </div>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                Please complete the bank transfer and submit the details below. Your payment will be verified within 24 hours.
              </div>
              <div className="grid gap-4">
                <div>
                  <Label>Your Account Name</Label>
                  <Input
                    placeholder="Name on your bank account"
                    value={payNowForm.payer_account_name}
                    onChange={(e) => setPayNowForm({ ...payNowForm, payer_account_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Your Bank Name</Label>
                  <Input
                    placeholder="Name of your bank"
                    value={payNowForm.payer_bank_name}
                    onChange={(e) => setPayNowForm({ ...payNowForm, payer_bank_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Your Account Number (last 4 digits)</Label>
                  <Input
                    placeholder="****1234"
                    maxLength={4}
                    value={payNowForm.payer_account_last4}
                    onChange={(e) => setPayNowForm({ ...payNowForm, payer_account_last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  />
                </div>
                <div>
                  <Label>Transaction Reference Number</Label>
                  <Input
                    placeholder="e.g., TRF2025012345"
                    value={payNowForm.transaction_reference}
                    onChange={(e) => setPayNowForm({ ...payNowForm, transaction_reference: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Transfer Date</Label>
                  <Input
                    type="date"
                    value={payNowForm.transfer_date}
                    max={getTodayDateValue()}
                    onChange={(e) => setPayNowForm({ ...payNowForm, transfer_date: getSafePastOrTodayDateValue(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Upload Proof of Transfer</Label>
                  <div
                    className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/30 p-6 cursor-pointer hover:bg-muted/50"
                    onClick={() => payNowProofInputRef.current?.click()}
                  >
                    <input
                      ref={payNowProofInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
                          if (!allowed.includes(file.type)) {
                            toast.error('Please upload PDF, JPG, or PNG only.');
                            return;
                          }
                          setPayNowForm({ ...payNowForm, proof_file: file });
                          setPayNowProofFileName(file.name);
                        }
                      }}
                    />
                    <Upload size={24} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{payNowProofFileName || 'Click to upload (PDF, JPG, PNG)'}</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowPayNowDialog(false)}>Cancel</Button>
                <Button onClick={handlePayNowSubmit} disabled={payNowSubmitLoading} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {payNowSubmitLoading ? <><Loader2 size={16} className="mr-2 animate-spin" />Submitting...</> : <>Complete Payment</>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Received Dialog (receiver confirms + rate) */}
      <Dialog open={showPaymentReceivedDialog} onOpenChange={setShowPaymentReceivedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payment Received & Rate</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">Confirm you have received the payment and rate your experience.</p>
          <div className="space-y-4">
            <div>
              <Label>Transaction Rating *</Label>
              <div className="flex items-center space-x-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setCompletionForm({ ...completionForm, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star size={28} className={star <= completionForm.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                  </button>
                ))}
                <span className="text-sm text-gray-600 ml-2">{completionForm.rating}/5</span>
              </div>
            </div>
            <div>
              <Label>Feedback</Label>
              <Textarea
                value={completionForm.feedback}
                onChange={(e) => setCompletionForm({ ...completionForm, feedback: e.target.value })}
                placeholder="Optional feedback..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPaymentReceivedDialog(false)} disabled={paymentReceivedSubmitLoading}>Cancel</Button>
              <Button onClick={handlePaymentReceivedSubmit} disabled={paymentReceivedSubmitLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {paymentReceivedSubmitLoading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Confirm Payment Received'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promote Quote Dialog */}
      {pointsSystemEnabled && (
      <Dialog open={showPromoteDialog} onOpenChange={(open) => {
        setShowPromoteDialog(open);
        if (!open) {
          setPromoteQuote(null);
          setPromotePriority('');
          setPromoteUntilDate('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Promote Quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Choose a priority level and how long you want it.
              {pointsSystemEnabled ? ' Points will be deducted based on the duration.' : ''}
            </p>
            {pointsSystemEnabled && memberProfile && (
              <p className="text-xs text-gray-500">
                You have <strong>{memberProfile.reward_points?.toLocaleString() ?? 0} points</strong> available.
                {memberProfile.tier && ` Tier discount: ${memberProfile.tier === 'Founder' ? 50 : memberProfile.tier === 'Titanium' ? 40 : memberProfile.tier === 'Rhodium' ? 30 : memberProfile.tier === 'Platinum' ? 20 : memberProfile.tier === 'Gold' ? 10 : 0}% off`}
              </p>
            )}
            <div>
              <Label>Promote to</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {[
                  { value: 'High', label: 'High', cost: pointsSystemEnabled ? '250 pts/day' : '', desc: 'Maximum visibility' },
                  { value: 'Medium', label: 'Medium', cost: pointsSystemEnabled ? '100 pts/day' : '', desc: 'Enhanced visibility' }
                ].map((p) => (
                  <label
                    key={p.value}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${promotePriority === p.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <input
                      type="radio"
                      name="promotePriority"
                      value={p.value}
                      checked={promotePriority === p.value}
                      onChange={(e) => setPromotePriority(e.target.value)}
                      className="sr-only"
                    />
                    <div className="text-sm font-medium">{p.label}</div>
                    {p.cost && <div className="text-xs text-blue-600 font-medium">{p.cost}</div>}
                    <div className="text-xs text-gray-400">{p.desc}</div>
                  </label>
                ))}
              </div>
            </div>
            {promotePriority && (
              <div>
                <Label>Priority until date</Label>
                {promoteQuote?.priority && promoteQuote.priority !== 'Normal' && getPriorityUntilDateValue(promoteQuote.priority_until) && (
                  <p className="mt-1 text-sm text-gray-500">
                    Current {promoteQuote.priority.toLowerCase()} priority ends on {formatPriorityUntilLabel(promoteQuote.priority_until)}.
                  </p>
                )}
                  <Input
                    type="date"
                    value={promoteUntilDate}
                    min={promoteQuote ? getPromotionStartDateValue(promoteQuote) : getTodayDateValue()}
                    onChange={(e) => setPromoteUntilDate(getSafeFutureDateValue(e.target.value))}
                    className="mt-1"
                  />
                {pointsSystemEnabled && promoteUntilDate && (() => {
                  const minimumDate = promoteQuote ? getPromotionStartDateValue(promoteQuote) : getTodayDateValue();
                  const minimum = new Date(`${minimumDate}T00:00:00`);
                  const until = new Date(promoteUntilDate);
                  until.setHours(0, 0, 0, 0);
                  const diffDays = Math.floor((until - minimum) / (24 * 60 * 60 * 1000));
                  const days = Math.max(1, diffDays + 1);
                  const baseCost = promotePriority === 'High' ? (pointsConfig?.costs?.priority_high ?? 250) : (pointsConfig?.costs?.priority_medium ?? 100);
                  const discount = memberProfile?.tier === 'Founder' ? 50 : memberProfile?.tier === 'Titanium' ? 40 : memberProfile?.tier === 'Rhodium' ? 30 : memberProfile?.tier === 'Platinum' ? 20 : memberProfile?.tier === 'Gold' ? 10 : 0;
                  const pointsToDeduct = Math.floor(days * baseCost * (100 - discount) / 100);
                  const available = pointsSystemEnabled ? (memberProfile?.reward_points ?? 0) : 0;
                  const insufficient = pointsToDeduct > available;
                  return (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-600">
                        {days} day{days !== 1 ? 's' : ''} × {baseCost} pts/day → <strong>{pointsToDeduct} pts</strong> (after {discount}% discount)
                      </p>
                      {promoteQuote?.priority && promoteQuote.priority !== 'Normal' && getPriorityUntilDateValue(promoteQuote.priority_until) && (
                        <p className="text-xs text-gray-500">
                          Extension starts from {minimumDate}.
                        </p>
                      )}
                      {insufficient ? (
                        <p className="text-sm text-red-600 font-medium">
                          You need {pointsToDeduct} pts but have {available} pts. Select a shorter period.
                        </p>
                      ) : (
                        <p className="text-xs text-green-600">
                          {pointsToDeduct} pts will be deducted on confirm.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPromoteDialog(false)}>Cancel</Button>
              <Button
                onClick={handlePromoteConfirm}
                disabled={(() => {
                  if (!promotePriority || !promoteUntilDate) return true;
                  const minimumDate = promoteQuote ? getPromotionStartDateValue(promoteQuote) : getTodayDateValue();
                  const minimum = new Date(`${minimumDate}T00:00:00`);
                  const until = new Date(promoteUntilDate);
                  until.setHours(0, 0, 0, 0);
                  if (until < minimum) return true;
                  const diffDays = Math.floor((until - minimum) / (24 * 60 * 60 * 1000));
                  const days = Math.max(1, diffDays + 1);
                  const baseCost = promotePriority === 'High' ? (pointsConfig?.costs?.priority_high ?? 250) : (pointsConfig?.costs?.priority_medium ?? 100);
                  const discount = memberProfile?.tier === 'Founder' ? 50 : memberProfile?.tier === 'Titanium' ? 40 : memberProfile?.tier === 'Rhodium' ? 30 : memberProfile?.tier === 'Platinum' ? 20 : memberProfile?.tier === 'Gold' ? 10 : 0;
                  const pointsToDeduct = Math.floor(days * baseCost * (100 - discount) / 100);
                  const available = pointsSystemEnabled ? (memberProfile?.reward_points ?? 0) : 0;
                  return pointsSystemEnabled && pointsToDeduct > available;
                })()}
              >
                Promote
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      )}

      {/* Void / Publish Confirmation Dialog */}
      <Dialog open={showVoidConfirmDialog} onOpenChange={(open) => {
        setShowVoidConfirmDialog(open);
        if (!open) setVoidConfirmQuote(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {voidConfirmQuote?.status === 'Draft'
                ? 'Delete Draft'
                : voidConfirmQuote?.is_voided
                  ? 'Publish Quote'
                  : 'Void Quote'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            {voidConfirmQuote?.status === 'Draft'
              ? 'Are you sure you want to delete this draft? This action cannot be undone.'
              : voidConfirmQuote?.is_voided
                ? 'Are you sure you want to publish this quote? It will become visible to all members.'
                : 'Are you sure you want to void this quote? It will be visible only to you.'}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowVoidConfirmDialog(false); setVoidConfirmQuote(null); }}
            >
              Cancel
            </Button>
            <Button
              className={voidConfirmQuote?.status === 'Draft'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : voidConfirmQuote?.is_voided
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'}
              onClick={handleVoidConfirm}
            >
              {voidConfirmQuote?.status === 'Draft'
                ? 'Delete'
                : voidConfirmQuote?.is_voided
                  ? 'Publish'
                  : 'Void'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bid Submission Dialog */}
      <Dialog open={showBidDialog} onOpenChange={(open) => {
        setShowBidDialog(open);
        if (!open) {
          setBidDraftLoaded(false);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-8">
              <span className="flex items-center">
                <Calculator size={20} className="mr-2 text-green-600" />
                Submit Quotation
              </span>
              {selectedQuote && !selectedQuote.has_submitted_bid && bidDraftInfo && !bidDraftLoaded && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openSavedBidDraft(selectedQuote)}
                >
                  Open Draft
                </Button>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Fill in prices for each line item, set how long your quotation stays valid, then submit your bid.
            </DialogDescription>
          </DialogHeader>

          {selectedQuote && (
            <div className="space-y-6">
              {bidDialogLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                  <span className="ml-2">Loading quotation details...</span>
                </div>
              ) : (
                <>
                  {/* Quote details at top – same as card */}
                  <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">Quote details</h4>
                    {/* Header: ID, type, status, date */}
                    <div className="flex items-center flex-wrap gap-2">
                      {selectedQuote.reference_number && (
                        <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          <Hash size={12} className="inline mr-1" />
                          {selectedQuote.reference_number}
                        </span>
                      )}
                      <span className="font-semibold text-gray-900">{selectedQuote.quote_type}</span>
                      <Badge className={getQuoteDisplayStatusBadge(selectedQuote).className}>
                        {getQuoteDisplayStatusBadge(selectedQuote).label}
                      </Badge>
                      <Badge className="bg-gray-100 text-gray-700">{selectedQuote.priority || 'Normal'}</Badge>
                      <span className="text-xs text-gray-400 flex items-center ml-auto">
                        <Clock size={12} className="mr-1" />
                        {new Date(selectedQuote.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </div>
                    {/* Main details grid: Route, Commodity, Incoterm, Scope, containers */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm items-start">
                      <div className="col-span-2 min-w-0">
                        <span className="text-gray-500 text-xs block">Route</span>
                        {selectedQuote.quote_type === 'Warehousing' ? (
                          <div className="flex items-center font-medium">
                            <MapPin size={14} className="mr-1 text-green-600 shrink-0" />
                            {getQuoteRouteDisplay(selectedQuote).originText}
                          </div>
                        ) : (
                          <div className="flex items-center font-medium">
                            <MapPin size={14} className="mr-1 text-green-600 shrink-0" />
                            {getQuoteRouteDisplay(selectedQuote).originText}
                            <span className="mx-2 text-gray-400">-&gt;</span>
                            <MapPin size={14} className="mr-1 text-red-600 shrink-0" />
                            {getQuoteRouteDisplay(selectedQuote).destinationText}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 text-xs block">Commodity</span>
                        <span className="font-medium">{selectedQuote.commodity || 'General'}</span>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 text-xs block">Incoterm</span>
                        <span className="font-medium">{Array.isArray(selectedQuote.incoterm) ? selectedQuote.incoterm.join(', ') : (selectedQuote.incoterm || '—')}</span>
                      </div>
                      <div className="min-w-0 min-h-[2rem]">
                        <span className="text-gray-500 text-xs block">Scope</span>
                        <span className="font-medium block break-words">{getQuoteScopeDisplay(selectedQuote)}</span>
                      </div>
                      {selectedQuote.quote_type !== 'Air' && (
                        <>
                          <div className="min-w-0 min-h-[2rem]">
                            <span className="text-gray-500 text-xs block">Standard container</span>
                            <span className="font-medium block break-words">{getQuoteStandardContainerDisplay(selectedQuote)}</span>
                          </div>
                          <div className="min-w-0 min-h-[2rem]">
                            <span className="text-gray-500 text-xs block">Special container</span>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={
                                      getQuoteSpecialContainerTypesOnly(selectedQuote) !== '—'
                                        ? 'font-medium block break-words text-blue-600 hover:text-blue-800 hover:underline cursor-help underline-offset-2 decoration-blue-500/70 transition-colors'
                                        : 'font-medium block break-words text-gray-600'
                                    }
                                  >
                                    {getQuoteSpecialContainerTypesOnly(selectedQuote)}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap text-left">
                                  {getQuoteSpecialContainerFullDetails(selectedQuote)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </>
                      )}
                    </div>
                    {/* Creator identity */}
                    <div className="rounded bg-blue-50 p-2">
                      <InquiryCreatorIdentity
                        name={selectedQuote.requester_name || selectedQuote.requester_company || selectedQuote.requester_company_name}
                        profilePicture={selectedQuote.requester_profile_picture}
                        companyName={selectedQuote.requester_company || selectedQuote.requester_company_name}
                        compact
                      />
                    </div>
                    {/* Cargo / weight summary */}
                    {renderCargoSummary(selectedQuote)}
                    {renderSubmitQuoteModeDetails(selectedQuote)}

                    {selectedQuote.special_instructions && selectedQuote.special_instructions.length > 0 && (
                      <div className="text-sm">
                        <span className="text-gray-500 block mb-1">Requirements</span>
                        <p className="font-medium text-gray-700">
                          {selectedQuote.special_instructions.map((i) => i.instruction_text || '').filter(Boolean).join('; ')}
                        </p>
                      </div>
                    )}
                    {getQuoteDisplayCargoDescription(selectedQuote) && (
                      <div className="text-sm">
                        <span className="text-gray-500 block mb-1">Notes</span>
                        <p className="text-gray-700 whitespace-pre-wrap">{getQuoteDisplayCargoDescription(selectedQuote)}</p>
                      </div>
                    )}
                  </div>

                  {/* Form section label */}
                  <h4 className="font-semibold text-gray-900 pt-2">Fill in your quotation</h4>

                  {/* Section 1: Requester's Charge Items - Code & Description view-only; receiver fills Unit, Qty, Price */}
                  {requesterLineItems.length > 0 && (
                    <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/30">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center text-amber-800">
                          <FileText size={16} className="mr-2" />
                          Requester&apos;s Charge Items (Fill in your prices)
                        </h4>
                        <Badge className="bg-amber-100 text-amber-700">Required</Badge>
                      </div>
                      <p className="text-xs text-amber-700 mb-3">Charge code and description are set by the sender. Fill in Unit, Quantity and Price.</p>

                      <div className="space-y-3">
                        {requesterLineItems.map((item, idx) => {
                          const chargeCodeLabel = (() => {
                            const code = chargeCodes.find((c) => c.code === item.charge_code);
                            if (code) return code.name;
                            const bidModeId = selectedQuote.quote_type === 'Ocean FCL' ? 'ocean-fcl' : selectedQuote.quote_type === 'Ocean LCL' ? 'ocean-lcl' : selectedQuote.quote_type === 'Air' ? 'air' : selectedQuote.quote_type === 'Transport' ? 'transport' : selectedQuote.quote_type === 'Warehousing' ? 'warehousing' : selectedQuote.quote_type === 'Breakbulk' ? 'breakbulk' : null;
                            const heads = bidModeId ? getChargeHeadsForModeAndScopes(bidModeId, selectedQuote.form_data?.scope || []) : [];
                            const head = heads.find((c) => c.code === item.charge_code);
                            return head ? head.name : (item.charge_code || item.description || '—');
                          })();
                          return (
                            <div key={item.id} className="p-3 bg-white rounded border border-amber-100">
                              {/* Row 1: Charge Code, Container Type, Unit, Qty, Unit Price, Delete */}
                              <div className="grid grid-cols-12 gap-2 items-end">
                                {/* Charge Code - View only (set by sender) */}
                                <div className={selectedQuote.quote_type === 'Air' ? 'col-span-5' : 'col-span-3'}>
                                  <Label className="text-xs text-gray-500">Charge Code</Label>
                                  <div className="h-8 px-2 text-sm border rounded bg-gray-50 flex items-center text-gray-700">
                                    {chargeCodeLabel}
                                  </div>
                                </div>
                                {/* Container Type - hide for Air freight */}
                                {selectedQuote.quote_type !== 'Air' && (
                                  <div className="col-span-2">
                                    <Label className="text-xs">Container Type</Label>
                                    <select
                                      className="w-full h-8 px-2 text-sm border rounded"
                                      value={item.container_type || 'All'}
                                      onChange={(e) => {
                                        const updated = [...requesterLineItems];
                                        updated[idx].container_type = e.target.value;
                                        setRequesterLineItems(updated);
                                      }}
                                    >
                                      {getDialogContainerTypesForBid().map(ct => (
                                        <option key={ct} value={ct}>{ct}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                {/* Unit - Editable by receiver; enough width so "Per Container" is not truncated */}
                                <div className="col-span-2">
                                  <Label className="text-xs">Unit</Label>
                                  <select
                                    className="h-8 text-sm border rounded px-2 w-full"
                                    value={item.unit}
                                    onChange={(e) => {
                                      const updated = [...requesterLineItems];
                                      updated[idx].unit = e.target.value;
                                      setRequesterLineItems(updated);
                                    }}
                                  >
                                    {units.map(u => <option key={u} value={u}>{u}</option>)}
                                  </select>
                                </div>
                                {/* Quantity - Editable by receiver */}
                                <div className="col-span-1">
                                  <Label className="text-xs">Qty</Label>
                                  <Input
                                    className="h-8 text-sm"
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => {
                                      const updated = [...requesterLineItems];
                                      updated[idx].quantity = parseFloat(e.target.value) || 1;
                                      setRequesterLineItems(updated);
                                    }}
                                  />
                                </div>
                                {/* Unit Price - Editable by receiver */}
                                <div className="col-span-3">
                                  <Label className="text-xs">Unit Price ($)</Label>
                                  <Input
                                    className="h-8 text-sm"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.unit_price === 0 ? '' : item.unit_price}
                                    onChange={(e) => {
                                      const updated = [...requesterLineItems];
                                      const raw = e.target.value;
                                      updated[idx].unit_price = raw === '' ? 0 : (parseFloat(raw) || 0);
                                      setRequesterLineItems(updated);
                                    }}
                                    disabled={item.is_at_actuals}
                                    placeholder="Enter price"
                                  />
                                </div>
                                {/* Delete - Receiver can remove this requester item */}
                                <div className="col-span-1 flex items-end justify-end pb-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 text-red-500 hover:text-red-700 p-0"
                                    onClick={() => setRequesterLineItems(requesterLineItems.filter((_, i) => i !== idx))}
                                    title="Remove this item"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              </div>
                              {/* Row 2: Description (below Charge Code) + At Actual on same line */}
                              <div className="grid grid-cols-12 gap-2 items-center mt-2">
                                <div className="col-span-10 min-w-0">
                                  <Label className="text-xs text-gray-500">Description</Label>
                                  <Input
                                    className="h-8 text-sm bg-gray-50"
                                    value={item.description || ''}
                                    readOnly
                                    disabled
                                  />
                                </div>
                                <div className="col-span-2 flex items-center space-x-2 pt-6">
                                  <input
                                    type="checkbox"
                                    id={`at-actual-req-${idx}`}
                                    checked={item.is_at_actuals}
                                    onChange={(e) => {
                                      const updated = [...requesterLineItems];
                                      updated[idx].is_at_actuals = e.target.checked;
                                      setRequesterLineItems(updated);
                                    }}
                                    className="h-4 w-4 rounded border-gray-300"
                                  />
                                  <Label htmlFor={`at-actual-req-${idx}`} className="text-xs cursor-pointer">At Actual</Label>
                                </div>
                              </div>
                              {/* Tax Row */}
                              <div className="grid grid-cols-12 gap-2 items-center mt-2 pt-2 border-t border-dashed">
                                <div className="col-span-3 flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id={`tax-req-${idx}`}
                                    checked={item.tax_applicable}
                                    onChange={(e) => {
                                      const updated = [...requesterLineItems];
                                      updated[idx].tax_applicable = e.target.checked;
                                      if (!e.target.checked) {
                                        updated[idx].tax_rate = 0;
                                      }
                                      setRequesterLineItems(updated);
                                    }}
                                    className="h-4 w-4 rounded border-gray-300"
                                  />
                                  <Label htmlFor={`tax-req-${idx}`} className="text-xs cursor-pointer">Tax Applicable</Label>
                                </div>
                                {item.tax_applicable && (
                                  <>
                                    <div className="col-span-2">
                                      <Label className="text-xs text-gray-500 block mb-0.5">Tax Type</Label>
                                      <Input
                                        className="h-7 text-xs uppercase"
                                        placeholder="e.g. VAT, GST"
                                        value={item.tax_type || ''}
                                        onChange={(e) => {
                                          const updated = [...requesterLineItems];
                                          updated[idx].tax_type = (e.target.value || '').toUpperCase();
                                          setRequesterLineItems(updated);
                                        }}
                                      />
                                    </div>
                                    <div className="col-span-2">
                                      <Label className="text-xs text-gray-500 block mb-0.5">Tax %</Label>
                                      <div className="flex items-center gap-1">
                                        <Input
                                          className="h-7 text-xs w-16"
                                          type="number"
                                          min="0.01"
                                          max="100"
                                          step="0.1"
                                          value={item.tax_rate}
                                          onChange={(e) => {
                                            const updated = [...requesterLineItems];
                                            updated[idx].tax_rate = parseFloat(e.target.value) || 0;
                                            setRequesterLineItems(updated);
                                          }}
                                          placeholder="0"
                                        />
                                        <span className="text-xs">%</span>
                                      </div>
                                    </div>
                                  </>
                                )}
                                <div className="col-span-5 text-right text-sm font-medium">
                                  {item.is_at_actuals ? (
                                    <span className="text-amber-600 italic">At Actuals</span>
                                  ) : (
                                    <span className="text-gray-700">
                                      Line Total: ${((item.unit_price * item.quantity) * (1 + (item.tax_applicable ? item.tax_rate / 100 : 0))).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Section 2: Additional Line Items (Added by Bidder) */}
                  <div className="border rounded-lg p-4">
                    <div className="mb-3">
                      <h4 className="font-semibold flex items-center">
                        <Plus size={16} className="mr-2 text-green-600" />
                        Additional Charges (Optional)
                      </h4>
                    </div>

                    {bidLineItems.length > 0 ? (
                      <div className="space-y-3">
                        {(() => {
                          const bidModeId = selectedQuote.quote_type === 'Ocean FCL' ? 'ocean-fcl' : selectedQuote.quote_type === 'Ocean LCL' ? 'ocean-lcl' : selectedQuote.quote_type === 'Air' ? 'air' : selectedQuote.quote_type === 'Transport' ? 'transport' : selectedQuote.quote_type === 'Warehousing' ? 'warehousing' : selectedQuote.quote_type === 'Breakbulk' ? 'breakbulk' : null;
                          const selectedScopes = selectedQuote.form_data?.scope || [];
                          const byMode = bidModeId ? getChargeHeadsForModeAndScopes(bidModeId, selectedScopes) : [];
                          const bidChargeCodesForDropdown = byMode.length > 0
                            ? byMode
                            : (selectedScopes.length > 0 ? [] : chargeCodes);
                          const bidChargeCodeOptions = bidChargeCodesForDropdown.map((code) => ({
                            value: code.code,
                            label: `${code.name} (${code.code})`
                          }));
                          return bidLineItems.map((item, idx) => (
                          <div key={item.id} className="p-3 bg-gray-50 rounded border">
                            <div className="grid grid-cols-12 gap-2 items-end">
                              {/* Charge Code (replaces Description) */}
                              <div className={selectedQuote.quote_type === 'Air' ? 'col-span-6' : 'col-span-3'}>
                                <Label className="text-xs">Charge Code *</Label>
                                <div className="flex flex-col gap-2">
                                  <SearchableSelect
                                    options={bidChargeCodeOptions}
                                    value={
                                      (item.is_custom_charge_code ||
                                        (item.charge_code &&
                                          !bidChargeCodesForDropdown.some((c) => c.code === item.charge_code)))
                                        ? '__other__'
                                        : (item.charge_code || '')
                                    }
                                    onChange={(next) => {
                                      const updated = [...bidLineItems];

                                      if (next === '__other__') {
                                        updated[idx].is_custom_charge_code = true;
                                        updated[idx].charge_code = '';
                                        updated[idx].description = '';
                                        setBidLineItems(updated);
                                        return;
                                      }

                                      const code = bidChargeCodesForDropdown.find((c) => c.code === next);
                                      updated[idx].is_custom_charge_code = false;
                                      updated[idx].charge_code = next;
                                      updated[idx].description = code ? code.name : '';
                                      setBidLineItems(updated);
                                    }}
                                    placeholder="Select charge code..."
                                    allowOther
                                    otherLabel="Other (type manually)"
                                    className="h-8 bg-white text-sm"
                                  />

                                  {(item.is_custom_charge_code ||
                                    (item.charge_code &&
                                      !bidChargeCodesForDropdown.some((c) => c.code === item.charge_code))) && (
                                    <Input
                                      className="h-8 text-sm"
                                      value={item.charge_code || ''}
                                      onChange={(e) => {
                                        const updated = [...bidLineItems];
                                        updated[idx].charge_code = e.target.value;
                                        updated[idx].is_custom_charge_code = true;
                                        setBidLineItems(updated);
                                      }}
                                      placeholder="Enter charge code"
                                    />
                                  )}
                                </div>
                              </div>
                              {/* Container Type - hide for Air freight */}
                              {selectedQuote.quote_type !== 'Air' && (
                                <div className="col-span-3">
                                  <Label className="text-xs">Container Type</Label>
                                  <select
                                    className="w-full h-8 px-2 text-sm border rounded"
                                    value={item.container_type || 'All'}
                                    onChange={(e) => {
                                      const updated = [...bidLineItems];
                                      updated[idx].container_type = e.target.value;
                                      setBidLineItems(updated);
                                    }}
                                  >
                                    {getDialogContainerTypesForBid().map(ct => <option key={ct} value={ct}>{ct}</option>)}
                                  </select>
                                </div>
                              )}
                              {/* Unit */}
                              <div className="col-span-1 min-w-0">
                                <Label className="text-xs">Unit</Label>
                                <select
                                  className="w-full h-8 px-2 text-sm border rounded"
                                  value={item.unit}
                                  onChange={(e) => {
                                    const updated = [...bidLineItems];
                                    updated[idx].unit = e.target.value;
                                    setBidLineItems(updated);
                                  }}
                                >
                                  {units.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                              </div>
                              {/* Quantity */}
                              <div className="col-span-1">
                                <Label className="text-xs">Qty</Label>
                                <Input
                                  className="h-8 text-sm"
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const updated = [...bidLineItems];
                                    updated[idx].quantity = parseFloat(e.target.value) || 1;
                                    setBidLineItems(updated);
                                  }}
                                />
                              </div>
                              {/* Unit Price */}
                              <div className="col-span-2">
                                <Label className="text-xs">Price ($)</Label>
                                <Input
                                  className="h-8 text-sm"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unit_price === 0 ? '' : item.unit_price}
                                  onChange={(e) => {
                                    const updated = [...bidLineItems];
                                    const raw = e.target.value;
                                    updated[idx].unit_price = raw === '' ? 0 : (parseFloat(raw) || 0);
                                    setBidLineItems(updated);
                                  }}
                                  disabled={item.is_at_actuals}
                                  placeholder="Enter price"
                                />
                              </div>
                              {/* At Actuals + Delete */}
                              <div className="col-span-2 flex items-center justify-between">
                                <div className="flex items-center space-x-1">
                                  <input
                                    type="checkbox"
                                    id={`at-actual-${idx}`}
                                    checked={item.is_at_actuals}
                                    onChange={(e) => {
                                      const updated = [...bidLineItems];
                                      updated[idx].is_at_actuals = e.target.checked;
                                      setBidLineItems(updated);
                                    }}
                                    className="h-4 w-4 rounded border-gray-300"
                                  />
                                  <Label htmlFor={`at-actual-${idx}`} className="text-xs cursor-pointer">At Actual</Label>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 text-red-500 p-1"
                                  onClick={() => {
                                    setBidLineItems(bidLineItems.filter(i => i.id !== item.id));
                                  }}
                                >
                                  <X size={14} />
                                </Button>
                              </div>
                            </div>
                            {/* Tax Row */}
                            <div className="grid grid-cols-12 gap-2 items-center mt-2 pt-2 border-t border-dashed">
                              <div className="col-span-3 flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`tax-${idx}`}
                                  checked={item.tax_applicable}
                                  onChange={(e) => {
                                    const updated = [...bidLineItems];
                                    updated[idx].tax_applicable = e.target.checked;
                                    if (!e.target.checked) {
                                      updated[idx].tax_rate = 0;
                                    }
                                    setBidLineItems(updated);
                                  }}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                                <Label htmlFor={`tax-${idx}`} className="text-xs cursor-pointer">Tax Applicable</Label>
                              </div>
                              {item.tax_applicable && (
                                <>
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500 block mb-0.5">Tax Type</Label>
                                    <Input
                                      className="h-7 text-xs uppercase"
                                      placeholder="e.g. VAT, GST"
                                      value={item.tax_type || ''}
                                      onChange={(e) => {
                                        const updated = [...bidLineItems];
                                        updated[idx].tax_type = (e.target.value || '').toUpperCase();
                                        setBidLineItems(updated);
                                      }}
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500 block mb-0.5">Tax %</Label>
                                    <div className="flex items-center gap-1">
                                      <Input
                                        className="h-7 text-xs w-16"
                                        type="number"
                                        min="0.01"
                                        max="100"
                                        step="0.1"
                                        value={item.tax_rate}
                                        onChange={(e) => {
                                          const updated = [...bidLineItems];
                                          updated[idx].tax_rate = parseFloat(e.target.value) || 0;
                                          setBidLineItems(updated);
                                        }}
                                        placeholder="0"
                                      />
                                      <span className="text-xs">%</span>
                                    </div>
                                  </div>
                                </>
                              )}
                              <div className="col-span-5 text-right text-sm font-medium">
                                {item.is_at_actuals ? (
                                  <span className="text-amber-600 italic">At Actuals</span>
                                ) : (
                                  <span className="text-gray-700">
                                    Line Total: ${((item.unit_price * item.quantity) * (1 + (item.tax_applicable ? item.tax_rate / 100 : 0))).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ));
                        })()}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-3 border border-dashed rounded">
                        Click &quot;Add Line Item&quot; below to include additional charges not listed by the requester
                      </p>
                    )}

                    <div className="mt-4 flex justify-start">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const newItem = {
                            id: Date.now(),
                            charge_code: '',
                            description: '',
                            unit: 'Per Container',
                            quantity: 1,
                            unit_price: 0,
                            tax_applicable: false,
                            tax_type: '',
                            tax_rate: 0,
                            is_at_actuals: false,
                            container_type: 'All',
                            is_from_requester: false
                          };
                          setBidLineItems([...bidLineItems, newItem]);
                        }}
                      >
                        <Plus size={14} className="mr-1" />
                        Add Line Item
                      </Button>
                    </div>
                  </div>

                  {/* Grand Total Summary */}
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-green-800">Quotation Summary</h4>
                      <div className="text-right">
                        {(() => {
                          const allItems = [...requesterLineItems, ...bidLineItems];
                          // Include all items in subtotal (including "At Actual" so total reflects entered prices)
                          const subtotal = allItems.reduce((sum, item) => {
                            return sum + ((item.unit_price || 0) * (item.quantity || 1));
                          }, 0);
                          const taxTotal = allItems.reduce((sum, item) => {
                            if (!item.tax_applicable) return sum;
                            return sum + (((item.unit_price || 0) * (item.quantity || 1)) * (item.tax_rate || 0) / 100);
                          }, 0);
                          const atActualsCount = allItems.filter(item => item.is_at_actuals).length;

                          return (
                            <div className="space-y-1">
                              <div className="text-sm text-gray-600">
                                Subtotal: <span className="font-medium">${subtotal.toFixed(2)}</span>
                              </div>
                              {taxTotal > 0 && (
                                <div className="text-sm text-gray-600">
                                  Tax: <span className="font-medium">${taxTotal.toFixed(2)}</span>
                                </div>
                              )}
                              {atActualsCount > 0 && (
                                <div className="text-sm text-amber-600">
                                  Include {atActualsCount} item(s) at actuals
                                </div>
                              )}
                              <div className="text-lg font-bold text-green-700 pt-1 border-t">
                                Grand Total: ${(subtotal + taxTotal).toFixed(2)}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Special Instructions - checkboxes (mandatory pre-checked, optional can be toggled) */}
                  {selectedQuote.special_instructions && selectedQuote.special_instructions.length > 0 && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <h4 className="font-semibold mb-3 flex items-center text-blue-800">
                        <FileText size={16} className="mr-2" />
                        Special Instructions
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">Acknowledge the instructions below. Mandatory ones must be checked to submit.</p>
                      <div className="space-y-2">
                        {selectedQuote.special_instructions.map((instruction, idx) => {
                          const isMandatory = !!instruction.is_mandatory;
                          const checked = bidSpecialInstructionsAccepted[idx] ?? false;
                          return (
                            <label
                              key={idx}
                              className={`flex items-start gap-3 p-2 rounded border cursor-pointer ${checked ? 'bg-white border-blue-200' : 'bg-white/80 border-gray-200'}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const instr = selectedQuote.special_instructions;
                                  const base = bidSpecialInstructionsAccepted.length >= instr.length
                                    ? [...bidSpecialInstructionsAccepted]
                                    : instr.map(() => false);
                                  const next = base.slice(0, instr.length);
                                  while (next.length < instr.length) next.push(false);
                                  next[idx] = e.target.checked;
                                  setBidSpecialInstructionsAccepted(next);
                                }}
                                className="h-4 w-4 mt-0.5 rounded border-gray-300 text-blue-600"
                              />
                              <span className="text-sm flex-1">
                                <span className="font-medium text-gray-700">
                                  {instruction.instruction_number || `${idx + 1}.${idx + 1}`}:
                                </span>{' '}
                                {instruction.instruction_text}
                                {isMandatory && (
                                  <Badge className="ml-2 bg-red-100 text-red-700 text-xs">Mandatory</Badge>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Bid Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quotation Valid Until *</Label>
                      <Input
                        type="date"
                        value={sanitizeHtmlDateValue(bidValidity)}
                        min={getTodayDateValue()}
                        onChange={(e) => setBidValidity(getSafeFutureDateValue(e.target.value))}
                        placeholder="Select validity date"
                      />
                    </div>
                    <div>
                      <Label>Remarks / Notes</Label>
                      <Textarea
                        value={bidRemarks}
                        onChange={(e) => setBidRemarks(e.target.value)}
                        placeholder="Any terms, conditions or additional notes..."
                        rows={2}
                      />
                    </div>
                  </div>

                  {!selectedQuote?.has_submitted_bid && (
                    <div className="rounded-lg border border-dashed border-blue-200 bg-blue-50/60 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h4 className="font-semibold text-blue-900">Draft</h4>
                          <p className="text-sm text-blue-800">
                            Your quotation is auto-saved while you edit it.
                            {bidDraftInfo?.saved_at ? ` Last saved ${new Date(bidDraftInfo.saved_at).toLocaleString()}.` : ' Add any pricing or notes to start a draft.'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => persistBidDraft(selectedQuote, getCurrentBidDraftState(), { silent: false })}
                          >
                            Save Draft
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => deleteBidDraft(selectedQuote.quote_id, { resetForm: true })}
                            disabled={!bidDraftInfo}
                          >
                            Delete Draft
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Submit Buttons */}
                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button variant="outline" onClick={() => setShowBidDialog(false)}>
                      Cancel
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={async () => {
                        const allLineItems = [...requesterLineItems, ...bidLineItems];

                        if (allLineItems.length === 0) {
                          toast.error('Please add at least one line item to your quotation');
                          return;
                        }

                        // Validate that requester items have prices (unless at actuals)
                        const missingPrices = requesterLineItems.filter(item => !item.is_at_actuals && item.unit_price <= 0);
                        if (missingPrices.length > 0) {
                          toast.error(`Please fill in prices for: ${missingPrices.map(i => i.description).join(', ')}`);
                          return;
                        }

                        // Validate that all mandatory special instructions are accepted
                        const instructions = selectedQuote.special_instructions || [];
                        const accepted = bidSpecialInstructionsAccepted.length >= instructions.length
                          ? bidSpecialInstructionsAccepted
                          : instructions.map((i) => !!i.is_mandatory);
                        const mandatoryUnchecked = instructions
                          .map((inst, idx) => ({ inst, idx }))
                          .filter(({ inst }) => !!inst.is_mandatory)
                          .filter(({ idx }) => !accepted[idx]);
                        if (mandatoryUnchecked.length > 0) {
                          toast.error('Please acknowledge all mandatory special instructions (check the boxes).');
                          return;
                        }

                        // If tax is applicable, percentage cannot be zero
                        const invalidTax = allLineItems.filter(item => item.tax_applicable && (!item.tax_rate || Number(item.tax_rate) <= 0));
                        if (invalidTax.length > 0) {
                          toast.error('When Tax Applicable is checked, tax percentage must be greater than zero.');
                          return;
                        }

                        setBidSubmitLoading(true);
                        try {
                          // Convert selected validity date to number of days (backend expects validity_days)
                          let validityDays = 30;
                          const bidUntil = sanitizeHtmlDateValue(bidValidity);
                          if (bidUntil) {
                            if (bidUntil < getTodayDateValue()) {
                              toast.error('Quotation validity date cannot be in the past.');
                              setBidSubmitLoading(false);
                              return;
                            }
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const selected = new Date(bidUntil);
                            selected.setHours(0, 0, 0, 0);
                            const diffMs = selected.getTime() - today.getTime();
                            const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                            validityDays = diffDays > 0 ? diffDays : 1;
                          }

                          const bidData = {
                            line_items: allLineItems.map(item => ({
                              charge_code: item.charge_code || '',
                              description: item.description,
                              unit: item.unit || 'Per Container',
                              quantity: Number(item.quantity) || 1,
                              unit_price: Number(item.unit_price) || 0,
                              tax_applicable: item.tax_applicable || false,
                              tax_type: item.tax_type || '',
                              tax_rate: Number(item.tax_rate) || 0,
                              is_at_actuals: item.is_at_actuals || false,
                              container_type: item.container_type || 'All',
                              is_from_requester: item.is_from_requester || false
                            })),
                            validity_days: validityDays,
                            valid_until_date: bidUntil || null,
                            remarks: bidRemarks
                          };

                          if (selectedQuote?.has_submitted_bid && selectedQuote?.my_bid_id) {
                            const response = await quotesApi.updateBid(selectedQuote.quote_id, selectedQuote.my_bid_id, bidData, currentMemberId);
                            applySubmittedBidToLocalState(selectedQuote.quote_id, selectedQuote.my_bid_id);
                            toast.success(`Quotation updated successfully! Current price rank: ${response?.data?.price_rank || 'Pending'}`);
                          } else {
                            const response = await quotesApi.createBid(selectedQuote.quote_id, bidData, currentMemberId);
                            applySubmittedBidToLocalState(selectedQuote.quote_id, response?.data?.bid_id || null);
                            deleteBidDraft(selectedQuote.quote_id, { silent: true });
                            toast.success(`Quotation submitted successfully! Current price rank: ${response?.data?.price_rank || 'Pending'}`);
                          }
                          setShowBidDialog(false);
                          fetchData();
                          await refreshTierConfig?.();
                        } catch (error) {
                          toast.error('Failed to submit quotation: ' + getErrorMessage(error.response?.data?.detail, error.message));
                        } finally {
                          setBidSubmitLoading(false);
                        }
                      }}
                      disabled={bidSubmitLoading}
                      data-testid="submit-quotation-btn"
                    >
                      {bidSubmitLoading ? (
                        <>
                          <Loader2 size={14} className="mr-1 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send size={14} className="mr-1" />
                          {selectedQuote?.has_submitted_bid ? 'Update Quotation' : 'Submit Quotation'}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Quote Dialog - company admin assigns to a team member */}
      <Dialog open={showAssignDialog} onOpenChange={(open) => { setShowAssignDialog(open); if (!open) { setAssignQuoteFor(null); setAssignSearchQuery(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <User size={18} className="mr-2" />
              Assign Quote to Team Member
            </DialogTitle>
          </DialogHeader>
          {assignQuoteFor && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Quote: <strong>{assignQuoteFor.reference_number}</strong> – {assignQuoteFor.quote_type}
              </p>
              <p className="text-xs text-gray-500">Search and select a team member to assign this quote to. They will receive an email notification.</p>
              {assignLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : assignTeamMembers.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">No active team members found. Add team members from Team Management.</p>
              ) : (
                <>
                  <Input
                    placeholder="Search by name, email or role..."
                    value={assignSearchQuery}
                    onChange={(e) => setAssignSearchQuery(e.target.value)}
                    className="w-full"
                  />
                  <div className="space-y-2 max-h-48 overflow-y-auto overflow-x-hidden min-h-0">
                    {assignTeamMembers
                      .filter((tm) => tm.member_id && tm.is_active !== false)
                      .filter((tm) => {
                        if (!assignSearchQuery.trim()) return true;
                        const q = assignSearchQuery.trim().toLowerCase();
                        const name = (tm.full_name || '').toLowerCase();
                        const email = (tm.email || '').toLowerCase();
                        const role = (tm.role || '').toLowerCase();
                        return name.includes(q) || email.includes(q) || role.includes(q);
                      })
                      .map((tm) => (
                        <div
                          key={tm.team_member_id || tm.member_id}
                          className="flex items-center justify-between p-2 border rounded hover:bg-gray-50"
                        >
                          <div>
                            <span className="font-medium text-sm">{tm.full_name || tm.email}</span>
                            {tm.role && <span className="text-xs text-gray-500 ml-2">({tm.role})</span>}
                            {tm.email && <p className="text-xs text-gray-400">{tm.email}</p>}
                          </div>
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await quotesApi.assignQuote(assignQuoteFor.quote_id, tm.member_id, currentMemberId);
                                toast.success(`Quote assigned to ${tm.full_name || tm.email}`);
                                setShowAssignDialog(false);
                                setAssignQuoteFor(null);
                                fetchData();
                              } catch (err) {
                                toast.error(getErrorMessage(err.response?.data?.detail, err.message));
                              }
                            }}
                          >
                            Assign
                          </Button>
                        </div>
                      ))}
                  </div>
                  {assignTeamMembers.filter((tm) => tm.member_id && tm.is_active !== false).filter((tm) => {
                    if (!assignSearchQuery.trim()) return true;
                    const q = assignSearchQuery.trim().toLowerCase();
                    const name = (tm.full_name || '').toLowerCase();
                    const email = (tm.email || '').toLowerCase();
                    const role = (tm.role || '').toLowerCase();
                    return name.includes(q) || email.includes(q) || role.includes(q);
                  }).length === 0 && assignSearchQuery.trim() && (
                      <p className="text-sm text-gray-500 py-2">No team members match your search.</p>
                    )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bid Comparison Dialog */}
      <Dialog open={showBidComparisonDialog} onOpenChange={setShowBidComparisonDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Scale size={20} className="mr-2 text-purple-600" />
              Bid Comparison & Nomination
            </DialogTitle>
          </DialogHeader>

          {selectedQuote && (
            <BidComparison
              quote={selectedQuote}
              bids={quoteBids}
              onNominate={handleNominateFromComparison}
              onMessageMember={handleMessageBidderFromComparison}
              onClose={() => setShowBidComparisonDialog(false)}
              currentMemberId={currentMemberId}
              currentUserDisplayName={currentUserDisplayName}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Detail Dialog (View Enquiry) */}
      <Dialog open={showQuoteDetailDialog} onOpenChange={setShowQuoteDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Eye size={20} className="mr-2 text-blue-600" />
                Quote Details
              </div>
              {selectedQuote?.reference_number && (
                <span className="font-mono text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded">
                  {selectedQuote.reference_number}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedQuote && (() => {
            const fd = selectedQuote.form_data || {};
            const qt = selectedQuote.quote_type || '';
            const selectedQuoteIsOwnedByCurrentMember = selectedQuote.requester_id === currentMemberId;
            const selectedQuoteIsTeamEnquiry =
              !selectedQuoteIsOwnedByCurrentMember &&
              Boolean(memberProfile?.company_id) &&
              selectedQuote.requester_company_id === memberProfile.company_id;
            const selectedQuoteBidLost = isBidLostQuote(selectedQuote);
            const isOceanFCL = qt === 'Ocean FCL';
            const isOceanLCL = qt === 'Ocean LCL';
            const isOcean = isOceanFCL || isOceanLCL;
            const isTransport = qt === 'Transport';
            const isAir = qt === 'Air';
            const isWarehousing = qt === 'Warehousing';
            const isBreakbulk = qt === 'Breakbulk';
            const scope = fd.scope || [];
            const scopeDisplayOptions = (() => {
              const modeId = qt === 'Ocean FCL' ? 'ocean-fcl' : qt === 'Ocean LCL' ? 'ocean-lcl' : qt === 'Air' ? 'air' : qt === 'Transport' ? 'transport' : qt === 'Warehousing' ? 'warehousing' : qt === 'Breakbulk' ? 'breakbulk' : null;
              return (modeId && SCOPE_OPTIONS_BY_MODE[modeId]) || [];
            })();
            const containersObj = fd.containers || selectedQuote.containers;
            const oogList = fd.oogContainers || selectedQuote.oogContainers || [];
            const hasDimensions = getQuoteDimensionRows(fd).length > 0;
            const reefer = fd.reefer || selectedQuote.reefer || {};
            const standardContainerTypes = ['20GP', '40GP', '40HC'];
            const pickup = fd.pickupLocation || {};
            const delivery = fd.deliveryLocation || {};
            const insuranceDisplay = getInsuranceDisplay(fd);
            const packingListInfo = getFileDownloadInfo(fd.packingListFile, 'Packing list');
            const responseDeadlineInfo = getResponseDeadlineInfo(selectedQuote, deadlineNow);

            return (
              <div className="space-y-6">
                {/* Status & Info Header */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={getQuoteDisplayStatusBadge(selectedQuote).className}>
                    {getQuoteDisplayStatusBadge(selectedQuote).label}
                  </Badge>
                  {pointsSystemEnabled && (
                    <Badge className={getPriorityBadge(selectedQuote.priority).className}>
                      {getPriorityBadge(selectedQuote.priority).label} Priority
                    </Badge>
                  )}
                  {pointsSystemEnabled && selectedQuote.is_promoted && (
                    <Badge className="bg-amber-100 text-amber-700">
                      <ArrowUp size={12} className="mr-1" />
                      Promoted
                    </Badge>
                  )}
                  {selectedQuote.has_submitted_bid && !selectedQuote.my_bid_nominated && !selectedQuoteBidLost && (
                    <Badge className="bg-green-100 text-green-700">
                      <Check size={12} className="mr-1" />
                      Bid Submitted
                    </Badge>
                  )}
                  {selectedQuoteBidLost && (
                    <Badge className="bg-amber-100 text-amber-800">
                      <X size={12} className="mr-1" />
                      Bid Lost
                    </Badge>
                  )}
                  {selectedQuote.my_bid_nominated && (
                    <Badge className="bg-purple-100 text-purple-700">
                      <CheckCircle size={12} className="mr-1" />
                      Your Bid Nominated
                    </Badge>
                  )}
                  {selectedQuote.rfq_accepted_at && (
                    <Badge className="bg-blue-100 text-blue-700">
                      <CheckCircle size={12} className="mr-1" />
                      Shipment Completed
                    </Badge>
                  )}
                  {selectedQuote.payment_received_at && (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle size={12} className="mr-1" />
                      Payment Completed
                    </Badge>
                  )}
                  {(selectedQuoteIsOwnedByCurrentMember || selectedQuoteIsTeamEnquiry) && selectedQuote.status !== 'Draft' && (
                    <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-800">
                      <Eye size={12} className="mr-1" />
                      Seen by {selectedQuote.enquiry_view_count || 0} {(selectedQuote.enquiry_view_count || 0) === 1 ? 'member' : 'members'}
                    </Badge>
                  )}
                  <span className="text-sm text-gray-500 ml-auto">
                    Created: {new Date(selectedQuote.created_at).toLocaleString()}
                  </span>
                </div>

                {selectedQuote.last_edited_at && (
                  <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                    <span className="font-medium">Inquiry updated</span>{' '}
                    {new Date(selectedQuote.last_edited_at).toLocaleString()}
                    {selectedQuote.last_edited_by_name ? ` by ${selectedQuote.last_edited_by_name}` : ''}
                    {selectedQuote.last_edit_summary ? ` — ${selectedQuote.last_edit_summary}` : ''}
                  </div>
                )}

                {/* Quote Type & Requester */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div>
                    <Label className="text-xs text-gray-500">Quote Type</Label>
                    <p className="font-semibold text-lg">{selectedQuote.quote_type}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Requested By</Label>
                    <InquiryCreatorIdentity
                      name={selectedQuote.requester_name || selectedQuote.requester_company || selectedQuote.requester_company_name}
                      profilePicture={selectedQuote.requester_profile_picture}
                      companyName={selectedQuote.requester_company || selectedQuote.requester_company_name}
                      label={null}
                      compact
                      className="mt-1"
                    />
                  </div>
                  {responseDeadlineInfo && (
                    <div>
                      <Label className="text-xs text-gray-500">Last date to submit quotations</Label>
                      <p className={`font-semibold ${responseDeadlineInfo.isClosed ? 'text-red-700' : 'text-amber-800'}`}>
                        {responseDeadlineInfo.dateLabel}
                      </p>
                      <p className={`text-xs font-medium ${responseDeadlineInfo.isClosed ? 'text-red-700' : 'text-amber-700'}`}>
                        {responseDeadlineInfo.countdown}
                      </p>
                    </div>
                  )}
                  {selectedQuote.hs_code && (
                    <div>
                      <Label className="text-xs text-gray-500">HS code</Label>
                      <p className="font-semibold">{selectedQuote.hs_code}</p>
                    </div>
                  )}
                </div>

                {selectedQuote.attachments?.length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    {renderInquiryAttachments(selectedQuote.attachments)}
                  </div>
                )}

                {insuranceDisplay && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3">Insurance</h4>
                    <p className="text-sm">{insuranceDisplay}</p>
                  </div>
                )}

                {/* Keep the selected vendor quote visible after nomination, including in Closed.
                   Only the requester (quote owner) or the nominated bidder may see these details. */}
                {(selectedQuote.nominated_bid_id || selectedQuote.nominated_company || selectedQuote.nominated_name || selectedQuote.nominated_member_id) &&
                 (selectedQuoteIsOwnedByCurrentMember || selectedQuote.my_bid_nominated) && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                    <h4 className="font-semibold text-purple-800 flex items-center">
                      <CheckCircle size={18} className="mr-2 text-purple-600" />
                      {selectedQuote.status === 'Nominated' ? 'Nominated Quote' : 'Last Nominated Vendor Quote'}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-gray-500">Vendor</Label>
                        <p className="font-semibold text-gray-900">
                          {nominatedBidDetail?.bidder_company || selectedQuote.nominated_company || selectedQuote.nominated_member_id || 'Vendor'}
                          {(nominatedBidDetail?.bidder_name || selectedQuote.nominated_name)
                            ? ` (${nominatedBidDetail?.bidder_name || selectedQuote.nominated_name})`
                            : ((nominatedBidDetail?.bidder_company || selectedQuote.nominated_company) && (nominatedBidDetail?.bidder_id || selectedQuote.nominated_member_id))
                                ? ` (${nominatedBidDetail?.bidder_id || selectedQuote.nominated_member_id})`
                                : ''}
                        </p>
                        {(nominatedBidDetail?.bidder_id || selectedQuote.nominated_member_id) && (
                          <p className="text-xs text-gray-500">{nominatedBidDetail?.bidder_id || selectedQuote.nominated_member_id}</p>
                        )}
                      </div>
                      {(nominatedBidDetail?.bidder_name || selectedQuote.nominated_name) && (
                        <div>
                          <Label className="text-xs text-gray-500">Contact</Label>
                          <p className="font-medium text-gray-900">{nominatedBidDetail?.bidder_name || selectedQuote.nominated_name}</p>
                        </div>
                      )}
                    </div>

                    {nominatedBidLoading && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                        <Loader2 size={16} className="animate-spin" />
                        Loading nominated bid details...
                      </div>
                    )}

                    {!nominatedBidLoading && nominatedBidDetail && (
                      <>
                        {/* Line items / pricing */}
                        <div>
                          <Label className="text-xs text-gray-500 block mb-2">Pricing</Label>
                          {nominatedBidDetail.line_items && nominatedBidDetail.line_items.length > 0 ? (
                            <div className="bg-white rounded border overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b">
                                  <tr className="text-xs text-gray-500">
                                    <th className="px-3 py-2 text-left">Description</th>
                                    <th className="px-3 py-2 text-left">Unit</th>
                                    <th className="px-3 py-2 text-right">Qty</th>
                                    <th className="px-3 py-2 text-right">Unit Price</th>
                                    <th className="px-3 py-2 text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {nominatedBidDetail.line_items.map((item, idx) => (
                                    <tr key={idx} className={`border-b border-gray-100 ${item.is_at_actuals ? 'bg-amber-50/50' : ''}`}>
                                      <td className="px-3 py-2">{item.description}</td>
                                      <td className="px-3 py-2">{item.unit || '—'}</td>
                                      <td className="px-3 py-2 text-right">{item.quantity ?? 1}</td>
                                      <td className="px-3 py-2 text-right">${Number(item.unit_price || 0).toFixed(2)}</td>
                                      <td className="px-3 py-2 text-right font-medium">
                                        {item.is_at_actuals ? (
                                          <span className="text-amber-600">At Actuals</span>
                                        ) : (
                                          `$${((item.unit_price || 0) * (item.quantity ?? 1)).toFixed(2)}`
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="bg-gray-50 border-t font-medium">
                                  <tr>
                                    <td colSpan={4} className="px-3 py-2 text-right">Total</td>
                                    <td className="px-3 py-2 text-right text-purple-700">
                                      ${calculateTotals(nominatedBidDetail.line_items).total}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No line items.</p>
                          )}
                        </div>

                        {/* Validity */}
                        {(nominatedBidDetail.validity_days != null || nominatedBidDetail.quote_valid_until) && (
                          <div>
                            <Label className="text-xs text-gray-500">Validity</Label>
                            <p className="text-sm text-gray-900">
                              {nominatedBidDetail.quote_valid_until
                                ? `Valid until ${new Date(nominatedBidDetail.quote_valid_until).toLocaleDateString()}`
                                : `${nominatedBidDetail.validity_days ?? 30} days`}
                            </p>
                          </div>
                        )}

                        {/* Remarks / notes */}
                        {(nominatedBidDetail.remarks || nominatedBidDetail.notes) && (
                          <div>
                            <Label className="text-xs text-gray-500">Remarks</Label>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{nominatedBidDetail.remarks || nominatedBidDetail.notes}</p>
                          </div>
                        )}
                      </>
                    )}

                    {!nominatedBidLoading && !nominatedBidDetail && selectedQuote.nominated_bid_id && (
                      <p className="text-sm text-gray-500">Nominated vendor charge details could not be loaded.</p>
                    )}
                  </div>
                )}

                {/* Transport: Service Type */}
                {isTransport && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3">Service Type</h4>
                    <p className="font-medium">{fd.serviceType || 'Transport Only'}</p>
                  </div>
                )}

                {/* Transport: Pickup Location */}
                {isTransport && (pickup.address || pickup.city || pickup.country || selectedQuote.origin_city) && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center">
                      <MapPin size={16} className="mr-2 text-gray-500" />
                      Pickup Location
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {pickup.address && <div><Label className="text-xs text-gray-500">Address</Label><p>{pickup.address}</p></div>}
                      {(pickup.city || selectedQuote.origin_city) && <div><Label className="text-xs text-gray-500">City</Label><p>{pickup.city || selectedQuote.origin_city}</p></div>}
                      {pickup.country && <div><Label className="text-xs text-gray-500">Country</Label><p>{pickup.country}</p></div>}
                      {pickup.postalCode && <div><Label className="text-xs text-gray-500">Postal Code</Label><p>{pickup.postalCode}</p></div>}
                      {fd.pickupDate && <div><Label className="text-xs text-gray-500">Pickup Date</Label><p>{fd.pickupDate}</p></div>}
                    </div>
                  </div>
                )}

                {/* Transport: Delivery Location */}
                {isTransport && (delivery.address || delivery.city || delivery.country || selectedQuote.destination_city) && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center">
                      <MapPin size={16} className="mr-2 text-gray-500" />
                      Delivery Location
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {delivery.address && <div><Label className="text-xs text-gray-500">Address</Label><p>{delivery.address}</p></div>}
                      {(delivery.city || selectedQuote.destination_city) && <div><Label className="text-xs text-gray-500">City</Label><p>{delivery.city || selectedQuote.destination_city}</p></div>}
                      {delivery.country && <div><Label className="text-xs text-gray-500">Country</Label><p>{delivery.country}</p></div>}
                      {delivery.postalCode && <div><Label className="text-xs text-gray-500">Postal Code</Label><p>{delivery.postalCode}</p></div>}
                      {fd.deliveryDate && <div><Label className="text-xs text-gray-500">Delivery Date</Label><p>{fd.deliveryDate}</p></div>}
                    </div>
                  </div>
                )}

                {/* Transport: Cargo Details */}
                {isTransport && (fd.cargoDetails?.length > 0 || fd.vehicleType || fd.loadType || selectedQuote.commodity || fd.commodity || fd.cargoWeight || selectedQuote.weight_kg || fd.cargoVolume || selectedQuote.volume_cbm || hasDimensions) && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center">
                      <Package size={16} className="mr-2 text-gray-500" />
                      Cargo Details
                    </h4>
                    {fd.cargoDetails?.length > 0 ? (
                      <div className="space-y-3">
                        {fd.cargoDetails.map((item, idx) => (
                          <div key={idx} className="p-3 bg-white rounded border text-sm">
                            <p className="font-medium text-gray-700 mb-2">Item {idx + 1}</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {item.vehicleType && <span>Vehicle: {item.vehicleType}</span>}
                              {item.subVehicleType && <span>Sub Vehicle: {item.subVehicleType}</span>}
                              {item.loadType && <span>Load: {item.loadType}</span>}
                              {item.commodity && <span>Commodity: {item.commodity}</span>}
                              {item.cargoWeight && <span>Weight: {item.cargoWeight} kg</span>}
                              {item.cargoVolume && <span>Vol: {item.cargoVolume} m³</span>}
                            </div>
                            {item.dimensions && (item.dimensions.length || item.dimensions.width || item.dimensions.height) && (
                              <p className="text-xs text-gray-500 mt-2">Dimensions: {item.dimensions.length || '-'} × {item.dimensions.width || '-'} × {item.dimensions.height || '-'} {item.dimensions.unit || 'cm'}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          {fd.vehicleType && <div><Label className="text-xs text-gray-500">Vehicle Type</Label><p>{fd.vehicleType}</p></div>}
                          {fd.subVehicleType && <div><Label className="text-xs text-gray-500">Sub Vehicle Type</Label><p>{fd.subVehicleType}</p></div>}
                          {fd.loadType && <div><Label className="text-xs text-gray-500">Load Type</Label><p>{fd.loadType}</p></div>}
                          {(selectedQuote.commodity || fd.commodity) && <div><Label className="text-xs text-gray-500">Commodity</Label><p>{selectedQuote.commodity || fd.commodity}</p></div>}
                          {(fd.cargoWeight || selectedQuote.weight_kg) && <div><Label className="text-xs text-gray-500">Total Weight (kg)</Label><p>{fd.cargoWeight || selectedQuote.weight_kg}</p></div>}
                          {(fd.cargoVolume || selectedQuote.volume_cbm) && <div><Label className="text-xs text-gray-500">Volume (m³)</Label><p>{fd.cargoVolume || selectedQuote.volume_cbm}</p></div>}
                        </div>
                        {renderQuoteDimensions(fd, 'cm')}
                      </>
                    )}
                  </div>
                )}

                {/* Transport: Special Requirements & Additional Notes */}
                {isTransport && (fd.specialRequirements?.length > 0 || fd.temperatureControl || fd.insuranceRequired || fd.notes || selectedQuote.cargo_description) && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3">Special Requirements & Notes</h4>
                    {fd.specialRequirements?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {fd.specialRequirements.map((req, i) => (
                          <span key={i} className="inline-flex px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">{req}</span>
                        ))}
                      </div>
                    )}
                    {fd.temperatureControl && <p className="text-sm">Temperature Controlled: {fd.tempRange || 'Yes'}</p>}
                    {fd.insuranceRequired && <p className="text-sm">Insurance {insuranceDisplay}</p>}
                    {(fd.notes || selectedQuote.cargo_description) && (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2">{fd.notes || selectedQuote.cargo_description}</p>
                    )}
                  </div>
                )}

                {/* Transport: Customs Clearance */}
                {isTransport && fd.requiresCustomsClearance && fd.customsDetails && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3">Customs Clearance Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {fd.customsDetails.exportCountry && <div><Label className="text-xs text-gray-500">Export Country</Label><p>{fd.customsDetails.exportCountry}</p></div>}
                      {fd.customsDetails.importCountry && <div><Label className="text-xs text-gray-500">Import Country</Label><p>{fd.customsDetails.importCountry}</p></div>}
                      {fd.customsDetails.hsCode && <div><Label className="text-xs text-gray-500">HS Code</Label><p>{fd.customsDetails.hsCode}</p></div>}
                      {fd.customsDetails.customsValue && <div><Label className="text-xs text-gray-500">Invoice Value (USD)</Label><p>{fd.customsDetails.customsValue}</p></div>}
                    </div>
                  </div>
                )}

                {/* Air: Scope & Route */}
                {isAir && (
                  <>
                    {(scope.length > 0 || scopeDisplayOptions.length > 0) && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-3">Scope (Select all that apply)</h4>
                        <div className="flex flex-wrap gap-2">
                          {scopeDisplayOptions.length > 0
                            ? scopeDisplayOptions.map((opt) => (
                                <span key={opt} className={`inline-flex items-center px-3 py-1 rounded text-sm ${scope.includes(opt) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
                                  {scope.includes(opt) ? '✓' : '○'} {opt}
                                </span>
                              ))
                            : scope.map((s) => (
                                <span key={s} className="inline-flex items-center px-3 py-1 rounded text-sm bg-blue-100 text-blue-800">✓ {s}</span>
                              ))}
                        </div>
                      </div>
                    )}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Route Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {formatAirportDisplay(fd.origin || selectedQuote.origin_city) && <div><Label className="text-xs text-gray-500">Origin Airport</Label><p>{formatAirportDisplay(fd.origin || selectedQuote.origin_city)}</p></div>}
                        {formatAirportDisplay(fd.destination || selectedQuote.destination_city) && <div><Label className="text-xs text-gray-500">Destination Airport</Label><p>{formatAirportDisplay(fd.destination || selectedQuote.destination_city)}</p></div>}
                        {fd.pickupAddress && <div className="md:col-span-2"><Label className="text-xs text-gray-500">Pick up Address</Label><p>{fd.pickupAddress}</p></div>}
                        {fd.deliveryAddress && <div className="md:col-span-2"><Label className="text-xs text-gray-500">Destination Address</Label><p>{fd.deliveryAddress}</p></div>}
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3 flex items-center"><Package size={16} className="mr-2 text-gray-500" />Cargo Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {(selectedQuote.commodity || fd.commodity) && <div><Label className="text-xs text-gray-500">Commodity</Label><p>{selectedQuote.commodity || fd.commodity}</p></div>}
                        {fd.cargoType && <div><Label className="text-xs text-gray-500">Cargo Type</Label><p>{fd.cargoType}</p></div>}
                        {(fd.totalWeight || selectedQuote.weight_kg) && <div><Label className="text-xs text-gray-500">Total Weight</Label><p>{fd.totalWeight || selectedQuote.weight_kg} {fd.weightUnit || 'kg'}</p></div>}
                        {fd.pieces && <div><Label className="text-xs text-gray-500">Pieces</Label><p>{fd.pieces}</p></div>}
                        {fd.serviceLevel && <div><Label className="text-xs text-gray-500">Service Level</Label><p>{fd.serviceLevel}</p></div>}
                        {fd.incoterm && <div><Label className="text-xs text-gray-500">Incoterm</Label><p>{Array.isArray(fd.incoterm) ? fd.incoterm.join(', ') : fd.incoterm}</p></div>}
                      </div>
                      {renderQuoteDimensions(fd, 'cm')}
                      {fd.specialHandling?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{fd.specialHandling.map((h, i) => <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">{h}</span>)}</div>}
                      {fd.specialHandling?.includes('Temperature Controlled') && fd.temperatureRange && (
                        <p className="text-sm mt-2"><span className="text-gray-500">Temperature range required:</span> {fd.temperatureRange}</p>
                      )}
                      {fd.insuranceRequired && <p className="text-sm mt-2">Insurance {insuranceDisplay}</p>}
                      {(fd.specialRequirements || selectedQuote.cargo_description) && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2">{fd.specialRequirements || selectedQuote.cargo_description}</p>}
                    </div>
                  </>
                )}

                {/* Warehousing */}
                {isWarehousing && (
                  <>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Warehouse Location</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {fd.location?.city && <div><Label className="text-xs text-gray-500">City</Label><p>{fd.location.city}</p></div>}
                        {fd.location?.country && <div><Label className="text-xs text-gray-500">Country</Label><p>{fd.location.country}</p></div>}
                        {fd.location?.address && <div className="md:col-span-2"><Label className="text-xs text-gray-500">Address</Label><p>{fd.location.address}</p></div>}
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3">Space Requirements</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {fd.startDate && <div><Label className="text-xs text-gray-500">Start Date</Label><p>{fd.startDate}</p></div>}
                        {fd.duration && <div><Label className="text-xs text-gray-500">Duration</Label><p>{fd.duration} {fd.durationUnit || 'months'}</p></div>}
                        {fd.spaceRequired && <div><Label className="text-xs text-gray-500">Space Required</Label><p>{fd.spaceRequired} {fd.spaceUnit || 'sqm'}</p></div>}
                        {fd.storageType && <div><Label className="text-xs text-gray-500">Storage Type</Label><p>{fd.storageType}</p></div>}
                        {(selectedQuote.commodity || fd.commodity) && <div><Label className="text-xs text-gray-500">Commodity</Label><p>{selectedQuote.commodity || fd.commodity}</p></div>}
                        {fd.palletCount && <div><Label className="text-xs text-gray-500">Pallet Count</Label><p>{fd.palletCount} ({fd.palletType || 'Euro'})</p></div>}
                      </div>
                    </div>
                    {packingListInfo && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-3">Documents</h4>
                        <a
                          href={packingListInfo.url}
                          download={packingListInfo.name}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          <Download size={14} className="mr-2" />
                          {packingListInfo.name}
                        </a>
                      </div>
                    )}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3">Cargo & Environment</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {fd.isStackable && <div><Label className="text-xs text-gray-500">Stackable</Label><p>{fd.isStackable}</p></div>}
                        {fd.stackHeight && <div><Label className="text-xs text-gray-500">Stack Height (m)</Label><p>{fd.stackHeight}</p></div>}
                        {fd.securityLevel && <div><Label className="text-xs text-gray-500">Security Level</Label><p>{fd.securityLevel}</p></div>}
                        {fd.temperatureControlled && <div><Label className="text-xs text-gray-500">Temperature Controlled</Label><p>{fd.tempRange || 'Yes'}</p></div>}
                        {fd.humidityControlled && <div><Label className="text-xs text-gray-500">Humidity Controlled</Label><p>{fd.humidityRange || 'Yes'}</p></div>}
                        {(fd.isHazardous || selectedQuote.is_hazardous) && <div><Label className="text-xs text-gray-500">Hazardous</Label><p>{fd.hazClass || selectedQuote.hazardous_class || 'Yes'}</p></div>}
                      </div>
                      {renderHazardousFileDownloads(fd.msdsFiles, selectedQuote.msdsFiles, selectedQuote.msds)}
                      {(fd.accessRequirements?.length > 0 || fd.handlingServices?.length > 0) && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {[...(fd.accessRequirements || []), ...(fd.handlingServices || [])].map((r, i) => <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">{r}</span>)}
                        </div>
                      )}
                      {(fd.specialRequirements || selectedQuote.cargo_description) && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-4">{fd.specialRequirements || selectedQuote.cargo_description}</p>}
                    </div>
                  </>
                )}

                {/* Breakbulk */}
                {isBreakbulk && (
                  <>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3">Cargo Type</h4>
                      <p className="font-medium">{fd.isRoRo ? 'Ro-Ro (Roll-on/Roll-off)' : 'Breakbulk Cargo'}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3 flex items-center"><MapPin size={16} className="mr-2 text-gray-500" />Route</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {fd.origin?.port && <div><Label className="text-xs text-gray-500">Origin Port</Label><p>{fd.origin.port} {fd.origin.country && `(${fd.origin.country})`}</p></div>}
                        {fd.destination?.port && <div><Label className="text-xs text-gray-500">Destination Port</Label><p>{fd.destination.port} {fd.destination.country && `(${fd.destination.country})`}</p></div>}
                      </div>
                    </div>
                    {fd.isRoRo ? (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-3">Ro-Ro Vehicle Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {fd.roRoDetails?.vehicleType && <div><Label className="text-xs text-gray-500">Vehicle Type</Label><p>{fd.roRoDetails.vehicleType}</p></div>}
                          {fd.roRoDetails?.quantity && <div><Label className="text-xs text-gray-500">Quantity</Label><p>{fd.roRoDetails.quantity}</p></div>}
                          {fd.roRoDetails?.make && <div><Label className="text-xs text-gray-500">Make</Label><p>{fd.roRoDetails.make}</p></div>}
                          {fd.roRoDetails?.model && <div><Label className="text-xs text-gray-500">Model</Label><p>{fd.roRoDetails.model}</p></div>}
                          {fd.roRoDetails?.running && <div><Label className="text-xs text-gray-500">Running</Label><p>{fd.roRoDetails.running}</p></div>}
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-3">Cargo Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {(selectedQuote.commodity || fd.commodity) && <div><Label className="text-xs text-gray-500">Commodity</Label><p>{selectedQuote.commodity || fd.commodity}</p></div>}
                          {fd.cargoDescription && <div className="md:col-span-2"><Label className="text-xs text-gray-500">Cargo Description</Label><p className="whitespace-pre-wrap">{fd.cargoDescription}</p></div>}
                          {fd.pieces && <div><Label className="text-xs text-gray-500">Pieces</Label><p>{fd.pieces}</p></div>}
                          {(fd.totalWeight || selectedQuote.weight_kg) && <div><Label className="text-xs text-gray-500">Total Weight (MT)</Label><p>{fd.totalWeight || selectedQuote.weight_kg}</p></div>}
                          {fd.packaging && <div><Label className="text-xs text-gray-500">Packaging</Label><p>{fd.packaging}</p></div>}
                          {fd.stowagePreference && <div><Label className="text-xs text-gray-500">Stowage Preference</Label><p>{fd.stowagePreference}</p></div>}
                        </div>
                        {renderQuoteDimensions(fd, 'm')}
                      </div>
                    )}
                    {(fd.specialHandling?.length > 0 || fd.lashingRequired || fd.insuranceRequired || fd.notes || selectedQuote.cargo_description) && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-3">Stowage & Notes</h4>
                        {fd.specialHandling?.length > 0 && <div className="flex flex-wrap gap-2 mb-2">{fd.specialHandling.map((h, i) => <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">{h}</span>)}</div>}
                        {fd.lashingRequired && <p className="text-sm">Lashing Required</p>}
                        {fd.insuranceRequired && <p className="text-sm">Insurance {insuranceDisplay}</p>}
                        {(fd.notes || selectedQuote.cargo_description) && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2">{fd.notes || selectedQuote.cargo_description}</p>}
                      </div>
                    )}
                  </>
                )}

                {/* Ocean only: Scope */}
                {isOcean && (scope.length > 0 || scopeDisplayOptions.length > 0) && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3">Scope (Select all that apply)</h4>
                    <div className="flex flex-wrap gap-2">
                      {scopeDisplayOptions.length > 0
                        ? scopeDisplayOptions.map((opt) => (
                            <span
                              key={opt}
                              className={`inline-flex items-center px-3 py-1 rounded text-sm ${scope.includes(opt) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}
                            >
                              {scope.includes(opt) ? '✓' : '○'} {opt}
                            </span>
                          ))
                        : scope.map((s) => (
                            <span key={s} className="inline-flex items-center px-3 py-1 rounded text-sm bg-blue-100 text-blue-800">✓ {s}</span>
                          ))}
                    </div>
                  </div>
                )}

                {/* Ocean only: Port Details */}
                {isOcean && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center">
                      <MapPin size={16} className="mr-2 text-gray-500" />
                      Port Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {(fd.por || selectedQuote.por) && (
                        <div><Label className="text-xs text-gray-500">POR (Place of Receipt)</Label><p>{formatPortDisplay(fd.porName, fd.por || selectedQuote.por)} {fd.porCountry && `— ${fd.porCountry}`}</p></div>
                      )}
                      <div><Label className="text-xs text-gray-500">POL (Port of Loading)</Label><p>{formatPortDisplay(selectedQuote.origin_port?.name, selectedQuote.origin_port?.code) || formatPortDisplay(fd.polName, fd.pol) || 'N/A'}</p></div>
                      <div><Label className="text-xs text-gray-500">POD (Port of Discharge)</Label><p>{formatPortDisplay(selectedQuote.destination_port?.name, selectedQuote.destination_port?.code) || formatPortDisplay(fd.podName, fd.pod) || 'N/A'}</p></div>
                      {(fd.fpod || selectedQuote.fpod) && (
                        <div><Label className="text-xs text-gray-500">FPOD (Final Place of Delivery)</Label><p>{formatPortDisplay(fd.fpodName, fd.fpod || selectedQuote.fpod)} {fd.fpodCountry && `— ${fd.fpodCountry}`}</p></div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 text-sm">
                      {selectedQuote.incoterm && <div><Label className="text-xs text-gray-500">Incoterm</Label><p className="font-medium">{Array.isArray(selectedQuote.incoterm) ? selectedQuote.incoterm.join(', ') : selectedQuote.incoterm}</p></div>}
                      {(fd.polFreeTime || selectedQuote.polFreeTime) && <div><Label className="text-xs text-gray-500">POL Free Time (days)</Label><p>{fd.polFreeTime || selectedQuote.polFreeTime}</p></div>}
                      {(fd.podFreeTime || selectedQuote.podFreeTime) && <div><Label className="text-xs text-gray-500">POD Free Time (days)</Label><p>{fd.podFreeTime || selectedQuote.podFreeTime}</p></div>}
                      {(fd.blType || selectedQuote.blType) && <div><Label className="text-xs text-gray-500">BL Type</Label><p>{fd.blType || selectedQuote.blType}</p></div>}
                      {(fd.blFormat || selectedQuote.blFormat) && <div><Label className="text-xs text-gray-500">BL Format</Label><p>{fd.blFormat || selectedQuote.blFormat}</p></div>}
                    </div>
                  </div>
                )}

                {/* Ocean FCL only: Standard Containers */}
                {isOceanFCL && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center">
                      <Ship size={16} className="mr-2 text-gray-500" />
                      Standard Containers
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      {standardContainerTypes.map((type) => {
                        const containerEntry = Array.isArray(containersObj)
                          ? containersObj.find(c => c.container_type === type)
                          : null;
                        const qty = containersObj && typeof containersObj === 'object' && !Array.isArray(containersObj)
                          ? containersObj[type] ?? 0
                          : containerEntry?.quantity ?? 0;
                        const weightKg = fd.containerWeights?.[type] || containerEntry?.weight_kg || '';
                        return (
                          <div key={type}>
                            <Label className="text-xs text-gray-500">{type}</Label>
                            <p className="font-medium">{qty}</p>
                            {String(weightKg).trim() !== '' && (
                              <p className="text-sm text-gray-600 mt-1">{weightKg} kg</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Ocean LCL: Cargo Details */}
                {isOceanLCL && (fd.cargoDetails?.length > 0 || selectedQuote.commodity || fd.commodity || selectedQuote.cargo_description) && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center"><Package size={16} className="mr-2 text-gray-500" />Cargo Details</h4>
                    {fd.cargoDetails?.length > 0 ? (
                      <div className="space-y-3">
                        {fd.cargoDetails.map((cargo, idx) => (
                          <OceanLclCargoCard key={idx} cargo={cargo} />
                        ))}
                      </div>
                    ) : null}
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {Boolean(fd.totalWeight || fd.totalVolume) && (
                      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                        Total: {fd.totalWeight || 0} kg / {fd.totalVolume || 0} CBM
                      </div>
                    )}
                      {fd.containerSharing && (
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                          Container Sharing: {fd.containerSharing}
                        </div>
                      )}
                      {fd.packagingType && (
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                          Packaging: {fd.packagingType}
                        </div>
                      )}
                      {(selectedQuote.commodity || fd.commodity) && (
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                          Commodity: {selectedQuote.commodity || fd.commodity}
                        </div>
                      )}
                    </div>
                    {Boolean(fd.specialHandling?.length) && <div className="mt-3 flex flex-wrap gap-2">{fd.specialHandling.map((h, i) => <span key={i} className="rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800">{h}</span>)}</div>}
                    {(fd.specialHandling?.includes('Fragile')) && (
                      <p className="text-sm mt-2 text-gray-700">
                        <span className="text-gray-500">Fragile applies to:</span>{' '}
                        {fd.fragileHandling === 'specific'
                          ? `Specific — ${fd.fragileBoxCount || '—'} box(es)`
                          : 'All cargo boxes'}
                      </p>
                    )}
                    {(fd.specialHandling?.includes('Temperature Sensitive') && fd.temperatureRange) && (
                      <p className="text-sm mt-2"><span className="text-gray-500">Temperature range required:</span> {fd.temperatureRange}</p>
                    )}
                    {(fd.isHazardous || selectedQuote.is_hazardous) && <p className="text-sm">Hazardous: {fd.hazClass || selectedQuote.hazardous_class || 'Yes'}</p>}
                    {renderHazardousFileDownloads(fd.msdsFiles, selectedQuote.msdsFiles, selectedQuote.msds)}
                    {getLclNotesText(selectedQuote) && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-3">{getLclNotesText(selectedQuote)}</p>}
                  </div>
                )}

                {/* Ocean FCL only: Special Containers */}
                {isOceanFCL && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3">Special Containers</h4>
                    {oogList.length > 0 ? (
                      <div className="space-y-4">
                        {oogList.map((container, idx) => {
                          const sameType = oogList.filter(c => c.type === container.type);
                          const displayCount = sameType.indexOf(container) + 1;
                          const attachments = container.attachments || [];
                          const apiBase = import.meta.env?.VITE_API_BASE || process.env.REACT_APP_BACKEND_URL || window.location.origin;
                          return (
                            <div key={container.id || idx} className="p-4 bg-amber-50 rounded border border-amber-200 text-sm space-y-3">
                              <div className="flex items-center justify-between border-b border-amber-200 pb-2">
                                <span className="font-medium">{container.type} Container<br /><span className="text-xs font-normal text-gray-600">Count-{displayCount}</span></span>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div><Label className="text-xs text-gray-500">Gauge Type</Label><p>{container.gauge === 'out' ? 'Out-Gauge' : 'In-Gauge'}</p></div>
                                {container.gauge === 'out' && (
                                  <>
                                    <div><Label className="text-xs text-gray-500">Length</Label><p>{container.length ?? '-'}</p></div>
                                    <div><Label className="text-xs text-gray-500">Width</Label><p>{container.width ?? '-'}</p></div>
                                    <div><Label className="text-xs text-gray-500">Height</Label><p>{container.height ?? '-'}</p></div>
                                    <div><Label className="text-xs text-gray-500">Unit</Label><p>{container.unit || 'mm'}</p></div>
                                  </>
                                )}
                                <div><Label className="text-xs text-gray-500">Cargo Weight (kg)</Label><p>{container.weight ?? '-'}</p></div>
                                <div className="md:col-span-2"><Label className="text-xs text-gray-500">Cargo Description</Label><p>{container.cargoDescription || '-'}</p></div>
                              </div>
                              {attachments.length > 0 && (
                                <div className="pt-2 border-t border-amber-200">
                                  <Label className="text-xs text-gray-500 block mb-2">Attached files</Label>
                                  <ul className="space-y-1">
                                    {attachments.map((att, ai) => {
                                      const url = typeof att === 'string' ? att : att?.url;
                                      const name = (typeof att === 'object' && att?.original_filename) ? att.original_filename : (url && url.split('/').pop()) || `File ${ai + 1}`;
                                      const fullUrl = url && (url.startsWith('http') ? url : `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`);
                                      return (
                                        <li key={ai} className="flex items-center justify-between gap-2 py-1">
                                          <span className="truncate text-xs">{name}</span>
                                          <span className="flex gap-1 shrink-0">
                                            <a href={fullUrl} download={name} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-200" title="Download"><Download size={14} /></a>
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No Special containers added.</p>
                    )}
                  </div>
                )}

                {/* Ocean FCL only: Reefer & Special Cargo */}
                {isOceanFCL && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3">Reefer &amp; Special Cargo</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-gray-500">20&apos; Reefer</Label>
                        <p>{reefer['20RF'] || reefer['20RF'] === 0 ? reefer['20RF'] : '0'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Temp Range (20&apos;)</Label>
                        <p>{(fd.tempRange20 || selectedQuote.tempRange20) || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">40&apos; Reefer</Label>
                        <p>{reefer['40RF'] || reefer['40RF'] === 0 ? reefer['40RF'] : '0'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Temp Range (40&apos;)</Label>
                        <p>{(fd.tempRange40 || selectedQuote.tempRange40) || '-'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                      <div>
                        <Label className="text-xs text-gray-500">Commodity</Label>
                        <p>{selectedQuote.commodity || fd.commodity || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Hazardous Cargo</Label>
                        <p>{(selectedQuote.is_hazardous || selectedQuote.isHaz || fd.isHaz) ? 'Hazardous' : 'Non-Hazardous'}</p>
                        {(selectedQuote.is_hazardous || selectedQuote.isHaz || fd.isHaz) && (selectedQuote.hazardous_class || selectedQuote.hazClass || fd.hazClass) && (
                          <p className="text-xs text-red-600 mt-1">Class: {selectedQuote.hazardous_class || selectedQuote.hazClass || fd.hazClass}</p>
                        )}
                      </div>
                    </div>
                    {renderHazardousFileDownloads(fd.msdsFiles, selectedQuote.msdsFiles, selectedQuote.msds)}
                    {(selectedQuote.cargo_description || selectedQuote.special_requirements || fd.specialRequirements) && (
                      <div className="mt-4">
                        <Label className="text-xs text-gray-500">Special Requirements</Label>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">
                          {selectedQuote.cargo_description || selectedQuote.special_requirements || fd.specialRequirements}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Requirements - hidden in Nominated tab View Details */}
                {selectedQuote.status !== 'Nominated' && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center">
                      <DollarSign size={16} className="mr-2 text-gray-500" />
                      Requirements
                    </h4>
                    {selectedQuote.sender_line_items && selectedQuote.sender_line_items.length > 0 ? (
                      <table className="w-full text-sm">
                        <thead className="bg-white">
                          <tr className="text-xs text-gray-500 border-b">
                            <th className="px-2 py-2 text-left">Description</th>
                            <th className="px-2 py-2 text-left">Unit</th>
                            <th className="px-2 py-2 text-right">Qty</th>
                            <th className="px-2 py-2 text-right">Unit Price</th>
                            <th className="px-2 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedQuote.sender_line_items.map((item, idx) => (
                            <tr key={idx} className={`border-b ${item.is_at_actuals ? 'bg-amber-50' : ''}`}>
                              <td className="px-2 py-2">{item.description}</td>
                              <td className="px-2 py-2">{item.unit}</td>
                              <td className="px-2 py-2 text-right">{item.quantity}</td>
                              <td className="px-2 py-2 text-right">${(item.unit_price || 0).toFixed(2)}</td>
                              <td className="px-2 py-2 text-right font-medium">
                                {item.is_at_actuals ? (
                                  <span className="text-amber-600">At Actuals</span>
                                ) : (
                                  `$${((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}`
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-white border-t font-medium">
                          <tr>
                            <td colSpan={4} className="px-2 py-2 text-right">Total:</td>
                            <td className="px-2 py-2 text-right text-blue-600">
                              ${calculateTotals(selectedQuote.sender_line_items).total}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      <p className="text-sm text-gray-500">No charge items added yet.</p>
                    )}
                  </div>
                )}

                {selectedQuote.has_submitted_bid && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold mb-3 flex items-center text-purple-800">
                      <DollarSign size={16} className="mr-2 text-purple-600" />
                      Your Submitted Charges
                    </h4>
                    {submittedBidLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                        <Loader2 size={16} className="animate-spin" />
                        Loading submitted quotation...
                      </div>
                    ) : submittedBidDetail?.line_items?.length > 0 ? (
                      <table className="w-full text-sm">
                        <thead className="bg-white">
                          <tr className="text-xs text-gray-500 border-b">
                            <th className="px-2 py-2 text-left">Description</th>
                            <th className="px-2 py-2 text-left">Unit</th>
                            <th className="px-2 py-2 text-right">Qty</th>
                            <th className="px-2 py-2 text-right">Unit Price</th>
                            <th className="px-2 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {submittedBidDetail.line_items.map((item, idx) => (
                            <tr key={idx} className={`border-b ${item.is_at_actuals ? 'bg-amber-50' : ''}`}>
                              <td className="px-2 py-2">{item.description}</td>
                              <td className="px-2 py-2">{item.unit || '—'}</td>
                              <td className="px-2 py-2 text-right">{item.quantity ?? 1}</td>
                              <td className="px-2 py-2 text-right">${Number(item.unit_price || 0).toFixed(2)}</td>
                              <td className="px-2 py-2 text-right font-medium">
                                {item.is_at_actuals ? (
                                  <span className="text-amber-600">At Actuals</span>
                                ) : (
                                  `$${((item.unit_price || 0) * (item.quantity ?? 1)).toFixed(2)}`
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-white border-t font-medium">
                          <tr>
                            <td colSpan={4} className="px-2 py-2 text-right">Total:</td>
                            <td className="px-2 py-2 text-right text-purple-700">
                              ${calculateTotals(submittedBidDetail.line_items).total}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      <p className="text-sm text-gray-500">No submitted charge items found.</p>
                    )}
                  </div>
                )}

                {/* Special Instructions */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center text-blue-800">
                    <FileText size={16} className="mr-2" />
                    Special Instructions
                  </h4>
                  {selectedQuote.special_instructions && selectedQuote.special_instructions.length > 0 ? (
                    <div className="space-y-2">
                      {selectedQuote.special_instructions.map((instruction, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <span className="font-medium text-gray-600">
                            {instruction.instruction_number || `1.${idx + 1}`}:
                          </span>
                          <span>{instruction.instruction_text}</span>
                          {instruction.is_mandatory && (
                            <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No special instructions added.</p>
                  )}
                </div>

                {/* Responses Summary (hidden in Nominated tab View Details per request) */}
                {selectedQuote.status !== 'Nominated' && (
                  <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600">
                        <strong>{selectedQuote.responses_count || 0}</strong> response(s) received
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {/* Chat Button */}
                      {!selectedQuoteIsOwnedByCurrentMember && !selectedQuoteIsTeamEnquiry && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowQuoteDetailDialog(false);
                            handleOpenQuoteChat(selectedQuote);
                          }}
                        >
                          <MessageSquare size={14} className="mr-1" />
                          Message Requester
                        </Button>
                      )}
                      {/* Compare/View Bids Button (only for requester; hidden for closed records) */}
                      {selectedQuoteIsOwnedByCurrentMember && (selectedQuote.responses_count || 0) > 0 && !['Completed', 'Cancelled', 'Expired'].includes(selectedQuote.status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-purple-600 border-purple-300"
                          onClick={() => {
                            setShowQuoteDetailDialog(false);
                            handleOpenBidComparison(selectedQuote);
                          }}
                        >
                          <Scale size={14} className="mr-1" />
                          {['Nominated', 'Completed'].includes(selectedQuote.status) ? 'View Bids' : 'Compare quote'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons - for Nominated tab view details: show Close + Requester actions (Mail Reminder, Message, etc.) */}
                <div className="flex justify-end flex-wrap gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowQuoteDetailDialog(false)}>
                    Close
                  </Button>
                  {/* Requester: Mail Reminder, Message, Accept Shipment Completion, Pay Now, Complete - show including after Shipment Completed */}
                  {selectedQuote.status === 'Nominated' && selectedQuoteIsOwnedByCurrentMember && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={reminderLoadingQuoteId === selectedQuote.quote_id}
                        onClick={async () => {
                          setReminderLoadingQuoteId(selectedQuote.quote_id);
                          try {
                            await quotesApi.sendQuoteReminder(selectedQuote.quote_id, currentMemberId);
                            toast.success('Reminder sent');
                          } catch (error) {
                            toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send reminder'));
                          } finally {
                            setReminderLoadingQuoteId(null);
                          }
                        }}
                      >
                        {reminderLoadingQuoteId === selectedQuote.quote_id ? (
                          <>
                            <Loader2 size={14} className="mr-1 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={14} className="mr-1" />
                            Mail Reminder
                          </>
                        )}
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to="/chat"
                          state={{
                            openQuoteChat: {
                              quote_id: selectedQuote.quote_id,
                              quote_reference: selectedQuote.reference_number || selectedQuote.quote_id,
                              member_id: selectedQuote.nominated_member_id,
                              company_name: selectedQuote.nominated_company || 'Quoted by',
                              other_participant_name: selectedQuote.nominated_name || selectedQuote.nominated_company || 'Quoted by'
                            }
                          }}
                          onClick={() => setShowQuoteDetailDialog(false)}
                        >
                          <MessageSquare size={14} className="mr-1" />
                          Message
                        </Link>
                      </Button>
                      {selectedQuote.rfq_completed_at && !selectedQuote.rfq_accepted_at && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={rfqReopenLoadingQuoteId === selectedQuote.quote_id || rfqAcceptLoadingQuoteId === selectedQuote.quote_id}
                            onClick={async () => {
                              setRfqReopenLoadingQuoteId(selectedQuote.quote_id);
                              try {
                                await quotesApi.rfqReopen(selectedQuote.quote_id, currentMemberId);
                                toast.success('RFQ reopened');
                                setShowQuoteDetailDialog(false);
                                fetchData();
                              } catch (error) {
                                toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to reopen RFQ'));
                              } finally {
                                setRfqReopenLoadingQuoteId(null);
                              }
                            }}
                          >
                            {rfqReopenLoadingQuoteId === selectedQuote.quote_id ? (
                              <>
                                <Loader2 size={14} className="mr-1 animate-spin" />
                                Reopening...
                              </>
                            ) : (
                              <>
                                <RotateCcw size={14} className="mr-1" />
                                Reopen
                              </>
                            )}
                          </Button>
                          <Button
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            size="sm"
                            disabled={rfqAcceptLoadingQuoteId === selectedQuote.quote_id || rfqReopenLoadingQuoteId === selectedQuote.quote_id}
                            onClick={async () => {
                              setRfqAcceptLoadingQuoteId(selectedQuote.quote_id);
                              try {
                                await quotesApi.rfqAccept(selectedQuote.quote_id, currentMemberId);
                                toast.success('Shipment completion accepted');
                                setShowQuoteDetailDialog(false);
                                fetchData();
                              } catch (error) {
                                toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to accept RFQ'));
                              } finally {
                                setRfqAcceptLoadingQuoteId(null);
                              }
                            }}
                          >
                            {rfqAcceptLoadingQuoteId === selectedQuote.quote_id ? (
                              <>
                                <Loader2 size={14} className="mr-1 animate-spin" />
                                Accepting...
                              </>
                            ) : (
                              <>
                                <CheckCircle size={14} className="mr-1" />
                                Accept Shipment Completion
                              </>
                            )}
                          </Button>
                        </>
                      )}
                      {selectedQuote.rfq_accepted_at && (selectedQuote.payment_details || selectedQuote.quote_payment_id) && !selectedQuote.payment_submitted_at && (
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          size="sm"
                          onClick={() => {
                            setShowQuoteDetailDialog(false);
                            setShowPayNowDialog(true);
                          }}
                        >
                          <DollarSign size={14} className="mr-1" />
                          Pay Now
                        </Button>
                      )}
                      {selectedQuote.payment_submitted_at && selectedQuote.payment_received_at && (
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          size="sm"
                          onClick={() => {
                            setShowQuoteDetailDialog(false);
                            setCompletionForm({ rating: 5, feedback: '' });
                            setShowCompletionDialog(true);
                          }}
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Complete
                        </Button>
                      )}
                    </>
                  )}
                  {/* Receiver: Shipment Completed, Request Payment Details, Payment Received */}
                  {selectedQuote.status === 'Nominated' && selectedQuote.my_bid_nominated && !selectedQuoteIsOwnedByCurrentMember && !selectedQuoteIsTeamEnquiry && (
                    <>
                      {!selectedQuote.rfq_completed_at && (
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          size="sm"
                          disabled={rfqCompleteLoadingQuoteId === selectedQuote.quote_id}
                          onClick={async () => {
                            setRfqCompleteLoadingQuoteId(selectedQuote.quote_id);
                            try {
                              await quotesApi.rfqComplete(selectedQuote.quote_id, currentMemberId);
                              toast.success('Shipment marked as completed');
                              setShowQuoteDetailDialog(false);
                              fetchData();
                            } catch (error) {
                              toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to complete RFQ'));
                            } finally {
                              setRfqCompleteLoadingQuoteId(null);
                            }
                          }}
                        >
                          {rfqCompleteLoadingQuoteId === selectedQuote.quote_id ? (
                            <>
                              <Loader2 size={14} className="mr-1 animate-spin" />
                              Completing...
                            </>
                          ) : (
                            <>
                              <CheckCircle size={14} className="mr-1" />
                              Shipment Completed
                            </>
                          )}
                        </Button>
                      )}
                      {selectedQuote.rfq_accepted_at && !selectedQuote.payment_details && !selectedQuote.quote_payment_id && (
                        <Button
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          size="sm"
                          onClick={() => {
                            openPaymentDetailsDialog(selectedQuote, { closeQuoteDetail: true });
                          }}
                        >
                          <DollarSign size={14} className="mr-1" />
                          Request Payment Details
                        </Button>
                      )}
                      {selectedQuote.payment_submitted_at && !selectedQuote.payment_received_at && (
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          size="sm"
                          onClick={() => {
                            setShowQuoteDetailDialog(false);
                            setCompletionForm({ rating: 5, feedback: '' });
                            setShowPaymentReceivedDialog(true);
                          }}
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Payment Received
                        </Button>
                      )}
                    </>
                  )}
                  {/* Submit Bid if not already submitted and not own quote */}
                  {!selectedQuoteIsOwnedByCurrentMember &&
                    !selectedQuoteIsTeamEnquiry &&
                    !selectedQuote.has_submitted_bid &&
                    selectedQuote.status === 'Active' &&
                    activeTab !== 'assignedToMe' && (
                      responseDeadlineInfo?.isClosed ? (
                        <Button variant="outline" disabled className="border-red-200 text-red-600 opacity-100">
                          <Clock size={14} className="mr-1" />
                          Submission Closed
                        </Button>
                      ) : canCreateQuote() ? (
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => {
                            setShowQuoteDetailDialog(false);
                            openBidDialog(selectedQuote);
                          }}
                        >
                          <Send size={14} className="mr-1" />
                          Submit Quotation
                        </Button>
                      ) : (
                        <p className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded">
                          Quotation limit reached. <Link to="/upgrade" className="font-medium underline">Upgrade your plan</Link> to submit more.
                        </p>
                      )
                    )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteToolEnhanced;
